import { sageBaseAdminApi as api } from './sageBaseAdminApi';

export const addTagTypes = [
    'residentActivities',
    'careTask',
    'ehrLockedFields',
    'residents',
    'units',
    'checkIns',
    'staff',
    'enterprises',
    'facilities',
    'tasks',
    'groups',
    'devices',
    'speakers',
    'nurseStations',
    'media',
    'care-info',
    'schedules',
    'companyPhones',
    'careZones',
    'staffPreferences',
    'doors',
    'doorAlarms',
    'features',
    'notificationPolicies',
    'shiftInstances',
] as const;
const injectedRtkApi = api
    .enhanceEndpoints({
        addTagTypes,
    })
    .injectEndpoints({
        endpoints: (build) => ({
            getCareEvent: build.query<GetCareEventApiResponse, GetCareEventApiArg>({
                query: (queryArg) => ({
                    url: `/careEvents/${queryArg.careEventId}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityId: queryArg.facilityId },
                }),
                providesTags: ['residentActivities'],
            }),
            addCareEventNote: build.mutation<AddCareEventNoteApiResponse, AddCareEventNoteApiArg>({
                query: (queryArg) => ({
                    url: `/careEvents/${queryArg.careEventId}/notes`,
                    method: 'POST',
                    body: queryArg.createCareEventNoteRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityId: queryArg.facilityId },
                }),
                invalidatesTags: ['residentActivities'],
            }),
            getCareTasks: build.query<GetCareTasksApiResponse, GetCareTasksApiArg>({
                query: (queryArg) => ({
                    url: `/careTasks`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        facilityId: queryArg.facilityId,
                        shiftInstanceId: queryArg.shiftInstanceId,
                        residentIds: queryArg.residentIds,
                        staffMemberId: queryArg.staffMemberId,
                        enrichTasks: queryArg.enrichTasks,
                        enrichCareItems: queryArg.enrichCareItems,
                    },
                }),
                providesTags: ['careTask'],
            }),
            updateCareTask: build.mutation<UpdateCareTaskApiResponse, UpdateCareTaskApiArg>({
                query: (queryArg) => ({
                    url: `/careTasks/${queryArg.careTaskId}`,
                    method: 'PATCH',
                    body: queryArg.updateCareTaskRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['careTask'],
            }),
            addEhrLockedFields: build.mutation<
                AddEhrLockedFieldsApiResponse,
                AddEhrLockedFieldsApiArg
            >({
                query: (queryArg) => ({
                    url: `/ehrLockedFields`,
                    method: 'POST',
                    body: queryArg.addEhrLockedFieldsRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['ehrLockedFields'],
            }),
            deleteEhrLockedField: build.mutation<
                DeleteEhrLockedFieldApiResponse,
                DeleteEhrLockedFieldApiArg
            >({
                query: (queryArg) => ({
                    url: `/ehrLockedFields/${queryArg.ehrLockedFieldRid}`,
                    method: 'DELETE',
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['ehrLockedFields'],
            }),
            createResident: build.mutation<CreateResidentApiResponse, CreateResidentApiArg>({
                query: (queryArg) => ({
                    url: `/residents`,
                    method: 'POST',
                    body: queryArg.createOrUpdateResidentRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            getResidents: build.query<GetResidentsApiResponse, GetResidentsApiArg>({
                query: (queryArg) => ({
                    url: `/residents`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        unitId: queryArg.unitId,
                        facilityId: queryArg.facilityId,
                        enterpriseId: queryArg.enterpriseId,
                        hasOpenEvent: queryArg.hasOpenEvent,
                        isArchived: queryArg.isArchived,
                    },
                }),
                providesTags: ['residents'],
            }),
            createResidents: build.mutation<CreateResidentsApiResponse, CreateResidentsApiArg>({
                query: (queryArg) => ({
                    url: `/residents/bulk`,
                    method: 'POST',
                    body: queryArg.bulkCreateResidentsRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['residents'],
            }),
            bulkSetRoutineCheckStatus: build.mutation<
                BulkSetRoutineCheckStatusApiResponse,
                BulkSetRoutineCheckStatusApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/bulk/routineChecks`,
                    method: 'PUT',
                    body: queryArg.bulkSetRoutineCheckRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            getUnits: build.query<GetUnitsApiResponse, GetUnitsApiArg>({
                query: (queryArg) => ({
                    url: `/units`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        residentId: queryArg.residentId,
                        facilityId: queryArg.facilityId,
                        enterpriseId: queryArg.enterpriseId,
                    },
                }),
                providesTags: ['units'],
            }),
            createUnit: build.mutation<CreateUnitApiResponse, CreateUnitApiArg>({
                query: (queryArg) => ({
                    url: `/units`,
                    method: 'POST',
                    body: queryArg.createOrUpdateUnitRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['units'],
            }),
            deleteUnits: build.mutation<DeleteUnitsApiResponse, DeleteUnitsApiArg>({
                query: (queryArg) => ({
                    url: `/units`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { unitRid: queryArg.unitRid },
                }),
                invalidatesTags: ['units'],
            }),
            createUnits: build.mutation<CreateUnitsApiResponse, CreateUnitsApiArg>({
                query: (queryArg) => ({
                    url: `/units/bulk`,
                    method: 'POST',
                    body: queryArg.bulkCreateUnitRequestForFacility,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['units'],
            }),
            updateUnits: build.mutation<UpdateUnitsApiResponse, UpdateUnitsApiArg>({
                query: (queryArg) => ({
                    url: `/units/bulk`,
                    method: 'PUT',
                    body: queryArg.bulkUpdateUnitsRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['units'],
            }),
            getCheckIns: build.query<GetCheckInsApiResponse, GetCheckInsApiArg>({
                query: (queryArg) => ({
                    url: `/checkIns`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        date: queryArg.date,
                        unitId: queryArg.unitId,
                        careZoneId: queryArg.careZoneId,
                        facilityId: queryArg.facilityId,
                        enterpriseId: queryArg.enterpriseId,
                        status: queryArg.status,
                        isOnHold: queryArg.isOnHold,
                    },
                }),
                providesTags: ['checkIns'],
            }),
            createCheckIn: build.mutation<CreateCheckInApiResponse, CreateCheckInApiArg>({
                query: (queryArg) => ({
                    url: `/checkIns`,
                    method: 'POST',
                    body: queryArg.createCheckInRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['checkIns'],
            }),
            updateCheckIn: build.mutation<UpdateCheckInApiResponse, UpdateCheckInApiArg>({
                query: (queryArg) => ({
                    url: `/checkIns/${queryArg.checkInId}`,
                    method: 'PUT',
                    body: queryArg.updateCheckInRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['checkIns'],
            }),
            getCheckIn: build.query<GetCheckInApiResponse, GetCheckInApiArg>({
                query: (queryArg) => ({
                    url: `/checkIns/${queryArg.checkInId}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['checkIns'],
            }),
            createStaffMember: build.mutation<
                CreateStaffMemberApiResponse,
                CreateStaffMemberApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff`,
                    method: 'POST',
                    body: queryArg.createOrUpdateStaffMemberRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    timeout: 60000,
                }),
                invalidatesTags: ['staff'],
            }),
            getStaffMembers: build.query<GetStaffMembersApiResponse, GetStaffMembersApiArg>({
                query: (queryArg) => ({
                    url: `/staff`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        enterpriseId: queryArg.enterpriseId,
                        phoneNumber: queryArg.phoneNumber,
                        facilityId: queryArg.facilityId,
                        groupId: queryArg.groupId,
                        isArchived: queryArg.isArchived,
                    },
                }),
                providesTags: ['staff'],
            }),
            updateStaffMemberPermissions: build.mutation<
                UpdateStaffMemberPermissionsApiResponse,
                UpdateStaffMemberPermissionsApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/permissions`,
                    method: 'POST',
                    body: queryArg.updateStaffPermissionsRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['staff'],
            }),
            getStaffWithFallReplayView: build.query<
                GetStaffWithFallReplayViewApiResponse,
                GetStaffWithFallReplayViewApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/permissions/fall-replay-view`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityRid: queryArg.facilityRid },
                }),
                providesTags: ['staff'],
            }),
            getStaffPermissions: build.query<
                GetStaffPermissionsApiResponse,
                GetStaffPermissionsApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/${queryArg.staffMemberId}/permissions`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityRid: queryArg.facilityRid },
                }),
                providesTags: ['staff'],
            }),
            grantRoleOnResource: build.mutation<
                GrantRoleOnResourceApiResponse,
                GrantRoleOnResourceApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/${queryArg.staffMemberId}/permissions/roles/${queryArg.resourceRid}`,
                    method: 'POST',
                    body: queryArg.staffPermissionRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['staff'],
            }),
            revokeRoleOnResource: build.mutation<
                RevokeRoleOnResourceApiResponse,
                RevokeRoleOnResourceApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/${queryArg.staffMemberId}/permissions/roles/${queryArg.resourceRid}`,
                    method: 'DELETE',
                    body: queryArg.staffPermissionRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['staff'],
            }),
            updatePhoneNumberVerificationStatus: build.mutation<
                UpdatePhoneNumberVerificationStatusApiResponse,
                UpdatePhoneNumberVerificationStatusApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/${queryArg.staffMemberId}/update-phone-verification`,
                    method: 'POST',
                    body: queryArg.updatePhoneNumberVerificationRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['staff'],
            }),
            createStaffMembers: build.mutation<
                CreateStaffMembersApiResponse,
                CreateStaffMembersApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/bulk`,
                    method: 'POST',
                    body: queryArg.bulkCreateStaffMembersRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
            }),
            mergeStaffProfiles: build.mutation<
                MergeStaffProfilesApiResponse,
                MergeStaffProfilesApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/sso/merge`,
                    method: 'POST',
                    body: queryArg.mergeStaffProfilesRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['staff'],
            }),
            getContactsOfResident: build.query<
                GetContactsOfResidentApiResponse,
                GetContactsOfResidentApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/contacts`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['residents'],
            }),
            createContactForResident: build.mutation<
                CreateContactForResidentApiResponse,
                CreateContactForResidentApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/contacts`,
                    method: 'POST',
                    body: queryArg.createOrUpdateContactRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            getResidentActivities: build.query<
                GetResidentActivitiesApiResponse,
                GetResidentActivitiesApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/activities`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        careOutcomes: queryArg.careOutcomes,
                        careProvided: queryArg.careProvided,
                        pageNumber: queryArg.pageNumber,
                        pageSize: queryArg.pageSize,
                    },
                }),
                providesTags: ['residents', 'residentActivities'],
            }),
            getEnterprises: build.query<GetEnterprisesApiResponse, GetEnterprisesApiArg>({
                query: (queryArg) => ({
                    url: `/enterprises`,
                    headers: { Authorization: queryArg?.authorization },
                }),
                providesTags: ['enterprises'],
            }),
            createEnterprise: build.mutation<CreateEnterpriseApiResponse, CreateEnterpriseApiArg>({
                query: (queryArg) => ({
                    url: `/enterprises`,
                    method: 'POST',
                    body: queryArg.createEnterpriseRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['enterprises'],
            }),
            getFacilities: build.query<GetFacilitiesApiResponse, GetFacilitiesApiArg>({
                query: (queryArg) => ({
                    url: `/facilities`,
                    headers: { Authorization: queryArg?.authorization },
                    params: {
                        enterpriseId: queryArg.enterpriseId,
                        emergencyNotificationNumber: queryArg.emergencyNotificationNumber,
                    },
                }),
                providesTags: ['facilities'],
            }),
            createFacility: build.mutation<CreateFacilityApiResponse, CreateFacilityApiArg>({
                query: (queryArg) => ({
                    url: `/facilities`,
                    method: 'POST',
                    body: queryArg.createFacilityRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['facilities'],
            }),
            createTask: build.mutation<CreateTaskApiResponse, CreateTaskApiArg>({
                query: (queryArg) => ({
                    url: `/tasks`,
                    method: 'POST',
                    body: queryArg.createOrUpdateTaskRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['tasks'],
            }),
            getGroups: build.query<GetGroupsApiResponse, GetGroupsApiArg>({
                query: (queryArg) => ({
                    url: `/groups`,
                    headers: { Authorization: queryArg?.authorization },
                    params: {
                        facilityId: queryArg.facilityId,
                        enterpriseId: queryArg.enterpriseId,
                        staffMemberId: queryArg.staffMemberId,
                    },
                }),
                providesTags: ['groups'],
            }),
            createGroup: build.mutation<CreateGroupApiResponse, CreateGroupApiArg>({
                query: (queryArg) => ({
                    url: `/groups`,
                    method: 'POST',
                    body: queryArg.createOrUpdateGroupRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['groups'],
            }),
            getEnterprise: build.query<GetEnterpriseApiResponse, GetEnterpriseApiArg>({
                query: (queryArg) => ({
                    url: `/enterprises/${queryArg.enterpriseId}`,
                    headers: { Authorization: queryArg?.authorization },
                }),
                providesTags: ['enterprises'],
            }),
            updateEnterprise: build.mutation<UpdateEnterpriseApiResponse, UpdateEnterpriseApiArg>({
                query: (queryArg) => ({
                    url: `/enterprises/${queryArg.enterpriseId}`,
                    method: 'PUT',
                    body: queryArg.updateEnterpriseRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['enterprises'],
            }),
            deleteEnterprise: build.mutation<DeleteEnterpriseApiResponse, DeleteEnterpriseApiArg>({
                query: (queryArg) => ({
                    url: `/enterprises/${queryArg.enterpriseId}`,
                    method: 'DELETE',
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['enterprises'],
            }),
            retryEnterpriseSsoEnforcement: build.mutation<
                RetryEnterpriseSsoEnforcementApiResponse,
                RetryEnterpriseSsoEnforcementApiArg
            >({
                query: (queryArg) => ({
                    url: `/enterprises/${queryArg.enterpriseId}/sso/enforcement/retry`,
                    method: 'POST',
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['enterprises'],
            }),
            getFacility: build.query<GetFacilityApiResponse, GetFacilityApiArg>({
                query: (queryArg) => ({
                    url: `/facilities/${queryArg.facilityId}`,
                    headers: {
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                        Authorization: queryArg?.authorization,
                    },
                }),
                providesTags: ['facilities'],
            }),
            updateFacility: build.mutation<UpdateFacilityApiResponse, UpdateFacilityApiArg>({
                query: (queryArg) => ({
                    url: `/facilities/${queryArg.facilityId}`,
                    method: 'PUT',
                    body: queryArg.updateFacilityRequest,
                    headers: {
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                        Authorization: queryArg?.authorization,
                    },
                }),
                invalidatesTags: ['facilities'],
            }),
            deleteFacility: build.mutation<DeleteFacilityApiResponse, DeleteFacilityApiArg>({
                query: (queryArg) => ({
                    url: `/facilities/${queryArg.facilityId}`,
                    method: 'DELETE',
                    headers: {
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                        Authorization: queryArg?.authorization,
                    },
                }),
                invalidatesTags: ['facilities'],
            }),
            updateFacilityStatus: build.mutation<
                UpdateFacilityStatusApiResponse,
                UpdateFacilityStatusApiArg
            >({
                query: (queryArg) => ({
                    url: `/facilities/status/${queryArg.facilityRid}`,
                    method: 'PUT',
                    body: queryArg.updateFacilityStatusRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['facilities'],
            }),
            getGroup: build.query<GetGroupApiResponse, GetGroupApiArg>({
                query: (queryArg) => ({
                    url: `/groups/${queryArg.groupId}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['groups'],
            }),
            updateGroup: build.mutation<UpdateGroupApiResponse, UpdateGroupApiArg>({
                query: (queryArg) => ({
                    url: `/groups/${queryArg.groupId}`,
                    method: 'PUT',
                    body: queryArg.createOrUpdateGroupRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['groups'],
            }),
            deleteGroup: build.mutation<DeleteGroupApiResponse, DeleteGroupApiArg>({
                query: (queryArg) => ({
                    url: `/groups/${queryArg.groupId}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['groups'],
            }),
            getResident: build.query<GetResidentApiResponse, GetResidentApiArg>({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { enrichExpectedExits: queryArg.enrichExpectedExits },
                }),
                providesTags: ['residents'],
            }),
            updateResident: build.mutation<UpdateResidentApiResponse, UpdateResidentApiArg>({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}`,
                    method: 'PUT',
                    body: queryArg.createOrUpdateResidentRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            deleteResident: build.mutation<DeleteResidentApiResponse, DeleteResidentApiArg>({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            createExpectedExit: build.mutation<
                CreateExpectedExitApiResponse,
                CreateExpectedExitApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/expected-exits`,
                    method: 'POST',
                    body: queryArg.createResidentExpectedExitRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            deleteResidentExpectedExit: build.mutation<
                DeleteResidentExpectedExitApiResponse,
                DeleteResidentExpectedExitApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/expected-exits/${queryArg.expectedExitRid}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            updateResidentDetectConsent: build.mutation<
                UpdateResidentDetectConsentApiResponse,
                UpdateResidentDetectConsentApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/detect-consent`,
                    method: 'PATCH',
                    body: queryArg.updateResidentDetectConsentRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            getLatestCareActivity: build.query<
                GetLatestCareActivityApiResponse,
                GetLatestCareActivityApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/careActivity/latest`,
                    method: 'POST',
                    body: queryArg.getLatestCareActivityRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['residents', 'tasks'],
            }),
            getContactOfResident: build.query<
                GetContactOfResidentApiResponse,
                GetContactOfResidentApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/contacts/${queryArg.contactId}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['residents'],
            }),
            updateContact: build.mutation<UpdateContactApiResponse, UpdateContactApiArg>({
                query: (queryArg) => ({
                    url: `/residents/contacts/${queryArg.contactId}`,
                    method: 'PUT',
                    body: queryArg.createOrUpdateContactRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            deleteContact: build.mutation<DeleteContactApiResponse, DeleteContactApiArg>({
                query: (queryArg) => ({
                    url: `/residents/contacts/${queryArg.contactId}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            getStaffMember: build.query<GetStaffMemberApiResponse, GetStaffMemberApiArg>({
                query: (queryArg) => ({
                    url: `/staff/${queryArg.staffMemberId}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['staff'],
            }),
            updateStaffMember: build.mutation<
                UpdateStaffMemberApiResponse,
                UpdateStaffMemberApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/${queryArg.staffMemberId}`,
                    method: 'PUT',
                    body: queryArg.createOrUpdateStaffMemberRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    timeout: 60000,
                }),
                invalidatesTags: ['staff'],
            }),
            archiveStaffMember: build.mutation<
                ArchiveStaffMemberApiResponse,
                ArchiveStaffMemberApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/${queryArg.staffMemberId}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['staff'],
            }),
            assignPhoneNumber: build.mutation<
                AssignPhoneNumberApiResponse,
                AssignPhoneNumberApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/${queryArg.staffMemberId}/assign-phone-number`,
                    method: 'POST',
                    body: queryArg.body,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityRid: queryArg.facilityRid },
                }),
                invalidatesTags: ['staff'],
            }),
            verifyPhoneNumber: build.mutation<
                VerifyPhoneNumberApiResponse,
                VerifyPhoneNumberApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/${queryArg.staffMemberId}/verify-phone-number`,
                    method: 'POST',
                    body: queryArg.body,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['staff'],
            }),
            getTask: build.query<GetTaskApiResponse, GetTaskApiArg>({
                query: (queryArg) => ({
                    url: `/tasks/${queryArg.taskId}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        enrichFollowUpAnswers: queryArg.enrichFollowUpAnswers,
                        enrichCareTasks: queryArg.enrichCareTasks,
                        enrichAcknowledgements: queryArg.enrichAcknowledgements,
                    },
                }),
                providesTags: ['tasks'],
            }),
            updateTask: build.mutation<UpdateTaskApiResponse, UpdateTaskApiArg>({
                query: (queryArg) => ({
                    url: `/tasks/${queryArg.taskId}`,
                    method: 'PUT',
                    body: queryArg.createOrUpdateTaskRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['tasks'],
            }),
            deleteTask: build.mutation<DeleteTaskApiResponse, DeleteTaskApiArg>({
                query: (queryArg) => ({
                    url: `/tasks/${queryArg.taskId}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['tasks'],
            }),
            resolveTask: build.mutation<ResolveTaskApiResponse, ResolveTaskApiArg>({
                query: (queryArg) => ({
                    url: `/tasks/${queryArg.taskId}/resolve`,
                    method: 'PUT',
                    body: queryArg.resolveTaskRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['careTask', 'tasks'],
            }),
            reviewTask: build.mutation<ReviewTaskApiResponse, ReviewTaskApiArg>({
                query: (queryArg) => ({
                    url: `/tasks/${queryArg.taskId}/review`,
                    method: 'PUT',
                    body: queryArg.reviewTaskRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['tasks'],
            }),
            acknowledgeTask: build.mutation<AcknowledgeTaskApiResponse, AcknowledgeTaskApiArg>({
                query: (queryArg) => ({
                    url: `/tasks/${queryArg.taskId}/acknowledge`,
                    method: 'POST',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['tasks'],
            }),
            getCareConfirmationTracker: build.query<
                GetCareConfirmationTrackerApiResponse,
                GetCareConfirmationTrackerApiArg
            >({
                query: (queryArg) => ({
                    url: `/tasks/${queryArg.taskId}/care-confirmation-tracker`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['tasks'],
            }),
            resolveAllAlerts: build.mutation<ResolveAllAlertsApiResponse, ResolveAllAlertsApiArg>({
                query: (queryArg) => ({
                    url: `/tasks/resolve/${queryArg.facilityRid}`,
                    method: 'POST',
                    body: queryArg.resolveAlertsRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['tasks'],
            }),
            syncOfflineAlert: build.mutation<SyncOfflineAlertApiResponse, SyncOfflineAlertApiArg>({
                query: (queryArg) => ({
                    url: `/tasks/sync-offline`,
                    method: 'POST',
                    body: queryArg.syncOfflineAlertRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['tasks'],
            }),
            getUnit: build.query<GetUnitApiResponse, GetUnitApiArg>({
                query: (queryArg) => ({
                    url: `/units/${queryArg.unitId}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['units'],
            }),
            updateUnit: build.mutation<UpdateUnitApiResponse, UpdateUnitApiArg>({
                query: (queryArg) => ({
                    url: `/units/${queryArg.unitId}`,
                    method: 'PUT',
                    body: queryArg.createOrUpdateUnitRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['units'],
            }),
            deleteUnit: build.mutation<DeleteUnitApiResponse, DeleteUnitApiArg>({
                query: (queryArg) => ({
                    url: `/units/${queryArg.unitId}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['units'],
            }),
            createUpdateForTask: build.mutation<
                CreateUpdateForTaskApiResponse,
                CreateUpdateForTaskApiArg
            >({
                query: (queryArg) => ({
                    url: `/tasks/updates`,
                    method: 'POST',
                    body: queryArg.createTaskUpdateRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['tasks'],
            }),
            deleteUpdate: build.mutation<DeleteUpdateApiResponse, DeleteUpdateApiArg>({
                query: (queryArg) => ({
                    url: `/tasks/updates/${queryArg.updateId}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['tasks'],
            }),
            getAllocatedDevices: build.query<
                GetAllocatedDevicesApiResponse,
                GetAllocatedDevicesApiArg
            >({
                query: (queryArg) => ({
                    url: `/devices/allocated`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        unitRid: queryArg.unitRid,
                        doorRid: queryArg.doorRid,
                        facilityRid: queryArg.facilityRid,
                        enterpriseRid: queryArg.enterpriseRid,
                        type: queryArg['type'],
                        deviceTypes: queryArg.deviceTypes,
                        residentRid: queryArg.residentRid,
                        deviceHardwareId: queryArg.deviceHardwareId,
                        isCheckIn: queryArg.isCheckIn,
                        isSilent: queryArg.isSilent,
                        deviceRid: queryArg.deviceRid,
                    },
                }),
                providesTags: ['devices'],
            }),
            allocateDevice: build.mutation<AllocateDeviceApiResponse, AllocateDeviceApiArg>({
                query: (queryArg) => ({
                    url: `/devices/allocated`,
                    method: 'POST',
                    body: queryArg.createOrUpdateDeviceAllocationRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['devices'],
            }),
            searchLatestAllocatedDeviceActivity: build.query<
                SearchLatestAllocatedDeviceActivityApiResponse,
                SearchLatestAllocatedDeviceActivityApiArg
            >({
                query: (queryArg) => ({
                    url: `/devices/activity/allocated/latest`,
                    method: 'POST',
                    body: queryArg.searchLatestAllocatedDeviceActivityRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['devices'],
            }),
            getDeviceAllocation: build.query<
                GetDeviceAllocationApiResponse,
                GetDeviceAllocationApiArg
            >({
                query: (queryArg) => ({
                    url: `/devices/allocated/${queryArg.deviceAllocationId}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['devices'],
            }),
            updateDeviceAllocation: build.mutation<
                UpdateDeviceAllocationApiResponse,
                UpdateDeviceAllocationApiArg
            >({
                query: (queryArg) => ({
                    url: `/devices/allocated/${queryArg.deviceAllocationId}`,
                    method: 'PUT',
                    body: queryArg.schema,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['devices'],
            }),
            deallocateDevice: build.mutation<DeallocateDeviceApiResponse, DeallocateDeviceApiArg>({
                query: (queryArg) => ({
                    url: `/devices/allocated/${queryArg.deviceAllocationId}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['devices'],
            }),
            getDevices: build.query<GetDevicesApiResponse, GetDevicesApiArg>({
                query: (queryArg) => ({
                    url: `/devices`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        createdBefore: queryArg.createdBefore,
                        createdAfter: queryArg.createdAfter,
                        type: queryArg['type'],
                        hardwareId: queryArg.hardwareId,
                        isAllocated: queryArg.isAllocated,
                    },
                }),
                providesTags: ['devices'],
            }),
            createDevice: build.mutation<CreateDeviceApiResponse, CreateDeviceApiArg>({
                query: (queryArg) => ({
                    url: `/devices`,
                    method: 'POST',
                    body: queryArg.createDeviceRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['devices'],
            }),
            getDevice: build.query<GetDeviceApiResponse, GetDeviceApiArg>({
                query: (queryArg) => ({
                    url: `/devices/${queryArg.deviceId}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['devices'],
            }),
            deleteDevice: build.mutation<DeleteDeviceApiResponse, DeleteDeviceApiArg>({
                query: (queryArg) => ({
                    url: `/devices/${queryArg.deviceId}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['devices'],
            }),
            createSpeaker: build.mutation<CreateSpeakerApiResponse, CreateSpeakerApiArg>({
                query: (queryArg) => ({
                    url: `/speakers`,
                    method: 'POST',
                    body: queryArg.createSpeakerRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['speakers'],
            }),
            getSpeakers: build.query<GetSpeakersApiResponse, GetSpeakersApiArg>({
                query: (queryArg) => ({
                    url: `/speakers`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        facilityRid: queryArg.facilityRid,
                        unitRid: queryArg.unitRid,
                        hardwareId: queryArg.hardwareId,
                    },
                }),
                providesTags: ['speakers'],
            }),
            getSpeaker: build.query<GetSpeakerApiResponse, GetSpeakerApiArg>({
                query: (queryArg) => ({
                    url: `/speakers/${queryArg.speakerRid}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['speakers'],
            }),
            updateSpeaker: build.mutation<UpdateSpeakerApiResponse, UpdateSpeakerApiArg>({
                query: (queryArg) => ({
                    url: `/speakers/${queryArg.speakerRid}`,
                    method: 'PUT',
                    body: queryArg.updateSpeakerRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['speakers'],
            }),
            deleteSpeaker: build.mutation<DeleteSpeakerApiResponse, DeleteSpeakerApiArg>({
                query: (queryArg) => ({
                    url: `/speakers/${queryArg.speakerRid}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['speakers'],
            }),
            createNurseStation: build.mutation<
                CreateNurseStationApiResponse,
                CreateNurseStationApiArg
            >({
                query: (queryArg) => ({
                    url: `/nurseStations`,
                    method: 'POST',
                    body: queryArg.createNurseStationRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['nurseStations'],
            }),
            getNurseStationsByFacility: build.query<
                GetNurseStationsByFacilityApiResponse,
                GetNurseStationsByFacilityApiArg
            >({
                query: (queryArg) => ({
                    url: `/nurseStations`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityId: queryArg.facilityId },
                }),
                providesTags: ['nurseStations'],
            }),
            getNurseStation: build.query<GetNurseStationApiResponse, GetNurseStationApiArg>({
                query: (queryArg) => ({
                    url: `/nurseStations/${queryArg.nurseStationId}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['nurseStations'],
            }),
            updateNurseStation: build.mutation<
                UpdateNurseStationApiResponse,
                UpdateNurseStationApiArg
            >({
                query: (queryArg) => ({
                    url: `/nurseStations/${queryArg.nurseStationId}`,
                    method: 'PUT',
                    body: queryArg.updateNurseStationRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['nurseStations'],
            }),
            deleteNurseStation: build.mutation<
                DeleteNurseStationApiResponse,
                DeleteNurseStationApiArg
            >({
                query: (queryArg) => ({
                    url: `/nurseStations/${queryArg.nurseStationId}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['nurseStations'],
            }),
            getOnCallState: build.query<GetOnCallStateApiResponse, GetOnCallStateApiArg>({
                query: (queryArg) => ({
                    url: `/staff/${queryArg.staffMemberId}/on-call/${queryArg.facilityId}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['staff'],
            }),
            updateOnCallState: build.mutation<
                UpdateOnCallStateApiResponse,
                UpdateOnCallStateApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/${queryArg.staffMemberId}/on-call/${queryArg.facilityId}`,
                    method: 'POST',
                    body: queryArg.updateOnCallStateRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['staff'],
            }),
            setEnterpriseGroupMemberships: build.mutation<
                SetEnterpriseGroupMembershipsApiResponse,
                SetEnterpriseGroupMembershipsApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/${queryArg.staffMemberId}/set-enterprise-group-memberships`,
                    method: 'PUT',
                    body: queryArg.setEnterpriseGroupMembershipsRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { enterpriseRid: queryArg.enterpriseRid },
                }),
                invalidatesTags: ['staff'],
            }),
            getMembershipDetails: build.query<
                GetMembershipDetailsApiResponse,
                GetMembershipDetailsApiArg
            >({
                query: (queryArg) => ({
                    url: `/groups/${queryArg.groupId}/membership`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['groups'],
            }),
            updateGroupMembership: build.mutation<
                UpdateGroupMembershipApiResponse,
                UpdateGroupMembershipApiArg
            >({
                query: (queryArg) => ({
                    url: `/groups/${queryArg.groupId}/membership`,
                    method: 'PUT',
                    body: queryArg.createOrUpdateGroupMembershipRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    timeout: 60000,
                }),
                invalidatesTags: ['groups'],
            }),
            removeMemberFromGroup: build.mutation<
                RemoveMemberFromGroupApiResponse,
                RemoveMemberFromGroupApiArg
            >({
                query: (queryArg) => ({
                    url: `/groups/${queryArg.groupId}/membership`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { staffMemberId: queryArg.staffMemberId },
                }),
                invalidatesTags: ['groups'],
            }),
            getApplicationConfiguration: build.query<
                GetApplicationConfigurationApiResponse,
                GetApplicationConfigurationApiArg
            >({
                query: (queryArg) => ({
                    url: `/configuration`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
            }),
            resolveNumbers: build.query<ResolveNumbersApiResponse, ResolveNumbersApiArg>({
                query: (queryArg) => ({
                    url: `/notificationCallback/resolve-numbers`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        senderNumber: queryArg.senderNumber,
                        recipientNumber: queryArg.recipientNumber,
                    },
                }),
            }),
            createNoteForTask: build.mutation<
                CreateNoteForTaskApiResponse,
                CreateNoteForTaskApiArg
            >({
                query: (queryArg) => ({
                    url: `/tasks/${queryArg.taskId}/notes`,
                    method: 'POST',
                    body: queryArg.createOrUpdateTaskNoteRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['tasks'],
            }),
            deleteTaskNote: build.mutation<DeleteTaskNoteApiResponse, DeleteTaskNoteApiArg>({
                query: (queryArg) => ({
                    url: `/tasks/notes/${queryArg.noteId}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['tasks'],
            }),
            updateTaskNote: build.mutation<UpdateTaskNoteApiResponse, UpdateTaskNoteApiArg>({
                query: (queryArg) => ({
                    url: `/tasks/notes/${queryArg.noteId}`,
                    method: 'PUT',
                    body: queryArg.createOrUpdateTaskNoteRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['tasks'],
            }),
            unassignTask: build.mutation<UnassignTaskApiResponse, UnassignTaskApiArg>({
                query: (queryArg) => ({
                    url: `/tasks/${queryArg.taskId}/unassign`,
                    method: 'POST',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['tasks'],
            }),
            updateResidentArchiveStatus: build.mutation<
                UpdateResidentArchiveStatusApiResponse,
                UpdateResidentArchiveStatusApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/archive`,
                    method: 'POST',
                    body: queryArg.updateResidentArchiveStatusRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            updateStaffMemberArchiveStatus: build.mutation<
                UpdateStaffMemberArchiveStatusApiResponse,
                UpdateStaffMemberArchiveStatusApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/${queryArg.staffMemberId}/archive`,
                    method: 'POST',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { archive: queryArg.archive },
                }),
                invalidatesTags: ['staff'],
            }),
            getStaffMemberFacilityMetadata: build.query<
                GetStaffMemberFacilityMetadataApiResponse,
                GetStaffMemberFacilityMetadataApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/${queryArg.staffMemberId}/facilities`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityId: queryArg.facilityId },
                }),
                providesTags: ['staff'],
            }),
            deleteStaffMemberFromFacilities: build.mutation<
                DeleteStaffMemberFromFacilitiesApiResponse,
                DeleteStaffMemberFromFacilitiesApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/${queryArg.staffMemberId}/facilities`,
                    method: 'DELETE',
                    body: queryArg.ridArray,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    timeout: 60000,
                }),
                invalidatesTags: ['staff'],
            }),
            getImageForResource: build.query<
                GetImageForResourceApiResponse,
                GetImageForResourceApiArg
            >({
                query: (queryArg) => ({
                    url: `/media/images/${queryArg.resourceId}`,
                    responseHandler: 'text',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['media'],
            }),
            uploadImageForResource: build.mutation<
                UploadImageForResourceApiResponse,
                UploadImageForResourceApiArg
            >({
                query: (queryArg) => ({
                    url: `/media/images/${queryArg.resourceId}`,
                    method: 'PUT',
                    body: queryArg.body,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['media'],
            }),
            deleteImageForResource: build.mutation<
                DeleteImageForResourceApiResponse,
                DeleteImageForResourceApiArg
            >({
                query: (queryArg) => ({
                    url: `/media/images/${queryArg.resourceId}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['media'],
            }),
            getVideoReplayDownloadUrl: build.query<
                GetVideoReplayDownloadUrlApiResponse,
                GetVideoReplayDownloadUrlApiArg
            >({
                query: (queryArg) => ({
                    url: `/tasks/${queryArg.taskId}/videoReplay`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { type: queryArg['type'] },
                }),
                providesTags: ['tasks', 'media'],
            }),
            getVideoReplayAvailability: build.query<
                GetVideoReplayAvailabilityApiResponse,
                GetVideoReplayAvailabilityApiArg
            >({
                query: (queryArg) => ({
                    url: `/tasks/${queryArg.taskId}/videoReplay/availability`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['tasks', 'media'],
            }),
            setPcpForResident: build.mutation<
                SetPcpForResidentApiResponse,
                SetPcpForResidentApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/pcp`,
                    method: 'PUT',
                    body: queryArg.createOrUpdateProfessionalContactRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            deletePcpForResident: build.mutation<
                DeletePcpForResidentApiResponse,
                DeletePcpForResidentApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/pcp`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            getPcpForResident: build.query<GetPcpForResidentApiResponse, GetPcpForResidentApiArg>({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/pcp`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['residents'],
            }),
            getSpecialistsForResident: build.query<
                GetSpecialistsForResidentApiResponse,
                GetSpecialistsForResidentApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/specialists`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['residents'],
            }),
            addSpecialistForResident: build.mutation<
                AddSpecialistForResidentApiResponse,
                AddSpecialistForResidentApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/specialists`,
                    method: 'POST',
                    body: queryArg.createOrUpdateProfessionalContactRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            updateSpecialistForResident: build.mutation<
                UpdateSpecialistForResidentApiResponse,
                UpdateSpecialistForResidentApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/specialists/${queryArg.specialistId}`,
                    method: 'PUT',
                    body: queryArg.createOrUpdateProfessionalContactRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            deleteSpecialistForResident: build.mutation<
                DeleteSpecialistForResidentApiResponse,
                DeleteSpecialistForResidentApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/specialists/${queryArg.specialistId}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            unallocateDevice: build.mutation<UnallocateDeviceApiResponse, UnallocateDeviceApiArg>({
                query: (queryArg) => ({
                    url: `/devices/allocated/${queryArg.deviceAllocationId}/unallocate`,
                    method: 'POST',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['devices'],
            }),
            bulkUpdateDeviceAllocations: build.mutation<
                BulkUpdateDeviceAllocationsApiResponse,
                BulkUpdateDeviceAllocationsApiArg
            >({
                query: (queryArg) => ({
                    url: `/devices/allocated/bulk`,
                    method: 'POST',
                    body: queryArg.bulkUpdateDeviceAllocations,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['devices'],
            }),
            taskSearch: build.query<TaskSearchApiResponse, TaskSearchApiArg>({
                query: (queryArg) => ({
                    url: `/tasks/search`,
                    method: 'POST',
                    body: queryArg.taskSearchRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['tasks'],
            }),
            searchClosedTasks: build.query<SearchClosedTasksApiResponse, SearchClosedTasksApiArg>({
                query: (queryArg) => ({
                    url: `/tasks/closed/search`,
                    method: 'POST',
                    body: queryArg.closedTaskSearchRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['tasks'],
            }),
            searchOpenAlerts: build.query<SearchOpenAlertsApiResponse, SearchOpenAlertsApiArg>({
                query: (queryArg) => ({
                    url: `/tasks/open-alerts/search`,
                    method: 'POST',
                    body: queryArg.searchOpenAlertsRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['tasks'],
            }),
            setResidentHealthInfo: build.mutation<
                SetResidentHealthInfoApiResponse,
                SetResidentHealthInfoApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/health`,
                    method: 'PUT',
                    body: queryArg.healthInfo,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            deleteResidentHealthInfo: build.mutation<
                DeleteResidentHealthInfoApiResponse,
                DeleteResidentHealthInfoApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/health`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            getResidentCareInfo: build.query<
                GetResidentCareInfoApiResponse,
                GetResidentCareInfoApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/care-info`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['residents', 'care-info'],
            }),
            setResidentCareInfo: build.mutation<
                SetResidentCareInfoApiResponse,
                SetResidentCareInfoApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/care-info`,
                    method: 'POST',
                    body: queryArg.setResidentCareInfoRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents', 'care-info'],
            }),
            createTaskSchedule: build.mutation<
                CreateTaskScheduleApiResponse,
                CreateTaskScheduleApiArg
            >({
                query: (queryArg) => ({
                    url: `/schedules/tasks`,
                    method: 'POST',
                    body: queryArg.createOrUpdateTaskScheduleRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['schedules'],
            }),
            getTaskSchedule: build.query<GetTaskScheduleApiResponse, GetTaskScheduleApiArg>({
                query: (queryArg) => ({
                    url: `/schedules/tasks/${queryArg.scheduleId}`,
                    headers: { Authorization: queryArg?.authorization },
                }),
                providesTags: ['schedules'],
            }),
            updateTaskSchedule: build.mutation<
                UpdateTaskScheduleApiResponse,
                UpdateTaskScheduleApiArg
            >({
                query: (queryArg) => ({
                    url: `/schedules/tasks/${queryArg.scheduleId}`,
                    method: 'PUT',
                    body: queryArg.createOrUpdateTaskScheduleRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['schedules'],
            }),
            deleteTaskSchedule: build.mutation<
                DeleteTaskScheduleApiResponse,
                DeleteTaskScheduleApiArg
            >({
                query: (queryArg) => ({
                    url: `/schedules/tasks/${queryArg.scheduleId}`,
                    method: 'DELETE',
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['schedules'],
            }),
            taskScheduleSearch: build.query<
                TaskScheduleSearchApiResponse,
                TaskScheduleSearchApiArg
            >({
                query: (queryArg) => ({
                    url: `/schedules/tasks/search`,
                    method: 'POST',
                    body: queryArg.taskScheduleSearchRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                providesTags: ['schedules'],
            }),
            getMyTasks: build.mutation<GetMyTasksApiResponse, GetMyTasksApiArg>({
                query: (queryArg) => ({
                    url: `/tasks/mine`,
                    method: 'POST',
                    body: queryArg.userTaskSearchRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['tasks'],
            }),
            unassignTaskSchedule: build.mutation<
                UnassignTaskScheduleApiResponse,
                UnassignTaskScheduleApiArg
            >({
                query: (queryArg) => ({
                    url: `/schedules/tasks/${queryArg.scheduleId}/unassign`,
                    method: 'POST',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['schedules'],
            }),
            addDeviceActivity: build.mutation<
                AddDeviceActivityApiResponse,
                AddDeviceActivityApiArg
            >({
                query: (queryArg) => ({
                    url: `/devices/activity`,
                    method: 'POST',
                    body: queryArg.addDeviceActivityRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['devices'],
            }),
            getLatestDeviceActivity: build.query<
                GetLatestDeviceActivityApiResponse,
                GetLatestDeviceActivityApiArg
            >({
                query: (queryArg) => ({
                    url: `/devices/activity/latest`,
                    method: 'POST',
                    body: queryArg.getLatestDeviceActivityRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['devices'],
            }),
            createResidentActivity: build.mutation<
                CreateResidentActivityApiResponse,
                CreateResidentActivityApiArg
            >({
                query: (queryArg) => ({
                    url: `/tasks/resident-activity`,
                    method: 'POST',
                    body: queryArg.createResidentActivityRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residentActivities', 'tasks', 'careTask'],
            }),
            unsetTaskScheduleDuration: build.mutation<
                UnsetTaskScheduleDurationApiResponse,
                UnsetTaskScheduleDurationApiArg
            >({
                query: (queryArg) => ({
                    url: `/schedules/tasks/${queryArg.scheduleId}/unset-duration`,
                    method: 'POST',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['schedules'],
            }),
            getCompanyPhones: build.query<GetCompanyPhonesApiResponse, GetCompanyPhonesApiArg>({
                query: (queryArg) => ({
                    url: `/companyPhones`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        enterpriseId: queryArg.enterpriseId,
                        facilityId: queryArg.facilityId,
                        availablePhonesOnly: queryArg.availablePhonesOnly,
                    },
                }),
                providesTags: ['companyPhones'],
            }),
            createCompanyPhone: build.mutation<
                CreateCompanyPhoneApiResponse,
                CreateCompanyPhoneApiArg
            >({
                query: (queryArg) => ({
                    url: `/companyPhones`,
                    method: 'POST',
                    body: queryArg.createOrUpdateCompanyPhoneRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['companyPhones'],
            }),
            deleteCompanyPhones: build.mutation<
                DeleteCompanyPhonesApiResponse,
                DeleteCompanyPhonesApiArg
            >({
                query: (queryArg) => ({
                    url: `/companyPhones`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { companyPhoneRids: queryArg.companyPhoneRids },
                }),
                invalidatesTags: ['companyPhones'],
            }),
            getCompanyPhone: build.query<GetCompanyPhoneApiResponse, GetCompanyPhoneApiArg>({
                query: (queryArg) => ({
                    url: `/companyPhones/${queryArg.companyPhoneId}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['companyPhones'],
            }),
            updateCompanyPhone: build.mutation<
                UpdateCompanyPhoneApiResponse,
                UpdateCompanyPhoneApiArg
            >({
                query: (queryArg) => ({
                    url: `/companyPhones/${queryArg.companyPhoneId}`,
                    method: 'PUT',
                    body: queryArg.createOrUpdateCompanyPhoneRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['companyPhones'],
            }),
            deleteCompanyPhone: build.mutation<
                DeleteCompanyPhoneApiResponse,
                DeleteCompanyPhoneApiArg
            >({
                query: (queryArg) => ({
                    url: `/companyPhones/${queryArg.companyPhoneId}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['companyPhones'],
            }),
            createCompanyPhones: build.mutation<
                CreateCompanyPhonesApiResponse,
                CreateCompanyPhonesApiArg
            >({
                query: (queryArg) => ({
                    url: `/companyPhones/bulk`,
                    method: 'POST',
                    body: queryArg.bulkCreateCompanyPhonesRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['companyPhones'],
            }),
            getHospitalForResident: build.query<
                GetHospitalForResidentApiResponse,
                GetHospitalForResidentApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/hospital`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['residents'],
            }),
            setHospitalForResident: build.mutation<
                SetHospitalForResidentApiResponse,
                SetHospitalForResidentApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/hospital`,
                    method: 'PUT',
                    body: queryArg.createOrUpdateHospitalRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            deleteHospitalForResident: build.mutation<
                DeleteHospitalForResidentApiResponse,
                DeleteHospitalForResidentApiArg
            >({
                query: (queryArg) => ({
                    url: `/residents/${queryArg.residentId}/hospital`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['residents'],
            }),
            getCareZone: build.query<GetCareZoneApiResponse, GetCareZoneApiArg>({
                query: (queryArg) => ({
                    url: `/careZones/${queryArg.careZoneId}`,
                    headers: { Authorization: queryArg?.authorization },
                }),
                providesTags: ['careZones'],
            }),
            updateCareZone: build.mutation<UpdateCareZoneApiResponse, UpdateCareZoneApiArg>({
                query: (queryArg) => ({
                    url: `/careZones/${queryArg.careZoneId}`,
                    method: 'POST',
                    body: queryArg.createOrUpdateCareZoneRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['careZones'],
            }),
            deleteCareZone: build.mutation<DeleteCareZoneApiResponse, DeleteCareZoneApiArg>({
                query: (queryArg) => ({
                    url: `/careZones/${queryArg.careZoneId}`,
                    method: 'DELETE',
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['careZones'],
            }),
            getAllCareZones: build.query<GetAllCareZonesApiResponse, GetAllCareZonesApiArg>({
                query: (queryArg) => ({
                    url: `/careZones`,
                    headers: { Authorization: queryArg?.authorization },
                    params: {
                        enterpriseRid: queryArg.enterpriseRid,
                        facilityRid: queryArg.facilityRid,
                    },
                }),
                providesTags: ['careZones'],
            }),
            createCareZone: build.mutation<CreateCareZoneApiResponse, CreateCareZoneApiArg>({
                query: (queryArg) => ({
                    url: `/careZones`,
                    method: 'POST',
                    body: queryArg.createOrUpdateCareZoneRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['careZones'],
            }),
            getOnCallStaffMembers: build.query<
                GetOnCallStaffMembersApiResponse,
                GetOnCallStaffMembersApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/oncall`,
                    method: 'POST',
                    body: queryArg.staffOnCallRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['staff'],
            }),
            unitsSearch: build.query<UnitsSearchApiResponse, UnitsSearchApiArg>({
                query: (queryArg) => ({
                    url: `/units/search`,
                    method: 'POST',
                    body: queryArg.unitSearchRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['units'],
            }),
            getCameras: build.query<GetCamerasApiResponse, GetCamerasApiArg>({
                query: (queryArg) => ({
                    url: `/units/cameras`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { unitRid: queryArg.unitRid, facilityRid: queryArg.facilityRid },
                }),
                providesTags: ['units'],
            }),
            checkInSearch: build.query<CheckInSearchApiResponse, CheckInSearchApiArg>({
                query: (queryArg) => ({
                    url: `/checkIns/search`,
                    method: 'POST',
                    body: queryArg.checkInSearchRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['checkIns'],
            }),
            createStaffFacilityPreferences: build.mutation<
                CreateStaffFacilityPreferencesApiResponse,
                CreateStaffFacilityPreferencesApiArg
            >({
                query: (queryArg) => ({
                    url: `/staffFacilityPreferences`,
                    method: 'POST',
                    body: queryArg.createStaffFacilityPreferencesRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['staffPreferences'],
            }),
            getStaffFacilityPreferences: build.query<
                GetStaffFacilityPreferencesApiResponse,
                GetStaffFacilityPreferencesApiArg
            >({
                query: (queryArg) => ({
                    url: `/staffFacilityPreferences/staff/${queryArg.staffMemberId}/facility/${queryArg.facilityId}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['staffPreferences'],
            }),
            updateStaffFacilityPreferences: build.mutation<
                UpdateStaffFacilityPreferencesApiResponse,
                UpdateStaffFacilityPreferencesApiArg
            >({
                query: (queryArg) => ({
                    url: `/staffFacilityPreferences/staff/${queryArg.staffMemberId}/facility/${queryArg.facilityId}`,
                    method: 'PUT',
                    body: queryArg.updateStaffFacilityPreferencesRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['staffPreferences'],
            }),
            deleteStaffFacilityPreferences: build.mutation<
                DeleteStaffFacilityPreferencesApiResponse,
                DeleteStaffFacilityPreferencesApiArg
            >({
                query: (queryArg) => ({
                    url: `/staffFacilityPreferences/staff/${queryArg.staffMemberId}/facility/${queryArg.facilityId}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['staffPreferences'],
            }),
            searchStaffFacilityPreferences: build.mutation<
                SearchStaffFacilityPreferencesApiResponse,
                SearchStaffFacilityPreferencesApiArg
            >({
                query: (queryArg) => ({
                    url: `/staffFacilityPreferences/search`,
                    method: 'POST',
                    body: queryArg.staffFacilityPreferenceSearchRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['staffPreferences'],
            }),
            createStaffMemberFacilityConfig: build.mutation<
                CreateStaffMemberFacilityConfigApiResponse,
                CreateStaffMemberFacilityConfigApiArg
            >({
                query: (queryArg) => ({
                    url: `/staffMemberFacilityConfig`,
                    method: 'POST',
                    body: queryArg.createOrUpdateStaffMemberFacilityConfigRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['staff'],
            }),
            getStaffMemberFacilityConfig: build.query<
                GetStaffMemberFacilityConfigApiResponse,
                GetStaffMemberFacilityConfigApiArg
            >({
                query: (queryArg) => ({
                    url: `/staffMemberFacilityConfig/${queryArg.staffMemberFacilityConfigId}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['staff'],
            }),
            updateStaffMemberFacilityConfig: build.mutation<
                UpdateStaffMemberFacilityConfigApiResponse,
                UpdateStaffMemberFacilityConfigApiArg
            >({
                query: (queryArg) => ({
                    url: `/staffMemberFacilityConfig/${queryArg.staffMemberFacilityConfigId}`,
                    method: 'PUT',
                    body: queryArg.createOrUpdateStaffMemberFacilityConfigRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['staff'],
            }),
            deleteStaffMemberFacilityConfig: build.mutation<
                DeleteStaffMemberFacilityConfigApiResponse,
                DeleteStaffMemberFacilityConfigApiArg
            >({
                query: (queryArg) => ({
                    url: `/staffMemberFacilityConfig/${queryArg.staffMemberFacilityConfigId}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['staff'],
            }),
            searchStaffMemberFacilityConfigs: build.mutation<
                SearchStaffMemberFacilityConfigsApiResponse,
                SearchStaffMemberFacilityConfigsApiArg
            >({
                query: (queryArg) => ({
                    url: `/staffMemberFacilityConfig/search`,
                    method: 'POST',
                    body: queryArg.staffMemberFacilityConfigSearchRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['staff'],
            }),
            staffProfileSearch: build.query<
                StaffProfileSearchApiResponse,
                StaffProfileSearchApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/profile/search`,
                    method: 'POST',
                    body: queryArg.staffProfileSearchRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['staff'],
            }),
            getUserStaffMemberProfiles: build.query<
                GetUserStaffMemberProfilesApiResponse,
                GetUserStaffMemberProfilesApiArg
            >({
                query: (queryArg) => ({
                    url: `/staff/profiles`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['staff'],
            }),
            createDoor: build.mutation<CreateDoorApiResponse, CreateDoorApiArg>({
                query: (queryArg) => ({
                    url: `/doors`,
                    method: 'POST',
                    body: queryArg.items,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['doors'],
            }),
            deleteDoors: build.mutation<DeleteDoorsApiResponse, DeleteDoorsApiArg>({
                query: (queryArg) => ({
                    url: `/doors`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { doorRids: queryArg.doorRids },
                }),
                invalidatesTags: ['doors'],
            }),
            createDoors: build.mutation<CreateDoorsApiResponse, CreateDoorsApiArg>({
                query: (queryArg) => ({
                    url: `/doors/bulk`,
                    method: 'POST',
                    body: queryArg.bulkCreateDoorsRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['doors'],
            }),
            getDoor: build.query<GetDoorApiResponse, GetDoorApiArg>({
                query: (queryArg) => ({
                    url: `/doors/${queryArg.doorRid}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['doors'],
            }),
            updateDoor: build.mutation<UpdateDoorApiResponse, UpdateDoorApiArg>({
                query: (queryArg) => ({
                    url: `/doors/${queryArg.doorRid}`,
                    method: 'POST',
                    body: queryArg.items,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['doors'],
            }),
            deleteDoor: build.mutation<DeleteDoorApiResponse, DeleteDoorApiArg>({
                query: (queryArg) => ({
                    url: `/doors/${queryArg.doorRid}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['doors'],
            }),
            createOrUpdateDoorAlarmSchedule: build.mutation<
                CreateOrUpdateDoorAlarmScheduleApiResponse,
                CreateOrUpdateDoorAlarmScheduleApiArg
            >({
                query: (queryArg) => ({
                    url: `/doors/doorAlarmSchedules`,
                    method: 'POST',
                    body: queryArg.createOrUpdateDoorAlarmScheduleRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['doorAlarms'],
            }),
            getDoorAlarmSchedule: build.query<
                GetDoorAlarmScheduleApiResponse,
                GetDoorAlarmScheduleApiArg
            >({
                query: (queryArg) => ({
                    url: `/doors/doorAlarmSchedules/${queryArg.doorAlarmScheduleRid}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['doorAlarms'],
            }),
            deleteDoorAlarmSchedule: build.mutation<
                DeleteDoorAlarmScheduleApiResponse,
                DeleteDoorAlarmScheduleApiArg
            >({
                query: (queryArg) => ({
                    url: `/doors/doorAlarmSchedules/${queryArg.doorAlarmScheduleRid}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['doorAlarms'],
            }),
            createOrUpdateFacilityDefaultDoorAlarmSchedule: build.mutation<
                CreateOrUpdateFacilityDefaultDoorAlarmScheduleApiResponse,
                CreateOrUpdateFacilityDefaultDoorAlarmScheduleApiArg
            >({
                query: (queryArg) => ({
                    url: `/doors/doorAlarmSchedules/defaults`,
                    method: 'POST',
                    body: queryArg.createOrUpdateDoorAlarmScheduleRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['doorAlarms'],
            }),
            getFacilityDefaultDoorAlarmSchedule: build.query<
                GetFacilityDefaultDoorAlarmScheduleApiResponse,
                GetFacilityDefaultDoorAlarmScheduleApiArg
            >({
                query: (queryArg) => ({
                    url: `/doors/doorAlarmSchedules/defaults`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityRid: queryArg.facilityRid },
                }),
                providesTags: ['doorAlarms'],
            }),
            deleteFacilityDefaultDoorAlarmSchedule: build.mutation<
                DeleteFacilityDefaultDoorAlarmScheduleApiResponse,
                DeleteFacilityDefaultDoorAlarmScheduleApiArg
            >({
                query: (queryArg) => ({
                    url: `/doors/doorAlarmSchedules/defaults`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityRid: queryArg.facilityRid },
                }),
                invalidatesTags: ['doorAlarms'],
            }),
            searchDoors: build.query<SearchDoorsApiResponse, SearchDoorsApiArg>({
                query: (queryArg) => ({
                    url: `/doors/search`,
                    method: 'POST',
                    body: queryArg.doorSearchRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['doors'],
            }),
            searchDoorAlarmSchedules: build.query<
                SearchDoorAlarmSchedulesApiResponse,
                SearchDoorAlarmSchedulesApiArg
            >({
                query: (queryArg) => ({
                    url: `/doors/doorAlarmsSchedules/search`,
                    method: 'POST',
                    body: queryArg.doorAlarmScheduleSearchRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['doorAlarms'],
            }),
            getAllFeatures: build.query<GetAllFeaturesApiResponse, GetAllFeaturesApiArg>({
                query: (queryArg) => ({
                    url: `/features`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityId: queryArg.facilityId },
                }),
                providesTags: ['features'],
            }),
            createFeature: build.mutation<CreateFeatureApiResponse, CreateFeatureApiArg>({
                query: (queryArg) => ({
                    url: `/features`,
                    method: 'POST',
                    body: queryArg.createFeatureRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['features'],
            }),
            getFeature: build.query<GetFeatureApiResponse, GetFeatureApiArg>({
                query: (queryArg) => ({
                    url: `/features/${queryArg.featureId}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['features'],
            }),
            updateFeature: build.mutation<UpdateFeatureApiResponse, UpdateFeatureApiArg>({
                query: (queryArg) => ({
                    url: `/features/${queryArg.featureId}`,
                    method: 'PUT',
                    body: queryArg.updateFeatureRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['features'],
            }),
            deleteFeature: build.mutation<DeleteFeatureApiResponse, DeleteFeatureApiArg>({
                query: (queryArg) => ({
                    url: `/features/${queryArg.featureId}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['features'],
            }),
            getNotificationContext: build.mutation<
                GetNotificationContextApiResponse,
                GetNotificationContextApiArg
            >({
                query: (queryArg) => ({
                    url: `/notificationContexts`,
                    method: 'POST',
                    body: queryArg.getNotificationContextRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
            }),
            createNotificationPolicy: build.mutation<
                CreateNotificationPolicyApiResponse,
                CreateNotificationPolicyApiArg
            >({
                query: (queryArg) => ({
                    url: `/notificationPolicies`,
                    method: 'POST',
                    body: queryArg.notificationPolicy,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityRid: queryArg.facilityRid },
                }),
                invalidatesTags: ['notificationPolicies'],
            }),
            getNotificationPolicies: build.query<
                GetNotificationPoliciesApiResponse,
                GetNotificationPoliciesApiArg
            >({
                query: (queryArg) => ({
                    url: `/notificationPolicies`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityRid: queryArg.facilityRid },
                }),
                providesTags: ['notificationPolicies'],
            }),
            getNotificationPolicy: build.query<
                GetNotificationPolicyApiResponse,
                GetNotificationPolicyApiArg
            >({
                query: (queryArg) => ({
                    url: `/notificationPolicies/${queryArg.notificationPolicyRid}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        facilityRid: queryArg.facilityRid,
                        notificationPolicyVersion: queryArg.notificationPolicyVersion,
                    },
                }),
                providesTags: ['notificationPolicies'],
            }),
            updateNotificationPolicy: build.mutation<
                UpdateNotificationPolicyApiResponse,
                UpdateNotificationPolicyApiArg
            >({
                query: (queryArg) => ({
                    url: `/notificationPolicies/${queryArg.notificationPolicyRid}`,
                    method: 'PATCH',
                    body: queryArg.notificationPolicy,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityRid: queryArg.facilityRid },
                }),
                invalidatesTags: ['notificationPolicies'],
            }),
            createNotificationBlock: build.mutation<
                CreateNotificationBlockApiResponse,
                CreateNotificationBlockApiArg
            >({
                query: (queryArg) => ({
                    url: `/notificationPolicies/${queryArg.notificationPolicyRid}/blocks`,
                    method: 'POST',
                    body: queryArg.notificationBlock,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityRid: queryArg.facilityRid },
                }),
                invalidatesTags: ['notificationPolicies'],
            }),
            updateNotificationBlock: build.mutation<
                UpdateNotificationBlockApiResponse,
                UpdateNotificationBlockApiArg
            >({
                query: (queryArg) => ({
                    url: `/notificationPolicies/${queryArg.notificationPolicyRid}/blocks/${queryArg.blockRid}`,
                    method: 'PATCH',
                    body: queryArg.notificationBlock,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityRid: queryArg.facilityRid },
                }),
                invalidatesTags: ['notificationPolicies'],
            }),
            deleteNotificationBlock: build.mutation<
                DeleteNotificationBlockApiResponse,
                DeleteNotificationBlockApiArg
            >({
                query: (queryArg) => ({
                    url: `/notificationPolicies/${queryArg.notificationPolicyRid}/blocks/${queryArg.blockRid}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityRid: queryArg.facilityRid },
                }),
                invalidatesTags: ['notificationPolicies'],
            }),
            getNotificationFilter: build.query<
                GetNotificationFilterApiResponse,
                GetNotificationFilterApiArg
            >({
                query: (queryArg) => ({
                    url: `/notificationPolicies/blocks/${queryArg.blockRid}/filters/${queryArg.filterRid}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityRid: queryArg.facilityRid },
                }),
                providesTags: ['notificationPolicies'],
            }),
            updateNotificationFilter: build.mutation<
                UpdateNotificationFilterApiResponse,
                UpdateNotificationFilterApiArg
            >({
                query: (queryArg) => ({
                    url: `/notificationPolicies/blocks/${queryArg.blockRid}/filters/${queryArg.filterRid}`,
                    method: 'PATCH',
                    body: queryArg.updateNotificationFilterRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityRid: queryArg.facilityRid },
                }),
                invalidatesTags: ['notificationPolicies'],
            }),
            deleteNotificationFilter: build.mutation<
                DeleteNotificationFilterApiResponse,
                DeleteNotificationFilterApiArg
            >({
                query: (queryArg) => ({
                    url: `/notificationPolicies/blocks/${queryArg.blockRid}/filters/${queryArg.filterRid}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { facilityRid: queryArg.facilityRid },
                }),
                invalidatesTags: ['notificationPolicies'],
            }),
            simulateDeviceEvent: build.mutation<
                SimulateDeviceEventApiResponse,
                SimulateDeviceEventApiArg
            >({
                query: (queryArg) => ({
                    url: `/devices/${queryArg.deviceType}/simulate`,
                    method: 'POST',
                    body: queryArg.simulateDeviceEventRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
            }),
            getShiftInstances: build.query<GetShiftInstancesApiResponse, GetShiftInstancesApiArg>({
                query: (queryArg) => ({
                    url: `/shiftInstances`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: {
                        facilityRid: queryArg.facilityRid,
                        currentTimeMs: queryArg.currentTimeMs,
                    },
                }),
                providesTags: ['shiftInstances'],
            }),
            upsertShiftInstances: build.mutation<
                UpsertShiftInstancesApiResponse,
                UpsertShiftInstancesApiArg
            >({
                query: (queryArg) => ({
                    url: `/shiftInstances`,
                    method: 'POST',
                    body: queryArg.upsertShiftInstancesRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['shiftInstances'],
            }),
        }),
        overrideExisting: false,
    });
export { injectedRtkApi as sageAdminApi };
export type GetCareEventApiResponse = /** status 200 OK */ CareEventResponse;
export type GetCareEventApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careEventId: string;
    /** The facility RID. Required for the time being for authorization checks since care event
    is not a resource on the resource graph. Will check for the [resident:read-phi] scope.
     */
    facilityId: string;
};
export type AddCareEventNoteApiResponse = /** status 200 OK */ RidResponse;
export type AddCareEventNoteApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careEventId: string;
    /** The facility RID. Required for the time being for authorization checks since care event
    is not a resource on the resource graph. Will check for the [resident:read-phi] scope.
     */
    facilityId: string;
    createCareEventNoteRequest: {
        /** This enum is add only. If you delete or update the values below, the api will break. */
        noteType: 'CREATION_NOTE' | 'CAREGIVER_NOTE' | 'NURSE_ACKNOWLEDGEMENT';
        content: string;
        creatorRid: string;
    };
};
export type GetCareTasksApiResponse = /** status 200 OK */ GetCareTasksResponse;
export type GetCareTasksApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    facilityId: string;
    shiftInstanceId: string;
    /** Cannot be set in the same request as staffMemberId, otherwise a 400 will be returned. Either residentIds or staffMemberId must be provided. */
    residentIds?: string[];
    /** Optional. Cannot be set in the same request as residentIds, otherwise a 400 will be returned. If set, the BE will retrieve the saved care assignment resident ids for this staff member and return care tasks for those residents. */
    staffMemberId?: string;
    /** if true, will enrich GetCareTasksResponse.tasks. these tasks should not be confused with care tasks */
    enrichTasks?: boolean;
    /** if true, will populate GetCareTasksResponse.careItems with care items regardless of whether or not care tasks exist. */
    enrichCareItems?: boolean;
};
export type UpdateCareTaskApiResponse = /** status 200 OK */ UpdateCareTaskResponse;
export type UpdateCareTaskApiArg = {
    authorization?: string;
    careTaskId: string;
    updateCareTaskRequest: UpdateCareTaskRequest;
};
export type AddEhrLockedFieldsApiResponse = /** status 200 OK */ AddEhrLockedFieldsResponse;
export type AddEhrLockedFieldsApiArg = {
    authorization?: string;
    addEhrLockedFieldsRequest: AddEhrLockedFieldsRequest;
};
export type DeleteEhrLockedFieldApiResponse = /** status 200 OK */ EhrLockedField;
export type DeleteEhrLockedFieldApiArg = {
    authorization?: string;
    ehrLockedFieldRid: string;
};
export type CreateResidentApiResponse = /** status 200 OK */ RidResponse;
export type CreateResidentApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateResidentRequest: CreateOrUpdateResidentRequest;
};
export type GetResidentsApiResponse = /** status 200 OK */ Resident[];
export type GetResidentsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    /** unit id of the resident */
    unitId?: string;
    /** facility id of the resident */
    facilityId?: string;
    /** enterprise id of the resident */
    enterpriseId?: string;
    /** whether or not a resident has an open event associated with them */
    hasOpenEvent?: boolean;
    /** whether or not a resident is archived */
    isArchived?: boolean;
};
export type CreateResidentsApiResponse = /** status 200 OK */ RidArray;
export type CreateResidentsApiArg = {
    authorization?: string;
    bulkCreateResidentsRequest: BulkCreateResidentsRequest;
};
export type BulkSetRoutineCheckStatusApiResponse = unknown;
export type BulkSetRoutineCheckStatusApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    /** List of resident rids to update */
    bulkSetRoutineCheckRequest: ToggleRoutineCheckRequest;
};
export type GetUnitsApiResponse = /** status 200 OK */ Unit[];
export type GetUnitsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    /** id of the resident occupying a unit */
    residentId?: string;
    /** facility id */
    facilityId?: string;
    /** enterpriseId */
    enterpriseId?: string;
};
export type CreateUnitApiResponse = /** status 200 OK */ {
    rid?: string;
};
export type CreateUnitApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateUnitRequest: CreateOrUpdateUnitRequest;
};
export type DeleteUnitsApiResponse = /** status 200 OK */ Unit[];
export type DeleteUnitsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    unitRid?: string[];
};
export type CreateUnitsApiResponse = /** status 200 OK */ RidArray;
export type CreateUnitsApiArg = {
    authorization?: string;
    bulkCreateUnitRequestForFacility: BulkCreateUnitRequestForFacility;
};
export type UpdateUnitsApiResponse = /** status 200 OK */ RidArray;
export type UpdateUnitsApiArg = {
    authorization?: string;
    bulkUpdateUnitsRequest: BulkUpdateUnitsRequest;
};
export type GetCheckInsApiResponse = /** status 200 OK */ CheckIn[];
export type GetCheckInsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    /** date of check in */
    date?: string;
    /** unit id */
    unitId?: string;
    /** care zone id */
    careZoneId?: string;
    /** facility id */
    facilityId?: string;
    /** enterprise id */
    enterpriseId?: string;
    /** check in status */
    status?: string;
    /** whether or not all occupants of the unit are on hold */
    isOnHold?: boolean;
};
export type CreateCheckInApiResponse = /** status 200 OK */ RidResponse;
export type CreateCheckInApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createCheckInRequest: CreateCheckInRequest;
};
export type UpdateCheckInApiResponse = /** status 200 OK */ RidResponse;
export type UpdateCheckInApiArg = {
    checkInId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    updateCheckInRequest: UpdateCheckInRequest;
};
export type GetCheckInApiResponse = /** status 200 OK */ CheckIn;
export type GetCheckInApiArg = {
    checkInId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type CreateStaffMemberApiResponse = /** status 200 OK */ RidResponse;
export type CreateStaffMemberApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateStaffMemberRequest: CreateOrUpdateStaffMemberRequest;
};
export type GetStaffMembersApiResponse = /** status 200 OK */ StaffMember[];
export type GetStaffMembersApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    /** id of the enterprise the staff member is associated with */
    enterpriseId?: string;
    /** phone number of the staff member */
    phoneNumber?: string;
    /** facility the staff member belongs to */
    facilityId?: string;
    /** a group the staff member belongs to */
    groupId?: string;
    /** whether or not a staff member is archived */
    isArchived?: boolean;
};
export type UpdateStaffMemberPermissionsApiResponse =
    /** status 200 OK */ UpdateStaffPermissionsResponse;
export type UpdateStaffMemberPermissionsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    updateStaffPermissionsRequest: UpdateStaffPermissionsRequest;
};
export type GetStaffWithFallReplayViewApiResponse =
    /** status 200 OK */ GetStaffWithFallReplayViewResponse;
export type GetStaffWithFallReplayViewApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    facilityRid: string;
};
export type GetStaffPermissionsApiResponse = /** status 200 OK */ GetStaffPermissionsResponse;
export type GetStaffPermissionsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    staffMemberId: string;
    facilityRid: string;
};
export type GrantRoleOnResourceApiResponse = unknown;
export type GrantRoleOnResourceApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    staffMemberId: string;
    resourceRid: string;
    staffPermissionRequest: StaffPermissionRequest;
};
export type RevokeRoleOnResourceApiResponse = unknown;
export type RevokeRoleOnResourceApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    staffMemberId: string;
    resourceRid: string;
    staffPermissionRequest: StaffPermissionRequest;
};
export type UpdatePhoneNumberVerificationStatusApiResponse = /** status 200 OK */ RidResponse;
export type UpdatePhoneNumberVerificationStatusApiArg = {
    authorization?: string;
    staffMemberId: string;
    updatePhoneNumberVerificationRequest: UpdatePhoneNumberVerificationRequest;
};
export type CreateStaffMembersApiResponse = /** status 200 OK */ CreateStaffMembersResponse;
export type CreateStaffMembersApiArg = {
    authorization?: string;
    bulkCreateStaffMembersRequest: BulkCreateStaffMembersRequest;
};
export type MergeStaffProfilesApiResponse = /** status 200 OK */ MergeStaffProfilesResponse;
export type MergeStaffProfilesApiArg = {
    authorization?: string;
    mergeStaffProfilesRequest: MergeStaffProfilesRequest;
};
export type GetContactsOfResidentApiResponse = /** status 200 OK */ Contact[];
export type GetContactsOfResidentApiArg = {
    /** id of the resident */
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type CreateContactForResidentApiResponse = /** status 200 OK */ RidResponse;
export type CreateContactForResidentApiArg = {
    /** id of the resident */
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateContactRequest: CreateOrUpdateContactRequest;
};
export type GetResidentActivitiesApiResponse = /** status 200 OK */ GetResidentActivitiesResponse;
export type GetResidentActivitiesApiArg = {
    /** id of the resident */
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    careOutcomes?: OutcomeType[];
    careProvided?: CareProvided[];
    pageNumber?: number;
    pageSize?: number;
};
export type GetEnterprisesApiResponse = /** status 200 OK */ Enterprise[];
export type GetEnterprisesApiArg = {
    authorization?: string;
};
export type CreateEnterpriseApiResponse = /** status 200 OK */ RidResponse;
export type CreateEnterpriseApiArg = {
    authorization?: string;
    createEnterpriseRequest: CreateEnterpriseRequest;
};
export type GetFacilitiesApiResponse = /** status 200 OK */ Facility[];
export type GetFacilitiesApiArg = {
    /** id of enterprise */
    enterpriseId?: string;
    authorization?: string;
    /** phone number for the emergency notification sender */
    emergencyNotificationNumber?: string;
};
export type CreateFacilityApiResponse = /** status 200 OK */ RidResponse;
export type CreateFacilityApiArg = {
    authorization?: string;
    createFacilityRequest: CreateFacilityRequest;
};
export type CreateTaskApiResponse = /** status 200 OK */ RidResponse;
export type CreateTaskApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateTaskRequest: CreateOrUpdateTaskRequest;
};
export type GetGroupsApiResponse = /** status 200 OK */ Group[];
export type GetGroupsApiArg = {
    /** facility id */
    facilityId?: string;
    /** enterprise id */
    enterpriseId?: string;
    authorization?: string;
    /** staff member id */
    staffMemberId?: string;
};
export type CreateGroupApiResponse = /** status 200 OK */ RidResponse;
export type CreateGroupApiArg = {
    authorization?: string;
    createOrUpdateGroupRequest: CreateOrUpdateGroupRequest;
};
export type GetEnterpriseApiResponse = /** status 200 OK */ Enterprise;
export type GetEnterpriseApiArg = {
    enterpriseId: string;
    authorization?: string;
};
export type UpdateEnterpriseApiResponse = /** status 200 OK */ RidResponse;
export type UpdateEnterpriseApiArg = {
    enterpriseId: string;
    authorization?: string;
    updateEnterpriseRequest: UpdateEnterpriseRequest;
};
export type DeleteEnterpriseApiResponse = /** status 200 OK */ Enterprise;
export type DeleteEnterpriseApiArg = {
    enterpriseId: string;
    authorization?: string;
};
export type RetryEnterpriseSsoEnforcementApiResponse = unknown;
export type RetryEnterpriseSsoEnforcementApiArg = {
    enterpriseId: string;
    authorization?: string;
};
export type GetFacilityApiResponse = /** status 200 OK */ Facility;
export type GetFacilityApiArg = {
    facilityId: string;
    'x-sage-profile-rid'?: string | null;
    authorization?: string;
};
export type UpdateFacilityApiResponse = /** status 200 OK */ RidResponse;
export type UpdateFacilityApiArg = {
    facilityId: string;
    'x-sage-profile-rid'?: string | null;
    authorization?: string;
    updateFacilityRequest: UpdateFacilityRequest;
};
export type DeleteFacilityApiResponse = /** status 200 OK */ Facility;
export type DeleteFacilityApiArg = {
    facilityId: string;
    'x-sage-profile-rid'?: string | null;
    authorization?: string;
};
export type UpdateFacilityStatusApiResponse = /** status 200 OK */ RidResponse;
export type UpdateFacilityStatusApiArg = {
    facilityRid: string;
    authorization?: string;
    updateFacilityStatusRequest: {
        /** Facility status (PRE_LIVE is deprecated. Use QA instead.) */
        status: 'QA' | 'PRE_LIVE' | 'LIVE';
    };
};
export type GetGroupApiResponse = /** status 200 OK */ Group;
export type GetGroupApiArg = {
    groupId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type UpdateGroupApiResponse = /** status 200 OK */ RidResponse;
export type UpdateGroupApiArg = {
    groupId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateGroupRequest: CreateOrUpdateGroupRequest;
};
export type DeleteGroupApiResponse = /** status 200 OK */ Group;
export type DeleteGroupApiArg = {
    groupId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type GetResidentApiResponse = /** status 200 OK */ Resident;
export type GetResidentApiArg = {
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    /** Include resident's active expected exits. Returns 10 most recent exits */
    enrichExpectedExits?: boolean;
};
export type UpdateResidentApiResponse = /** status 200 OK */ RidResponse;
export type UpdateResidentApiArg = {
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateResidentRequest: CreateOrUpdateResidentRequest;
};
export type DeleteResidentApiResponse = /** status 200 OK */ Resident;
export type DeleteResidentApiArg = {
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type CreateExpectedExitApiResponse = /** status 200 OK */ RidResponse;
export type CreateExpectedExitApiArg = {
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    createResidentExpectedExitRequest: CreateResidentExpectedExitRequest;
};
export type DeleteResidentExpectedExitApiResponse = unknown;
export type DeleteResidentExpectedExitApiArg = {
    residentId: string;
    expectedExitRid: string;
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
};
export type UpdateResidentDetectConsentApiResponse = /** status 200 OK */ RidResponse;
export type UpdateResidentDetectConsentApiArg = {
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    updateResidentDetectConsentRequest: UpdateResidentDetectConsentRequest;
};
export type GetLatestCareActivityApiResponse = /** status 200 OK */ LatestCareActivityMap;
export type GetLatestCareActivityApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    getLatestCareActivityRequest: GetLatestCareActivityRequest;
};
export type GetContactOfResidentApiResponse = /** status 200 OK */ {
    id?: string;
    firstName?: string;
    lastName?: string;
    primaryPhone?: string;
    secondaryPhone?: string;
    email?: string;
    relationship?: string;
    isLocal?: boolean;
    preferredContactMethod?: string;
    hasPowerOfAttorney?: boolean;
    address?: Address;
    salutation?: string;
    primaryLanguage?: string;
};
export type GetContactOfResidentApiArg = {
    contactId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type UpdateContactApiResponse = /** status 200 OK */ RidResponse;
export type UpdateContactApiArg = {
    contactId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateContactRequest: CreateOrUpdateContactRequest;
};
export type DeleteContactApiResponse = /** status 200 OK */ Contact;
export type DeleteContactApiArg = {
    contactId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type GetStaffMemberApiResponse = /** status 200 OK */ StaffMember;
export type GetStaffMemberApiArg = {
    staffMemberId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type UpdateStaffMemberApiResponse = /** status 200 OK */ RidResponse;
export type UpdateStaffMemberApiArg = {
    staffMemberId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateStaffMemberRequest: CreateOrUpdateStaffMemberRequest;
};
export type ArchiveStaffMemberApiResponse = /** status 200 OK */ StaffMember;
export type ArchiveStaffMemberApiArg = {
    staffMemberId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type AssignPhoneNumberApiResponse = /** status 200 OK */ string;
export type AssignPhoneNumberApiArg = {
    staffMemberId: string;
    facilityRid: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    body: {
        /** The requested phone number which will be verified */
        phoneNumber: string;
    };
};
export type VerifyPhoneNumberApiResponse = unknown;
export type VerifyPhoneNumberApiArg = {
    staffMemberId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    body: {
        /** The phone number to verify */
        phoneNumber: string;
        /** The verification code */
        code: string;
    };
};
export type GetTaskApiResponse = /** status 200 OK */ Task;
export type GetTaskApiArg = {
    taskId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    /** Include PCC task followup answers if available */
    enrichFollowUpAnswers?: boolean;
    /** Include care tasks if available */
    enrichCareTasks?: boolean;
    /** Include task acknowledgements if available */
    enrichAcknowledgements?: boolean;
};
export type UpdateTaskApiResponse = /** status 200 OK */ RidResponse;
export type UpdateTaskApiArg = {
    taskId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateTaskRequest: CreateOrUpdateTaskRequest;
};
export type DeleteTaskApiResponse = /** status 200 OK */ Task;
export type DeleteTaskApiArg = {
    taskId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type ResolveTaskApiResponse = /** status 200 OK */ RidResponse;
export type ResolveTaskApiArg = {
    taskId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    resolveTaskRequest: ResolveTaskRequest;
};
export type ReviewTaskApiResponse = /** status 200 OK */ RidResponse;
export type ReviewTaskApiArg = {
    taskId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    reviewTaskRequest: ReviewTaskRequest;
};
export type AcknowledgeTaskApiResponse = unknown;
export type AcknowledgeTaskApiArg = {
    taskId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
};
export type GetCareConfirmationTrackerApiResponse = /** status 200 OK */ CareConfirmationTracker;
export type GetCareConfirmationTrackerApiArg = {
    taskId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type ResolveAllAlertsApiResponse = /** status 200 OK */ number;
export type ResolveAllAlertsApiArg = {
    facilityRid: string;
    authorization?: string;
    resolveAlertsRequest: {
        staffMemberRid?: string;
    };
};
export type SyncOfflineAlertApiResponse =
    /** status 200 The trail was applied, or had already been applied under the same idempotencyKey. Returns the closed Task either way. */ SyncOfflineAlertResponse;
export type SyncOfflineAlertApiArg = {
    authorization?: string;
    syncOfflineAlertRequest: SyncOfflineAlertRequest;
};
export type GetUnitApiResponse = /** status 200 OK */ Unit;
export type GetUnitApiArg = {
    unitId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type UpdateUnitApiResponse = /** status 200 OK */ RidResponse;
export type UpdateUnitApiArg = {
    unitId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateUnitRequest: CreateOrUpdateUnitRequest;
};
export type DeleteUnitApiResponse = /** status 200 OK */ Unit;
export type DeleteUnitApiArg = {
    unitId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type CreateUpdateForTaskApiResponse = /** status 200 OK */ RidResponse;
export type CreateUpdateForTaskApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createTaskUpdateRequest: CreateTaskUpdateRequest;
};
export type DeleteUpdateApiResponse = /** status 200 OK */ TaskUpdate;
export type DeleteUpdateApiArg = {
    updateId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type GetAllocatedDevicesApiResponse = /** status 200 OK */ DeviceAllocation[];
export type GetAllocatedDevicesApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    unitRid?: string;
    doorRid?: string;
    facilityRid?: string;
    enterpriseRid?: string;
    type?: string;
    deviceTypes?: string[];
    residentRid?: string;
    deviceHardwareId?: string;
    isCheckIn?: boolean;
    isSilent?: boolean;
    deviceRid?: string;
};
export type AllocateDeviceApiResponse = /** status 200 OK */ RidResponse;
export type AllocateDeviceApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateDeviceAllocationRequest: {
        /** Deprecated. Use deviceRids instead. */
        deviceRid?: string;
        deviceRids?: string[];
        allocationType?: AllocationType;
        unitRid?: string;
        room?: string;
        facilityId?: string;
        enterpriseId?: string;
        displayName?: string;
        residentId?: string;
        isCheckIn?: boolean;
        doorRid?: string;
        isSilent?: boolean;
    };
};
export type SearchLatestAllocatedDeviceActivityApiResponse = /** status 200 OK */ {
    deviceRid: string;
    deviceAllocationRid?: string;
    deviceDisplayName?: string;
    createDateTimestampInEpochMs: number;
    latestUpdateTimestampInEpochMs?: number;
    /** Not every device's latest activity event carries a battery reading -- e.g. the Milesight
    WS203's Historical Data uplink never includes one (only its Battery Report does). Null means
    the originating event doesn't report battery, not a device fault or missing data.
     */
    batteryLevel?: number | null;
    facilityRid: string;
    deviceType?: string;
}[];
export type SearchLatestAllocatedDeviceActivityApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    searchLatestAllocatedDeviceActivityRequest: {
        facilityRid: string;
        toTimestampInEpochMs?: number;
        fromTimestampInEpochMs?: number;
        getOnlyUnassigned?: boolean;
        getOnlyAssigned?: boolean;
        /** Device types must be any of Sage Button or Door Alarm */
        deviceTypes?: string[];
    };
};
export type GetDeviceAllocationApiResponse = /** status 200 OK */ DeviceAllocation;
export type GetDeviceAllocationApiArg = {
    deviceAllocationId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type UpdateDeviceAllocationApiResponse = /** status 200 OK */ RidResponse;
export type UpdateDeviceAllocationApiArg = {
    deviceAllocationId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    schema: CreateOrUpdateDeviceAllocationRequest;
};
export type DeallocateDeviceApiResponse = /** status 200 OK */ DeviceAllocation;
export type DeallocateDeviceApiArg = {
    deviceAllocationId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type GetDevicesApiResponse = /** status 200 OK */ Device[];
export type GetDevicesApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createdBefore?: string;
    createdAfter?: string;
    type?: string;
    hardwareId?: string;
    isAllocated?: boolean;
};
export type CreateDeviceApiResponse = /** status 200 OK */ RidResponse;
export type CreateDeviceApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createDeviceRequest: CreateDeviceRequest;
};
export type GetDeviceApiResponse = /** status 200 OK */ Device;
export type GetDeviceApiArg = {
    deviceId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type DeleteDeviceApiResponse = /** status 200 OK */ Device;
export type DeleteDeviceApiArg = {
    deviceId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type CreateSpeakerApiResponse = /** status 200 OK */ RidResponse;
export type CreateSpeakerApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createSpeakerRequest: CreateSpeakerRequest;
};
export type GetSpeakersApiResponse = /** status 200 OK */ Speaker[];
export type GetSpeakersApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    /** FacilityRid to fetch speakers for */
    facilityRid?: string;
    /** UnitRid to fetch speakers for */
    unitRid?: string;
    /** Hardware ID of speaker to fetch */
    hardwareId?: string;
};
export type GetSpeakerApiResponse = /** status 200 OK */ Speaker;
export type GetSpeakerApiArg = {
    speakerRid: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type UpdateSpeakerApiResponse = /** status 200 OK */ Speaker;
export type UpdateSpeakerApiArg = {
    speakerRid: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    updateSpeakerRequest: UpdateSpeakerRequest;
};
export type DeleteSpeakerApiResponse = /** status 200 OK */ Speaker;
export type DeleteSpeakerApiArg = {
    speakerRid: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type CreateNurseStationApiResponse = /** status 200 OK */ RidResponse;
export type CreateNurseStationApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    createNurseStationRequest: CreateNurseStationRequest;
};
export type GetNurseStationsByFacilityApiResponse = /** status 200 OK */ NurseStation[];
export type GetNurseStationsByFacilityApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    facilityId: string;
};
export type GetNurseStationApiResponse = /** status 200 OK */ NurseStation;
export type GetNurseStationApiArg = {
    nurseStationId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
};
export type UpdateNurseStationApiResponse = /** status 200 OK */ RidResponse;
export type UpdateNurseStationApiArg = {
    nurseStationId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    updateNurseStationRequest: UpdateNurseStationRequest;
};
export type DeleteNurseStationApiResponse = /** status 200 OK */ NurseStation;
export type DeleteNurseStationApiArg = {
    nurseStationId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
};
export type GetOnCallStateApiResponse = /** status 200 OK */ OnCallState;
export type GetOnCallStateApiArg = {
    staffMemberId: string;
    facilityId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type UpdateOnCallStateApiResponse = /** status 200 OK */ OnCallState;
export type UpdateOnCallStateApiArg = {
    staffMemberId: string;
    facilityId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    updateOnCallStateRequest: UpdateOnCallStateRequest;
};
export type SetEnterpriseGroupMembershipsApiResponse = /** status 200 OK */ StaffMember;
export type SetEnterpriseGroupMembershipsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    staffMemberId: string;
    /** The RID of the enterprise whose groups should be targeted */
    enterpriseRid: string;
    setEnterpriseGroupMembershipsRequest: SetEnterpriseGroupMembershipsRequest;
};
export type GetMembershipDetailsApiResponse = /** status 200 OK */ GroupMembershipEntry[];
export type GetMembershipDetailsApiArg = {
    groupId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type UpdateGroupMembershipApiResponse = /** status 200 OK */ GroupMembershipEntry;
export type UpdateGroupMembershipApiArg = {
    groupId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateGroupMembershipRequest: CreateOrUpdateGroupMembershipRequest;
};
export type RemoveMemberFromGroupApiResponse = /** status 200 OK */ GroupMembershipEntry[];
export type RemoveMemberFromGroupApiArg = {
    groupId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    staffMemberId: string;
};
export type GetApplicationConfigurationApiResponse = /** status 200 OK */ ServiceConfiguration;
export type GetApplicationConfigurationApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type ResolveNumbersApiResponse = /** status 200 OK */ ResolveNumbersResponse;
export type ResolveNumbersApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    /** Phone number of the sender */
    senderNumber?: string;
    /** Phone number of the recipient */
    recipientNumber?: string;
};
export type CreateNoteForTaskApiResponse = /** status 200 OK */ RidResponse;
export type CreateNoteForTaskApiArg = {
    taskId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateTaskNoteRequest: CreateOrUpdateTaskNoteRequest;
};
export type DeleteTaskNoteApiResponse = /** status 200 OK */ Note;
export type DeleteTaskNoteApiArg = {
    noteId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type UpdateTaskNoteApiResponse = /** status 200 OK */ Note;
export type UpdateTaskNoteApiArg = {
    noteId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateTaskNoteRequest: CreateOrUpdateTaskNoteRequest;
};
export type UnassignTaskApiResponse = /** status 200 OK */ Task;
export type UnassignTaskApiArg = {
    taskId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type UpdateResidentArchiveStatusApiResponse = unknown;
export type UpdateResidentArchiveStatusApiArg = {
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    updateResidentArchiveStatusRequest: UpdateResidentArchiveStatusRequest;
};
export type UpdateStaffMemberArchiveStatusApiResponse = unknown;
export type UpdateStaffMemberArchiveStatusApiArg = {
    staffMemberId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    archive?: boolean;
};
export type GetStaffMemberFacilityMetadataApiResponse =
    /** status 200 OK */ StaffMemberFacilityMetadata[];
export type GetStaffMemberFacilityMetadataApiArg = {
    staffMemberId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    /** facility id to get metadata for */
    facilityId?: string;
};
export type DeleteStaffMemberFromFacilitiesApiResponse =
    /** status 200 OK */ StaffMemberFacilityMetadata[];
export type DeleteStaffMemberFromFacilitiesApiArg = {
    staffMemberId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    ridArray: RidArray;
};
export type GetImageForResourceApiResponse = string;
export type GetImageForResourceApiArg = {
    resourceId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type UploadImageForResourceApiResponse = unknown;
export type UploadImageForResourceApiArg = {
    resourceId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    body: string;
};
export type DeleteImageForResourceApiResponse = unknown;
export type DeleteImageForResourceApiArg = {
    resourceId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type GetVideoReplayDownloadUrlApiResponse =
    /** status 200 OK */ VideoReplayResponse | /** status 202 Accepted - Video replay exists but is not ready yet, try again shortly */ void;
export type GetVideoReplayDownloadUrlApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    /** The ID of the task (alert) to get the video replay for */
    taskId: string;
    /** The type of video replay to retrieve */
    type: VideoReplayType;
};
export type GetVideoReplayAvailabilityApiResponse =
    /** status 200 OK */ VideoReplayAvailabilityResponse;
export type GetVideoReplayAvailabilityApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    /** The ID of the task (alert) to get the video replay availability for */
    taskId: string;
};
export type SetPcpForResidentApiResponse = /** status 200 OK */ RidResponse;
export type SetPcpForResidentApiArg = {
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateProfessionalContactRequest: CreateOrUpdateProfessionalContactRequest;
};
export type DeletePcpForResidentApiResponse = /** status 200 OK */ ProfessionalContact;
export type DeletePcpForResidentApiArg = {
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type GetPcpForResidentApiResponse = /** status 200 OK */ {
    id?: string;
    companyName?: string;
    companyDescription?: string;
    salutation?: string;
    firstName?: string;
    lastName?: string;
    officePhoneNumber?: string;
    personalPhoneNumber?: string;
    email?: string;
    officeFax?: string;
};
export type GetPcpForResidentApiArg = {
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type GetSpecialistsForResidentApiResponse = /** status 200 OK */ ProfessionalContact[];
export type GetSpecialistsForResidentApiArg = {
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type AddSpecialistForResidentApiResponse = /** status 200 OK */ RidResponse;
export type AddSpecialistForResidentApiArg = {
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateProfessionalContactRequest: CreateOrUpdateProfessionalContactRequest;
};
export type UpdateSpecialistForResidentApiResponse = /** status 200 OK */ RidResponse;
export type UpdateSpecialistForResidentApiArg = {
    residentId: string;
    specialistId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateProfessionalContactRequest: CreateOrUpdateProfessionalContactRequest;
};
export type DeleteSpecialistForResidentApiResponse = /** status 200 OK */ ProfessionalContact;
export type DeleteSpecialistForResidentApiArg = {
    residentId: string;
    specialistId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type UnallocateDeviceApiResponse = /** status 200 OK */ DeviceAllocation;
export type UnallocateDeviceApiArg = {
    deviceAllocationId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type BulkUpdateDeviceAllocationsApiResponse = /** status 200 OK */ RidArray;
export type BulkUpdateDeviceAllocationsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    bulkUpdateDeviceAllocations: {
        requests?: {
            deviceAllocationRid: string;
            unitRid?: string;
            room?: string;
            facilityId?: string;
            enterpriseId?: string;
            displayName?: string;
            residentRid?: string;
            isCheckIn?: boolean;
            doorRid?: string;
            isSilent?: boolean;
            allocationType?: AllocationType;
        }[];
    };
};
export type TaskSearchApiResponse = /** status 200 OK */ TaskSearchResult;
export type TaskSearchApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    taskSearchRequest: TaskSearchRequest;
};
export type SearchClosedTasksApiResponse = /** status 200 OK */ TaskSearchResult;
export type SearchClosedTasksApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    closedTaskSearchRequest: ClosedTaskSearchRequest;
};
export type SearchOpenAlertsApiResponse = /** status 200 OK */ OpenAlert[];
export type SearchOpenAlertsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    searchOpenAlertsRequest: SearchOpenAlertsRequest;
};
export type SetResidentHealthInfoApiResponse = /** status 200 OK */ RidResponse;
export type SetResidentHealthInfoApiArg = {
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    healthInfo: HealthInfo;
};
export type DeleteResidentHealthInfoApiResponse = /** status 200 OK */ HealthInfo;
export type DeleteResidentHealthInfoApiArg = {
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type GetResidentCareInfoApiResponse = /** status 200 OK */ GetResidentCareInfoResponse;
export type GetResidentCareInfoApiArg = {
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type SetResidentCareInfoApiResponse = /** status 200 OK */ GetResidentCareInfoResponse;
export type SetResidentCareInfoApiArg = {
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    setResidentCareInfoRequest: SetResidentCareInfoRequest;
};
export type CreateTaskScheduleApiResponse = /** status 200 OK */ RidResponse;
export type CreateTaskScheduleApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateTaskScheduleRequest: CreateOrUpdateTaskScheduleRequest;
};
export type GetTaskScheduleApiResponse = /** status 200 OK */ TaskSchedule;
export type GetTaskScheduleApiArg = {
    scheduleId: string;
    authorization?: string;
};
export type UpdateTaskScheduleApiResponse = /** status 200 OK */ RidResponse;
export type UpdateTaskScheduleApiArg = {
    scheduleId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateTaskScheduleRequest: CreateOrUpdateTaskScheduleRequest;
};
export type DeleteTaskScheduleApiResponse = /** status 200 OK */ TaskSchedule;
export type DeleteTaskScheduleApiArg = {
    scheduleId: string;
    authorization?: string;
};
export type TaskScheduleSearchApiResponse = /** status 200 OK */ TaskScheduleSearchResult;
export type TaskScheduleSearchApiArg = {
    authorization?: string;
    taskScheduleSearchRequest: TaskScheduleSearchRequest;
};
export type GetMyTasksApiResponse = /** status 200 OK */ TaskSearchResult;
export type GetMyTasksApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    userTaskSearchRequest: UserTaskSearchRequest;
};
export type UnassignTaskScheduleApiResponse = /** status 200 OK */ RidResponse;
export type UnassignTaskScheduleApiArg = {
    scheduleId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type AddDeviceActivityApiResponse = unknown;
export type AddDeviceActivityApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    addDeviceActivityRequest: AddDeviceActivityRequest;
};
export type GetLatestDeviceActivityApiResponse = /** status 200 OK */ LatestDeviceActivities;
export type GetLatestDeviceActivityApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    getLatestDeviceActivityRequest: GetLatestDeviceActivityRequest;
};
export type CreateResidentActivityApiResponse = /** status 200 OK */ RidResponse;
export type CreateResidentActivityApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createResidentActivityRequest: CreateResidentActivityRequest;
};
export type UnsetTaskScheduleDurationApiResponse = /** status 200 OK */ RidResponse;
export type UnsetTaskScheduleDurationApiArg = {
    scheduleId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type GetCompanyPhonesApiResponse = /** status 200 OK */ CompanyPhone[];
export type GetCompanyPhonesApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    enterpriseId?: string;
    facilityId?: string;
    availablePhonesOnly?: boolean;
};
export type CreateCompanyPhoneApiResponse = /** status 200 OK */ RidResponse;
export type CreateCompanyPhoneApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateCompanyPhoneRequest: CreateOrUpdateCompanyPhoneRequest;
};
export type DeleteCompanyPhonesApiResponse = /** status 200 OK */ CompanyPhone[];
export type DeleteCompanyPhonesApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    companyPhoneRids?: string[];
};
export type GetCompanyPhoneApiResponse = /** status 200 OK */ {
    id?: string;
    phoneNumber?: string;
    displayName?: string;
    facility?: Facility;
    enterprise?: Enterprise;
};
export type GetCompanyPhoneApiArg = {
    companyPhoneId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type UpdateCompanyPhoneApiResponse = /** status 200 OK */ RidResponse;
export type UpdateCompanyPhoneApiArg = {
    companyPhoneId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateCompanyPhoneRequest: CreateOrUpdateCompanyPhoneRequest;
};
export type DeleteCompanyPhoneApiResponse = /** status 200 OK */ CompanyPhone;
export type DeleteCompanyPhoneApiArg = {
    companyPhoneId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type CreateCompanyPhonesApiResponse = /** status 200 OK */ RidArray;
export type CreateCompanyPhonesApiArg = {
    authorization?: string;
    bulkCreateCompanyPhonesRequest: BulkCreateCompanyPhonesRequest;
};
export type GetHospitalForResidentApiResponse = /** status 200 OK */ {
    id?: string;
    name?: string;
    phoneNumber?: string;
    faxNumber?: string;
    address?: Address;
};
export type GetHospitalForResidentApiArg = {
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type SetHospitalForResidentApiResponse = /** status 200 OK */ RidResponse;
export type SetHospitalForResidentApiArg = {
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateHospitalRequest: CreateOrUpdateHospitalRequest;
};
export type DeleteHospitalForResidentApiResponse = /** status 200 OK */ Hospital;
export type DeleteHospitalForResidentApiArg = {
    residentId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type GetCareZoneApiResponse = /** status 200 OK */ {
    id?: string;
    name?: string;
    facilityId?: string;
    unitTags?: UnitTags;
};
export type GetCareZoneApiArg = {
    careZoneId: string;
    authorization?: string;
};
export type UpdateCareZoneApiResponse = /** status 200 OK */ RidResponse;
export type UpdateCareZoneApiArg = {
    careZoneId: string;
    authorization?: string;
    createOrUpdateCareZoneRequest: CreateOrUpdateCareZoneRequest;
};
export type DeleteCareZoneApiResponse = /** status 200 OK */ CareZone;
export type DeleteCareZoneApiArg = {
    careZoneId: string;
    authorization?: string;
};
export type GetAllCareZonesApiResponse = /** status 200 OK */ CareZone[];
export type GetAllCareZonesApiArg = {
    authorization?: string;
    enterpriseRid?: string;
    facilityRid?: string;
};
export type CreateCareZoneApiResponse = /** status 200 OK */ RidResponse;
export type CreateCareZoneApiArg = {
    authorization?: string;
    createOrUpdateCareZoneRequest: CreateOrUpdateCareZoneRequest;
};
export type GetOnCallStaffMembersApiResponse = /** status 200 OK */ StaffMember[];
export type GetOnCallStaffMembersApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    staffOnCallRequest: StaffOnCallRequest;
};
export type UnitsSearchApiResponse = /** status 200 OK */ UnitSearchResult;
export type UnitsSearchApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    unitSearchRequest: UnitSearchRequest;
};
export type GetCamerasApiResponse = /** status 200 OK */ {
    /** The unique Reolink identifier for the camera */
    uid: string;
    /** This is the human-readable name given to the camera during the unit allocation process. It may be null if
    the camera was not allocated to a unit, or the camera was allocated before we started using this field.
     */
    name?: string;
    /** The IP address of the camera. Absent for WiFi cameras that have been provisioned but not
    yet activated - their static IP is assigned at installation-time activation.
     */
    ipAddress?: string | null;
    /** The MAC address of the camera's ethernet (PoE port) interface. WiFi cameras have a separate
    MAC for the WiFi radio, which is not stored in this field.
     */
    macAddress: string;
    /** The MAC address of the camera's WiFi radio. Differs from the ethernet macAddress and is only
    discoverable once the camera has joined a WiFi network, so it is recorded at installation-time
    activation. Null for PoE cameras and for WiFi cameras that have not been activated yet.
     */
    wifiMacAddress?: string | null;
    /** The enterprise that the camera belongs to */
    enterpriseRid: string;
    /** The facility that the camera belongs to */
    facilityRid: string;
    /** The unit that the camera belongs to */
    unitRid?: string;
    /** Processing mode for the camera stream.
    
    Standard operating modes:
    - DEFAULT: Camera is processing alerts and events - current resident has opted in.
    - DISABLED: Camera feed is not being processed whatsoever, it remains on the camera only. Resident has opted
    out or the room is empty.
    
    Non-standard operating modes:
    - NO_ALERTS: Camera is being processed, but fall alerts are not generated. This is usually used before a
    community goes live to calibrate.
    - NO_ALERTS_OR_EVENTS: Camera is being processed, but we aren't generating any alerts and we aren't storing any.
    inference events in our DB.
     */
    mode: 'NO_ALERTS' | 'NO_ALERTS_OR_EVENTS' | 'DEFAULT' | 'DISABLED';
    connectionType?: ('POE' | 'WIFI') | null;
    /** Fall alert sensitivity level for this camera. Currently maps to the number of consecutive
    detections required before triggering a fall alert, but this interpretation is subject to
    change. Higher values mean fewer alerts. If null, the service default is used.
     */
    fallAlertSensitivity?: number | null;
    /** Whether health/offline alerting is active for this camera. When false, a known issue is being
    tracked: the stream is still processed, but offline/health pages are suppressed. camera-service
    automatically sets this back to true once the camera becomes healthy again.
     */
    isActive?: boolean;
    /** Free-text note about this camera - any operational context (for example, the reason it was set inactive).
     */
    description?: string | null;
}[];
export type GetCamerasApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    unitRid?: string;
    facilityRid?: string;
};
export type CheckInSearchApiResponse = /** status 200 OK */ CheckInSearchResult;
export type CheckInSearchApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    checkInSearchRequest: CheckInSearchRequest;
};
export type CreateStaffFacilityPreferencesApiResponse = /** status 200 OK */ RidResponse;
export type CreateStaffFacilityPreferencesApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createStaffFacilityPreferencesRequest: CreateStaffFacilityPreferencesRequest;
};
export type GetStaffFacilityPreferencesApiResponse = /** status 200 OK */ {
    id?: string;
    staffMemberId?: string;
    facilityId?: string;
    pinnedResidentIds?: RidArray;
};
export type GetStaffFacilityPreferencesApiArg = {
    staffMemberId: string;
    facilityId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type UpdateStaffFacilityPreferencesApiResponse = /** status 200 OK */ RidResponse;
export type UpdateStaffFacilityPreferencesApiArg = {
    staffMemberId: string;
    facilityId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    updateStaffFacilityPreferencesRequest: UpdateStaffFacilityPreferencesRequest;
};
export type DeleteStaffFacilityPreferencesApiResponse =
    /** status 200 OK */ StaffFacilityPreferences;
export type DeleteStaffFacilityPreferencesApiArg = {
    staffMemberId: string;
    facilityId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type SearchStaffFacilityPreferencesApiResponse =
    /** status 200 OK */ StaffFacilityPreferences[];
export type SearchStaffFacilityPreferencesApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    staffFacilityPreferenceSearchRequest: StaffFacilityPreferenceSearchRequest;
};
export type CreateStaffMemberFacilityConfigApiResponse = /** status 200 OK */ RidResponse;
export type CreateStaffMemberFacilityConfigApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateStaffMemberFacilityConfigRequest: CreateOrUpdateStaffMemberFacilityConfigRequest;
};
export type GetStaffMemberFacilityConfigApiResponse = /** status 200 OK */ {
    id?: string;
    facilityId?: string;
    staffMemberId?: string;
    careZones?: CareZone[];
    jobTitle?: string;
    isAgencyStaff?: boolean;
    isCareAdmin?: boolean;
    shouldReceiveAllCareMessages?: boolean;
};
export type GetStaffMemberFacilityConfigApiArg = {
    staffMemberFacilityConfigId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type UpdateStaffMemberFacilityConfigApiResponse = /** status 200 OK */ RidResponse;
export type UpdateStaffMemberFacilityConfigApiArg = {
    staffMemberFacilityConfigId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateStaffMemberFacilityConfigRequest: CreateOrUpdateStaffMemberFacilityConfigRequest;
};
export type DeleteStaffMemberFacilityConfigApiResponse =
    /** status 200 OK */ StaffMemberFacilityConfig;
export type DeleteStaffMemberFacilityConfigApiArg = {
    staffMemberFacilityConfigId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type SearchStaffMemberFacilityConfigsApiResponse =
    /** status 200 OK */ StaffMemberFacilityConfig[];
export type SearchStaffMemberFacilityConfigsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    staffMemberFacilityConfigSearchRequest: StaffMemberFacilityConfigSearchRequest;
};
export type StaffProfileSearchApiResponse = /** status 200 OK */ StaffMember;
export type StaffProfileSearchApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    staffProfileSearchRequest: StaffProfileSearchRequest;
};
export type GetUserStaffMemberProfilesApiResponse = /** status 200 OK */ StaffMember[];
export type GetUserStaffMemberProfilesApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type CreateDoorApiResponse = /** status 200 OK */ RidResponse;
export type CreateDoorApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    items: CreateOrUpdateDoorRequest;
};
export type DeleteDoorsApiResponse = /** status 200 OK */ Door[];
export type DeleteDoorsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    doorRids?: string[];
};
export type CreateDoorsApiResponse = /** status 200 OK */ RidArray;
export type CreateDoorsApiArg = {
    authorization?: string;
    bulkCreateDoorsRequest: BulkCreateDoorsRequest;
};
export type GetDoorApiResponse = /** status 200 OK */ Door;
export type GetDoorApiArg = {
    doorRid: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type UpdateDoorApiResponse = /** status 200 OK */ RidResponse;
export type UpdateDoorApiArg = {
    doorRid: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    items: CreateOrUpdateDoorRequest;
};
export type DeleteDoorApiResponse = /** status 200 OK */ Door;
export type DeleteDoorApiArg = {
    doorRid: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type CreateOrUpdateDoorAlarmScheduleApiResponse = /** status 200 OK */ RidResponse;
export type CreateOrUpdateDoorAlarmScheduleApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateDoorAlarmScheduleRequest: {
        doorRid: string;
        alwaysAlert: boolean;
        neverAlert: boolean;
        /** A map where the key is a day of the week, and the value is a list of time ranges. It accepts All as a wildcard. */
        dayOfWeekTimeRangeMap?: {
            [key: string]: TimeRangeArray;
        };
        dayOfMonthTimeRangeMap?: {
            [key: string]: TimeRangeArray;
        };
        holidayOverrides?: {
            [key: string]: {
                startTime?: string;
                endTime?: string;
            }[];
        };
    };
};
export type GetDoorAlarmScheduleApiResponse = /** status 200 OK */ DoorAlarmSchedule;
export type GetDoorAlarmScheduleApiArg = {
    doorAlarmScheduleRid: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type DeleteDoorAlarmScheduleApiResponse = /** status 200 OK */ DoorAlarmSchedule;
export type DeleteDoorAlarmScheduleApiArg = {
    doorAlarmScheduleRid: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type CreateOrUpdateFacilityDefaultDoorAlarmScheduleApiResponse =
    /** status 200 OK */ RidResponse;
export type CreateOrUpdateFacilityDefaultDoorAlarmScheduleApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createOrUpdateDoorAlarmScheduleRequest: {
        facilityRid: string;
        alwaysAlert: boolean;
        neverAlert: boolean;
        dayOfWeekTimeRangeMap?: DayOfWeekTimeRangeMap;
        dayOfMonthTimeRangeMap?: TimeRangeMap;
        holidayOverrides?: {
            [key: string]: TimeRangeArray;
        };
    };
};
export type GetFacilityDefaultDoorAlarmScheduleApiResponse = /** status 200 OK */ {
    rid?: string;
    /** If left blank, it is assumed that this a default, facility wide schedule. */
    doorRid?: string;
    facilityRid: string;
    alwaysAlert: boolean;
    neverAlert: boolean;
    /** The object represents a union set of excluded time ranges. Because it is a union set, it is possible to have overlapping time ranges, which will not affect the evaluation of the schedule. In other words, if the current time matches any of the given time ranges, then the schedule is inside it's exclusion window. */
    dailySchedules?: {
        dayOfWeekTimeRangeMap?: DayOfWeekTimeRangeMap;
        dayOfMonthTimeRangeMap?: TimeRangeMap;
    };
    /** An inverted representation of the current schedule for the day. It represents the times that a door alarm will fire on a given day. */
    currentDayDisplaySchedules?: string;
    holidaySchedules?: {
        [key: string]: TimeRangeArray;
    };
};
export type GetFacilityDefaultDoorAlarmScheduleApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    facilityRid: string;
};
export type DeleteFacilityDefaultDoorAlarmScheduleApiResponse =
    /** status 200 OK */ DoorAlarmSchedule;
export type DeleteFacilityDefaultDoorAlarmScheduleApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    facilityRid: string;
};
export type SearchDoorsApiResponse = /** status 200 OK */ Door[];
export type SearchDoorsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    doorSearchRequest: {
        facilityRids?: RidArray;
        unitRids?: RidArray;
        doorRids?: RidArray;
    };
};
export type SearchDoorAlarmSchedulesApiResponse = /** status 200 OK */ DoorAlarmSchedule[];
export type SearchDoorAlarmSchedulesApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    doorAlarmScheduleSearchRequest: {
        doorAlarmScheduleRids?: RidArray;
        facilityRids: RidArray;
        doorRids?: RidArray;
    };
};
export type GetAllFeaturesApiResponse = /** status 200 OK */ FeaturesResponse;
export type GetAllFeaturesApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    facilityId?: string;
};
export type CreateFeatureApiResponse = /** status 201 OK */ RidResponse;
export type CreateFeatureApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    createFeatureRequest: CreateFeatureRequest;
};
export type GetFeatureApiResponse = /** status 200 OK */ Feature;
export type GetFeatureApiArg = {
    featureId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type UpdateFeatureApiResponse = /** status 200 OK */ RidResponse;
export type UpdateFeatureApiArg = {
    featureId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
    updateFeatureRequest: UpdateFeatureRequest;
};
export type DeleteFeatureApiResponse = /** status 200 OK */ Feature;
export type DeleteFeatureApiArg = {
    featureId: string;
    authorization?: string;
    'x-sage-profile-rid'?: string;
};
export type GetNotificationContextApiResponse = /** status 200 OK */ NotificationContext;
export type GetNotificationContextApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    getNotificationContextRequest: GetNotificationContextRequest;
};
export type CreateNotificationPolicyApiResponse = /** status 200 OK */ string;
export type CreateNotificationPolicyApiArg = {
    authorization?: string;
    facilityRid: string;
    'x-sage-profile-rid'?: string | null;
    notificationPolicy: NotificationPolicy;
};
export type GetNotificationPoliciesApiResponse = /** status 200 OK */ NotificationPolicy[];
export type GetNotificationPoliciesApiArg = {
    authorization?: string;
    facilityRid: string;
    'x-sage-profile-rid'?: string | null;
};
export type GetNotificationPolicyApiResponse = /** status 200 OK */ NotificationPolicy;
export type GetNotificationPolicyApiArg = {
    authorization?: string;
    notificationPolicyRid: string;
    facilityRid: string;
    'x-sage-profile-rid'?: string | null;
    notificationPolicyVersion?: number;
};
export type UpdateNotificationPolicyApiResponse = /** status 200 OK */ string;
export type UpdateNotificationPolicyApiArg = {
    authorization?: string;
    notificationPolicyRid: string;
    facilityRid: string;
    'x-sage-profile-rid'?: string | null;
    notificationPolicy: NotificationPolicy;
};
export type CreateNotificationBlockApiResponse = /** status 200 OK */ NotificationPolicy;
export type CreateNotificationBlockApiArg = {
    authorization?: string;
    notificationPolicyRid: string;
    facilityRid: string;
    'x-sage-profile-rid'?: string | null;
    notificationBlock: NotificationBlock;
};
export type UpdateNotificationBlockApiResponse = /** status 200 OK */ NotificationBlock;
export type UpdateNotificationBlockApiArg = {
    authorization?: string;
    notificationPolicyRid: string;
    blockRid: string;
    facilityRid: string;
    'x-sage-profile-rid'?: string | null;
    notificationBlock: NotificationBlock;
};
export type DeleteNotificationBlockApiResponse = /** status 200 OK */ NotificationBlock;
export type DeleteNotificationBlockApiArg = {
    authorization?: string;
    notificationPolicyRid: string;
    blockRid: string;
    facilityRid: string;
    'x-sage-profile-rid'?: string | null;
};
export type GetNotificationFilterApiResponse = /** status 200 OK */ NotificationFilter;
export type GetNotificationFilterApiArg = {
    authorization?: string;
    blockRid: string;
    filterRid: string;
    facilityRid: string;
    'x-sage-profile-rid'?: string | null;
};
export type UpdateNotificationFilterApiResponse = /** status 200 OK */ NotificationFilter;
export type UpdateNotificationFilterApiArg = {
    authorization?: string;
    blockRid: string;
    filterRid: string;
    facilityRid: string;
    'x-sage-profile-rid'?: string | null;
    updateNotificationFilterRequest: UpdateNotificationFilterRequest;
};
export type DeleteNotificationFilterApiResponse = /** status 200 OK */ NotificationFilter;
export type DeleteNotificationFilterApiArg = {
    authorization?: string;
    blockRid: string;
    filterRid: string;
    facilityRid: string;
    'x-sage-profile-rid'?: string | null;
};
export type SimulateDeviceEventApiResponse = unknown;
export type SimulateDeviceEventApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    /** The type of device to simulate */
    deviceType:
        | 'ACCESS_CONTROL_KEYPAD'
        | 'BUTTON'
        | 'DOOR'
        | 'FALL_DETECTION_DEVICE'
        | 'M3'
        | 'MOVEMENT_SENSOR'
        | 'UNIVERSAL_TRANSMITTER';
    simulateDeviceEventRequest: SimulateDeviceEventRequest;
};
export type GetShiftInstancesApiResponse = /** status 200 OK */ GetShiftInstancesResponse;
export type GetShiftInstancesApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string;
    facilityRid: string;
    /** Optional current time in milliseconds */
    currentTimeMs?: number;
};
export type UpsertShiftInstancesApiResponse = /** status 200 OK */ UpsertShiftInstancesResponse;
export type UpsertShiftInstancesApiArg = {
    authorization?: string;
    upsertShiftInstancesRequest: UpsertShiftInstancesRequest;
};
export type CareProvided =
    | 'ADL_BATHING'
    | 'ADL_DRESSING'
    | 'ADL_EATING'
    | 'ADL_ESCORT'
    | 'ADL_GROOMING'
    | 'ADL_ORAL_HYGIENE'
    | 'ADL_ROUTINE_CHECK'
    | 'ADL_TOILETING'
    | 'ADL_TRANSFER'
    | 'EMERGENCY_FALL'
    | 'EMERGENCY_OTHER'
    | 'EMERGENCY_SEEN_ON_FLOOR'
    | 'HOUSEKEEPING_BATHROOM'
    | 'HOUSEKEEPING_BED'
    | 'HOUSEKEEPING_LAUNDRY'
    | 'HOUSEKEEPING_OTHER'
    | 'HOUSEKEEPING_TRASH'
    | 'HOUSEKEEPING_UNSPECIFIED'
    | 'NON_EMERGENCY_ACCIDENTAL_PRESS'
    | 'NON_EMERGENCY_FOUND_DEVICE'
    | 'NON_EMERGENCY_ILLNESS'
    | 'NON_EMERGENCY_QUESTION'
    | 'NON_EMERGENCY_NO_CARE_GIVEN'
    | 'REQUEST_EMOTIONAL_SUPPORT'
    | 'REQUEST_MAINTENANCE'
    | 'REQUEST_MEDTECH'
    | 'REQUEST_OTHER'
    | 'REQUEST_OXYGEN'
    | 'REQUEST_REPAIR'
    | 'ADL_CLINICAL_MONITORING'
    | 'ADL_COGNITIVE_BEHAVIORAL'
    | 'ADL_HEARING_ASSISTANCE'
    | 'ADL_MOBILITY_AID'
    | 'ADL_OTHER'
    | 'ADL_SKIN_CARE'
    | 'ADL_VISION_ASSISTANCE'
    | 'IADL_ACTIVITY_ASSISTANCE'
    | 'IADL_FINANCIAL_ASSISTANCE'
    | 'IADL_MEDICATION_ASSISTANCE'
    | 'IADL_PET_ASSISTANCE'
    | 'IADL_TRANSPORTATION_ASSISTANCE'
    | 'IADL_OTHER'
    | 'SAFETY_ELOPEMENT_RISK_MANAGEMENT'
    | 'SAFETY_FALL_INTERVENTION'
    | 'SAFETY_OTHER'
    | 'ADL_DRESSING_FOOTWEAR'
    | 'ADL_DRESSING_LOWER_BODY'
    | 'ADL_DRESSING_UPPER_BODY'
    | 'ADL_MOBILITY_ROLL'
    | 'ADL_MOBILITY_SIT_TO_LYING'
    | 'ADL_MOBILITY_LYING_TO_SIT'
    | 'ADL_MOBILITY_SIT_TO_STAND'
    | 'ADL_MOBILITY_PICK_UP_OBJECT'
    | 'ADL_PERSONAL_HYGIENE'
    | 'ADL_TRANSFER_CAR'
    | 'ADL_TRANSFER_SHOWER'
    | 'ADL_TRANSFER_TO_CHAIR'
    | 'ADL_TRANSFER_TOILET'
    | 'WANDER_RETURNED_SAFELY'
    | 'WANDER_ESCORTED'
    | 'WANDER_OTHER';
export type OutcomeType = 'COMPLETED' | 'NOT_COMPLETED' | 'REFUSED';
export type CareOutcome = {
    careProvided: CareProvided;
    outcome: OutcomeType;
    /** This enum is add only. If you delete or update the values below, the api will break. */
    assistanceLevel?:
        | 'INDEPENDENT'
        | 'SETUP_CLEANUP'
        | 'SUPERVISION_TOUCH'
        | 'MODERATE_ASSIST'
        | 'SUBSTANTIAL_ASSIST'
        | 'DEPENDENT';
    creatorEntityRid?: string;
    alertRid?: string;
};
export type Observation = {
    /** Supported options for observation types */
    observationType:
        | 'BOWEL_MOVEMENT'
        | 'URINE'
        | 'MOOD_BEHAVIOR'
        | 'PAIN'
        | 'SKIN'
        | 'EATING_DRINKING_LESS'
        | 'OTHER'
        | 'SEEMS_DIFFERENT'
        | 'NEEDS_MORE_HELP'
        | 'SKIN_ISSUE'
        | 'ABNORMAL_BOWEL_MOVEMENT'
        | 'ABNORMAL_URINE'
        | 'MOOD_BEHAVIOR_ISSUE';
    observationValue?: string;
    observationNote?: string;
};
export type CreateCareEventNoteRequest = {
    /** This enum is add only. If you delete or update the values below, the api will break. */
    noteType: 'CREATION_NOTE' | 'CAREGIVER_NOTE' | 'NURSE_ACKNOWLEDGEMENT';
    content: string;
    creatorRid: string;
};
export type CareEvent = {
    rid?: string;
    residentRid?: string;
    facilityRid?: string;
    alertRid?: string;
    plannedCareRid?: string;
    /** Rid of the "logged care" task that was migrated to a care event. This field is only used for migration purposes
    and will be deprecated after the care event migration is complete.
     */
    loggedCareRid?: string;
    /** Rid of the staff that documented the care event */
    creatorRid?: string;
    /** Rid of the staff that updated the care event */
    lastUpdatedByRid?: string;
    careOutcomes?: CareOutcome[];
    observations?: Observation[];
    notes?: (CreateCareEventNoteRequest & {
        /** The timestamp in milliseconds when the note was created. */
        createTimestampMs?: number;
        /** Rid of the staff that updated the note */
        lastUpdatedByRid?: string;
        /** The timestamp in milliseconds when the note was last updated. */
        updateTimestampMs?: number;
    })[];
    /** The timestamp in milliseconds when the care event was created */
    createTimestampMs?: number;
    /** The timestamp in milliseconds when the care event was last updated. */
    updateTimestampMs?: number;
    /** A list of all staff rids that responded to the care event. */
    respondingStaffRids?: string[];
};
export type Address = {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipcode?: string;
    country?: string;
};
export type RidArray = string[];
export type UnitTags = {
    floors?: string[];
    wings?: string[];
    careTypes?: string[];
    buildings?: string[];
    wards?: string[];
    unitRids?: RidArray;
};
export type CareZone = {
    id?: string;
    name?: string;
    facilityId?: string;
    unitTags?: UnitTags;
};
export type MessageDeliveryMethods = string[];
export type FacilityStatus = 'QA' | 'PRE_LIVE' | 'LIVE';
export type ObservationType =
    | 'BOWEL_MOVEMENT'
    | 'URINE'
    | 'MOOD_BEHAVIOR'
    | 'PAIN'
    | 'SKIN'
    | 'EATING_DRINKING_LESS'
    | 'OTHER'
    | 'SEEMS_DIFFERENT'
    | 'NEEDS_MORE_HELP'
    | 'SKIN_ISSUE'
    | 'ABNORMAL_BOWEL_MOVEMENT'
    | 'ABNORMAL_URINE'
    | 'MOOD_BEHAVIOR_ISSUE';
export type EhrFeatureType = 'TASKING';
export type EhrFeature = {
    featureType?: EhrFeatureType;
    isEnabled?: boolean;
    /** Care types for which feature is enabled. If empty, feature is enabled for all care types. */
    enabledForCareTypes?: string[];
};
export type EhrProviderConfig = {
    isResidentProfileSyncEnabled?: boolean;
    /** Care types for which resident profile sync is enabled for this EHR provider. If empty, sync is enabled for all care types. */
    enabledForCareTypes?: string[];
    features?: EhrFeature[];
};
export type Gen3FeatureType = 'WANDER';
export type Gen3Feature = {
    featureType?: Gen3FeatureType;
    isEnabled?: boolean;
    /** Care types for which the feature is enabled. */
    enabledForCareTypes?: string[];
};
export type FacilityConfig = {
    isAlertLocationEnabled?: boolean;
    /** Care types for which check-in buttons are enabled at this facility. Defaults to [INDEPENDENT_LIVING] when absent.
     */
    checkInsEnabledCareTypes?: string[];
    routineCheckIntervalInMin?: number;
    isObservationsEnabled?: boolean;
    isEventPublishingEnabled?: boolean;
    isExtendedFallVideoEnabled?: boolean;
    isDomeLightsEnabled?: boolean;
    isStaleM3AlertClosureEnabled?: boolean;
    isDetectHumanInTheLoopEnabled?: boolean;
    isDoorAlertCareConfirmationEnabled?: boolean;
    /** Mapping of CareType (string) to list of ObservationTypes */
    observationsByCareType?: {
        [key: string]: ObservationType[];
    };
    /** Non-SNF care types for which observation details are enabled. Details are enabled for SNF care types. SNF care types are SKILLED_NURSING, SHORT_TERM_REHAB and LONG_TERM_CARE. Other care types (INDEPENDENT_LIVING, ASSISTED_LIVING, MEMORY_CARE) are considered non-SNF care types. */
    observationDetailsEnabledCareTypes?: string[];
    /** Map of EHR provider names to their configurations */
    ehrIntegrationsConfig?: {
        [key: string]: EhrProviderConfig;
    };
    /** Detect information for the facility */
    detectConfig?: {
        /** Whether proactive alerting is enabled at this facility. Absent means disabled. */
        isProactiveAlertingEnabled?: boolean;
        /** Facility-local time of day (HH:mm, 24-hour) at which the proactive alerting window opens. Defaults to "22:00" when absent. May be later than proactiveAlertWindowEndTime, in which case the window spans midnight. */
        proactiveAlertWindowStartTime?: string;
        /** Facility-local time of day (HH:mm, 24-hour) at which the proactive alerting window closes. Defaults to "06:00" when absent. */
        proactiveAlertWindowEndTime?: string;
    } & {
        detectCameras?: 'NONE' | 'ONBOARDING' | 'QA' | 'LIVE';
        isDetectHumanInTheLoopEnabled?: boolean;
        isExtendedFallVideoEnabled?: boolean;
    };
    /** Gen3 features configured for this facility */
    gen3Features?: Gen3Feature[];
};
export type EhrProvider = 'ALIS' | 'AUGUST' | 'ECP' | 'PCC' | 'YARDI';
export type ResourceType = 'RESIDENT';
export type ResourceField =
    | 'RESIDENT_UNIT'
    | 'RESIDENT_CODE_STATUS'
    | 'RESIDENT_ON_HOLD_STATUS'
    | 'RESIDENT_DOB'
    | 'RESIDENT_PHONE_NUMBER'
    | 'RESIDENT_PREFERRED_NAME'
    | 'RESIDENT_FIRST_NAME'
    | 'RESIDENT_LAST_NAME'
    | 'RESIDENT_ARCHIVED_STATUS';
export type EhrLockedField = {
    id?: string;
    ehrProvider?: EhrProvider;
    resourceType?: ResourceType;
    resourceField?: ResourceField;
    /** Timestamp when the locked field was created */
    createdAtMillis?: number;
};
export type Facility = {
    id?: string;
    name?: string;
    address?: Address;
    phoneNumber?: string;
    administrator?: StaffMember;
    enterprise?: Enterprise;
    logoUrl?: string;
    emergencyNotificationNumber?: string;
    requestNotificationNumber?: string;
    taskNotificationNumber?: string;
    timezone?: string;
    careZones?: CareZone[];
    messageDeliveryMethods?: MessageDeliveryMethods;
    status?: FacilityStatus;
    facilityConfig?: FacilityConfig;
    /** List of fields that are locked for editing due to EHR integration */
    ehrLockedFields?: EhrLockedField[];
};
export type EnterpriseConfig = {
    ssoStatus?: 'DISABLED' | 'PENDING' | 'ENABLED' | 'ENFORCED';
    ssoExemptDomains?: string[];
};
export type Enterprise = {
    id?: string;
    name?: string;
    address?: Address;
    phoneNumber?: string;
    administrator?: StaffMember;
    facilities?: Facility[];
    logoUrl?: string;
    notificationApplicationId?: string;
    enterpriseConfig?: EnterpriseConfig;
};
export type OnCallState = {
    isOnCall: boolean;
    lastModified: string;
    facilityId: string;
    companyPhoneId?: string;
    shiftInstanceId?: string;
};
export type StaffMemberFacilityConfig = {
    id?: string;
    facilityId?: string;
    staffMemberId?: string;
    careZones?: CareZone[];
    jobTitle?: string;
    isAgencyStaff?: boolean;
    isCareAdmin?: boolean;
    shouldReceiveAllCareMessages?: boolean;
};
export type StaffMemberGroupMembership = {
    groupId?: string;
    groupName?: string;
    isAdmin?: boolean;
};
export type StaffMember = {
    id?: string;
    firstName?: string;
    middleName?: string;
    middleInitial?: string;
    lastName?: string;
    phoneNumber?: string;
    email?: string;
    address?: Address;
    enterprise?: Enterprise;
    facilities?: Facility[];
    onCallStates?: OnCallState[];
    staffMemberFacilityConfigs?: StaffMemberFacilityConfig[];
    groupMemberships?: StaffMemberGroupMembership[];
    isArchived?: boolean;
    isPhoneNumberVerified?: boolean;
    /** Deprecated. Use staffMemberFacilityConfigs.shouldReceiveAllCareMessages instead. */
    shouldReceiveAllCareMessages?: boolean;
    /** Deprecated. Use staffMemberFacilityConfigs.isCareAdmin instead. */
    isCareAdmin?: boolean;
    /** Deprecated. Use staffMemberFacilityConfigs.isAgencyStaff instead. */
    isAgencyStaff?: boolean;
    /** Deprecated. Use staffMemberFacilityConfigs.jobTitle instead. */
    jobTitle?: string;
};
export type CareEventWithStaffDetails = CareEvent & {
    /** A map of staff member RIDs to staff member details */
    staffMembers?: {
        [key: string]: StaffMember;
    };
};
export type Contact = {
    id?: string;
    firstName?: string;
    lastName?: string;
    primaryPhone?: string;
    secondaryPhone?: string;
    email?: string;
    relationship?: string;
    isLocal?: boolean;
    preferredContactMethod?: string;
    hasPowerOfAttorney?: boolean;
    address?: Address;
    salutation?: string;
    primaryLanguage?: string;
};
export type Note = {
    id?: string;
    creationTimestamp?: string;
    lastUpdatedTimestamp?: string;
    content?: string;
    subject?: string;
    creator?: StaffMember;
    lastUpdatedBy?: StaffMember;
};
export type Device = {
    id?: string;
    type?: string;
    connectivityStatus?: string;
    activationStatus?: string;
    hardwareId?: string;
    creationTimestamp?: string;
    notes?: Note[];
    lastPressTimestamp?: string;
};
export type TimeRangeArray = {
    startTime?: string;
    endTime?: string;
}[];
export type DayOfWeekTimeRangeMap = {
    [key: string]: TimeRangeArray;
};
export type TimeRangeMap = {
    [key: string]: TimeRangeArray;
};
export type DoorAlarmSchedule = {
    rid?: string;
    /** If left blank, it is assumed that this a default, facility wide schedule. */
    doorRid?: string;
    facilityRid: string;
    alwaysAlert: boolean;
    neverAlert: boolean;
    /** The object represents a union set of excluded time ranges. Because it is a union set, it is possible to have overlapping time ranges, which will not affect the evaluation of the schedule. In other words, if the current time matches any of the given time ranges, then the schedule is inside it's exclusion window. */
    dailySchedules?: {
        dayOfWeekTimeRangeMap?: DayOfWeekTimeRangeMap;
        dayOfMonthTimeRangeMap?: TimeRangeMap;
    };
    /** An inverted representation of the current schedule for the day. It represents the times that a door alarm will fire on a given day. */
    currentDayDisplaySchedules?: string;
    holidaySchedules?: {
        [key: string]: TimeRangeArray;
    };
};
export type Door = {
    rid: string;
    facilityRid: string;
    name?: string;
    doorAlarmSchedule: DoorAlarmSchedule;
    unit: Unit;
    deviceAllocation?: DeviceAllocation;
    /** The number of seconds a door can be left open before an alert is triggered. A value of 0 means there is no threshold and alerts will not be triggered for propped doors. This setting is currently only applicable to offline doors, with an access control device and a door sensor. */
    doorProppedThresholdInSeconds: number;
};
export type DeviceAllocation = {
    room?: string;
    /** Deprecated. Use devices array instead. The single device field will be removed in a future version. */
    device?: Device;
    /** The type of device allocation (since complex allocations can have multiple devices) */
    allocationType?:
        | 'BUTTON'
        | 'BUTTON_WITH_RESET'
        | 'CARE_CONFIRMATION_BUTTON'
        | 'CHECKIN_MOVEMENT_SENSOR'
        | 'DOOR'
        | 'FALL_DETECTION_DEVICE'
        | 'M3'
        | 'OFFLINE_DOOR'
        | 'ONLINE_DOOR'
        | 'PULL_CORD'
        | 'UNIVERSAL_TRANSMITTER'
        | 'UNIVERSAL_TRANSMITTER_BUTTON';
    devices: Device[];
    id: string;
    door?: Door;
    unit?: Unit;
    facility?: Facility;
    enterprise?: Enterprise;
    displayName?: string;
    resident?: Resident;
    isCheckIn?: boolean;
    isSilent?: boolean;
};
export type Unit = {
    id?: string;
    currentOccupants?: Resident[];
    devices?: DeviceAllocation[];
    phoneNumber?: string;
    displayName?: string;
    facility?: Facility;
    enterprise?: Enterprise;
    floor?: string;
    wing?: string;
    careType?: string;
    building?: string;
    ward?: string;
};
export type ProfessionalContact = {
    id?: string;
    companyName?: string;
    companyDescription?: string;
    salutation?: string;
    firstName?: string;
    lastName?: string;
    officePhoneNumber?: string;
    personalPhoneNumber?: string;
    email?: string;
    officeFax?: string;
};
export type HealthInfo = {
    /** Resident memory health information */
    memoryHealthInfo?: {
        memoryLevel?: string;
    };
    /** Resident continence health information */
    continenceHealthInfo?: {
        continenceLevel?: string;
        productProvider?: string;
        wearsPads?: boolean;
        wearsPullups?: boolean;
        wearsBriefs?: boolean;
    };
    /** Resident's activities of daily living information */
    adlHealthInfo?: {
        safetyLevel?: string;
        feedingLevel?: string;
        bathingLevel?: string;
        toiletingLevel?: string;
        transferringLevel?: string;
        mobilityLevel?: string;
        dressingLevel?: string;
        groomingLevel?: string;
    };
    resuscitationStrategy?: string;
    /** Resident hospice health information */
    hospiceHealthInfo?: {
        hospiceStatus: string;
    };
};
export type Hospital = {
    id?: string;
    name?: string;
    phoneNumber?: string;
    faxNumber?: string;
    address?: Address;
};
export type DetectConsentStatusEnum = 'ACCEPTED' | 'DECLINED' | 'NOT_RECEIVED';
export type Resident = {
    id: string;
    salutation?: string;
    firstName?: string;
    middleName?: string;
    middleInitial?: string;
    lastName?: string;
    preferredName?: string;
    dateOfBirth?: string;
    age?: number;
    personalPhone?: string;
    createTimestampMs?: number;
    updateTimestampMs?: number;
    email?: string;
    emergencyContact?: Contact;
    contacts?: Contact[];
    unit?: Unit;
    primaryCarePhysician?: ProfessionalContact;
    specialists?: ProfessionalContact[];
    notes?: Note[];
    facility?: Facility;
    enterprise?: Enterprise;
    isRoutineCheckEnabled?: boolean;
    /** Whether proactive (fall-risk) alerting is enabled for this resident. Defaults to false when absent. Alerts also require the facility's proactive alerting configuration to be enabled and within its alert window. */
    isProactiveAlertingEnabled?: boolean;
    isOnHold?: boolean;
    isArchived?: boolean;
    isCheckInsEnabled?: boolean;
    archiveReason?: string;
    healthInfo?: HealthInfo;
    preferredHospital?: Hospital;
    detectConsentStatus?: DetectConsentStatusEnum;
    /** Returns all expected exits for resident, sorted by active, then create datetime DESC */
    expectedExits?: {
        beginTimestampInUtc: string;
        endTimestampInUtc: string;
        isActive?: boolean;
        /** The staff member who created the expected exit */
        staffMemberRid?: string;
    }[];
};
export type Group = {
    id?: string;
    name?: string;
    members?: StaffMember[];
    numberOfMembers?: number;
    iconUrl?: string;
    facility?: Facility;
    enterprise?: Enterprise;
};
export type TaskTemplate = {
    title?: string;
    description?: string;
    severity?: string;
    assignee?: StaffMember;
    group?: Group;
    resident?: Resident;
    unit?: Unit;
};
export type ScheduleRecurrenceDefinition = {
    cronScheduleExpression?: string;
    futureExecutionTimeInMs?: number;
    durationInMinutes?: number;
};
export type TaskSchedule = {
    id?: string;
    taskTemplate?: TaskTemplate;
    scheduleRecurrenceDefinition?: ScheduleRecurrenceDefinition;
    creator?: StaffMember;
    lastUpdatedBy?: StaffMember;
    facility?: Facility;
    enterprise?: Enterprise;
    creationTimestamp?: string;
    lastUpdateTimestamp?: string;
    isRecurring?: boolean;
    hasRunOnce?: boolean;
    lastSuccessfulExecutionTimeInMs?: number;
};
export type Author = {
    resident?: Resident;
    staffMember?: StaffMember;
    device?: {
        id?: string;
        type?: string;
        connectivityStatus?: string;
        activationStatus?: string;
        hardwareId?: string;
        creationTimestamp?: string;
        notes?: Note[];
        lastPressTimestamp?: string;
    };
    schedule?: TaskSchedule;
    /** All the fields represented in a care event object. Each API call will specify the fields that are required. */
    careEvent?: {
        rid?: string;
        residentRid?: string;
        facilityRid?: string;
        alertRid?: string;
        plannedCareRid?: string;
        /** Rid of the "logged care" task that was migrated to a care event. This field is only used for migration purposes
        and will be deprecated after the care event migration is complete.
         */
        loggedCareRid?: string;
        /** Rid of the staff that documented the care event */
        creatorRid?: string;
        /** Rid of the staff that updated the care event */
        lastUpdatedByRid?: string;
        careOutcomes?: CareOutcome[];
        observations?: Observation[];
        notes?: (CreateCareEventNoteRequest & {
            /** The timestamp in milliseconds when the note was created. */
            createTimestampMs?: number;
            /** Rid of the staff that updated the note */
            lastUpdatedByRid?: string;
            /** The timestamp in milliseconds when the note was last updated. */
            updateTimestampMs?: number;
        })[];
        /** The timestamp in milliseconds when the care event was created */
        createTimestampMs?: number;
        /** The timestamp in milliseconds when the care event was last updated. */
        updateTimestampMs?: number;
        /** A list of all staff rids that responded to the care event. */
        respondingStaffRids?: string[];
    };
};
export type AlertOutcomeType = 'RESIDENT_INTERACTION' | 'TEST_ALERT' | 'FALSE_ALARM';
export type AlertOutcome = {
    rid?: string;
    type?: AlertOutcomeType;
    taskUpdateRid?: string;
    taskRid?: string;
};
export type TaskUpdate = {
    id?: string;
    type?: string;
    creationTimestamp?: string;
    creationTimestampInMs?: string;
    description?: string;
    author?: Author;
    careProvided?: string[];
    alertOutcome?: AlertOutcome;
};
export type PccTaskFollowUpAnswer = {
    /** The PCC question text. Question enums will be defined in the front end. */
    question?: string;
    /** Answer to the PCC question. Answer enums will be defined in the front end. */
    answer?: string;
    /** Care outcome submitted along with the answer */
    careProvided?: CareProvided;
};
export type PccTaskFollowUp = {
    taskRid: string;
    answers: PccTaskFollowUpAnswer[];
};
export type CareTaskType = 'PLANNED' | 'UNPLANNED' | 'PRN';
export type CareTaskOutcomeType = 'COMPLETE_INCOMPLETE' | 'CUSTOM';
export type CareTaskSupportedCustomOutcome = {
    id: string;
    name: string;
};
export type CareTaskField = 'START_TIME' | 'END_TIME' | 'DURATION' | 'NOTE' | 'REASON';
export type CareTaskRequirement =
    | 'REQUIRED'
    | 'REQUIRED_INCOMPLETE'
    | 'REQUIRED_COMPLETE'
    | 'OPTIONAL'
    | 'OPTIONAL_INCOMPLETE'
    | 'OPTIONAL_COMPLETE';
export type CareTaskChartRequirement = {
    field: CareTaskField;
    requirement: CareTaskRequirement;
};
export type CareTaskChartingReason = {
    id?: string;
    label?: string;
    relatedOutcome?: OutcomeType;
    /** Id of the CareTaskSupportedCustomOutcome this reason relates to. */
    relatedCustomOutcomeId?: string;
};
export type CareTaskIntervention = {
    adlCategory?: CareProvided;
    note?: string;
    displayName: string;
};
export type CareTask = {
    id: string;
    facilityId: string;
    shiftInstanceId: string;
    residentId: string;
    name?: string;
    careTaskType: CareTaskType;
    /** Supported options for care provided */
    careTaskCategory:
        | 'ADL_BATHING'
        | 'ADL_DRESSING'
        | 'ADL_EATING'
        | 'ADL_ESCORT'
        | 'ADL_GROOMING'
        | 'ADL_ORAL_HYGIENE'
        | 'ADL_ROUTINE_CHECK'
        | 'ADL_TOILETING'
        | 'ADL_TRANSFER'
        | 'EMERGENCY_FALL'
        | 'EMERGENCY_OTHER'
        | 'EMERGENCY_SEEN_ON_FLOOR'
        | 'HOUSEKEEPING_BATHROOM'
        | 'HOUSEKEEPING_BED'
        | 'HOUSEKEEPING_LAUNDRY'
        | 'HOUSEKEEPING_OTHER'
        | 'HOUSEKEEPING_TRASH'
        | 'HOUSEKEEPING_UNSPECIFIED'
        | 'NON_EMERGENCY_ACCIDENTAL_PRESS'
        | 'NON_EMERGENCY_FOUND_DEVICE'
        | 'NON_EMERGENCY_ILLNESS'
        | 'NON_EMERGENCY_QUESTION'
        | 'NON_EMERGENCY_NO_CARE_GIVEN'
        | 'REQUEST_EMOTIONAL_SUPPORT'
        | 'REQUEST_MAINTENANCE'
        | 'REQUEST_MEDTECH'
        | 'REQUEST_OTHER'
        | 'REQUEST_OXYGEN'
        | 'REQUEST_REPAIR'
        | 'ADL_CLINICAL_MONITORING'
        | 'ADL_COGNITIVE_BEHAVIORAL'
        | 'ADL_HEARING_ASSISTANCE'
        | 'ADL_MOBILITY_AID'
        | 'ADL_OTHER'
        | 'ADL_SKIN_CARE'
        | 'ADL_VISION_ASSISTANCE'
        | 'IADL_ACTIVITY_ASSISTANCE'
        | 'IADL_FINANCIAL_ASSISTANCE'
        | 'IADL_MEDICATION_ASSISTANCE'
        | 'IADL_PET_ASSISTANCE'
        | 'IADL_TRANSPORTATION_ASSISTANCE'
        | 'IADL_OTHER'
        | 'SAFETY_ELOPEMENT_RISK_MANAGEMENT'
        | 'SAFETY_FALL_INTERVENTION'
        | 'SAFETY_OTHER'
        | 'ADL_DRESSING_FOOTWEAR'
        | 'ADL_DRESSING_LOWER_BODY'
        | 'ADL_DRESSING_UPPER_BODY'
        | 'ADL_MOBILITY_ROLL'
        | 'ADL_MOBILITY_SIT_TO_LYING'
        | 'ADL_MOBILITY_LYING_TO_SIT'
        | 'ADL_MOBILITY_SIT_TO_STAND'
        | 'ADL_MOBILITY_PICK_UP_OBJECT'
        | 'ADL_PERSONAL_HYGIENE'
        | 'ADL_TRANSFER_CAR'
        | 'ADL_TRANSFER_SHOWER'
        | 'ADL_TRANSFER_TO_CHAIR'
        | 'ADL_TRANSFER_TOILET'
        | 'WANDER_RETURNED_SAFELY'
        | 'WANDER_ESCORTED'
        | 'WANDER_OTHER';
    responderType: 'COMMUNITY' | 'THIRD_PARTY';
    careTaskOutcomeType: CareTaskOutcomeType;
    taskAuditedId?: string;
    scheduledAtMs?: number;
    startTimeMs?: number;
    endTimeMs?: number;
    durationMs?: number;
    estimatedTimeMs?: number;
    instructions?: string;
    note?: string;
    /** This enum is add only. If you delete or update the values below, the api will break. */
    chartedOutcome?: 'COMPLETED' | 'NOT_COMPLETED' | 'REFUSED';
    chartedCustomOutcomeId?: string;
    supportedCustomOutcomes?: CareTaskSupportedCustomOutcome[];
    chartedAtMs?: number;
    wasChartedFromEhr?: boolean;
    respondingStaffMemberIds?: string[];
    chartRequirements?: CareTaskChartRequirement[];
    supportedChartingReasons?: CareTaskChartingReason[];
    chartedReasonId?: string;
    interventions?: CareTaskIntervention[];
    /** The care item this care task was created from, if any. */
    careItemId?: string;
};
export type TaskAcknowledgement = {
    staffMemberRid: string;
    acknowledgedAtUtc?: string;
    taskRid: string;
};
export type Task = {
    id?: string;
    title?: string;
    description?: string;
    status?: string;
    severity?: string;
    updates?: TaskUpdate[];
    notes?: {
        id?: string;
        creationTimestamp?: string;
        lastUpdatedTimestamp?: string;
        content?: string;
        subject?: string;
        creator?: StaffMember;
        lastUpdatedBy?: StaffMember;
    }[];
    assignee?: {
        id?: string;
        firstName?: string;
        middleName?: string;
        middleInitial?: string;
        lastName?: string;
        phoneNumber?: string;
        email?: string;
        address?: Address;
        enterprise?: Enterprise;
        facilities?: Facility[];
        onCallStates?: OnCallState[];
        staffMemberFacilityConfigs?: StaffMemberFacilityConfig[];
        groupMemberships?: StaffMemberGroupMembership[];
        isArchived?: boolean;
        isPhoneNumberVerified?: boolean;
        /** Deprecated. Use staffMemberFacilityConfigs.shouldReceiveAllCareMessages instead. */
        shouldReceiveAllCareMessages?: boolean;
        /** Deprecated. Use staffMemberFacilityConfigs.isCareAdmin instead. */
        isCareAdmin?: boolean;
        /** Deprecated. Use staffMemberFacilityConfigs.isAgencyStaff instead. */
        isAgencyStaff?: boolean;
        /** Deprecated. Use staffMemberFacilityConfigs.jobTitle instead. */
        jobTitle?: string;
    };
    facility?: {
        id?: string;
        name?: string;
        address?: Address;
        phoneNumber?: string;
        administrator?: StaffMember;
        enterprise?: Enterprise;
        logoUrl?: string;
        emergencyNotificationNumber?: string;
        requestNotificationNumber?: string;
        taskNotificationNumber?: string;
        timezone?: string;
        careZones?: CareZone[];
        messageDeliveryMethods?: MessageDeliveryMethods;
        status?: FacilityStatus;
        facilityConfig?: FacilityConfig;
        /** List of fields that are locked for editing due to EHR integration */
        ehrLockedFields?: EhrLockedField[];
    };
    enterprise?: {
        id?: string;
        name?: string;
        address?: Address;
        phoneNumber?: string;
        administrator?: StaffMember;
        facilities?: Facility[];
        logoUrl?: string;
        notificationApplicationId?: string;
        enterpriseConfig?: EnterpriseConfig;
    };
    resident?: {
        id: string;
        salutation?: string;
        firstName?: string;
        middleName?: string;
        middleInitial?: string;
        lastName?: string;
        preferredName?: string;
        dateOfBirth?: string;
        age?: number;
        personalPhone?: string;
        createTimestampMs?: number;
        updateTimestampMs?: number;
        email?: string;
        emergencyContact?: Contact;
        contacts?: Contact[];
        unit?: Unit;
        primaryCarePhysician?: ProfessionalContact;
        specialists?: ProfessionalContact[];
        notes?: Note[];
        facility?: Facility;
        enterprise?: Enterprise;
        isRoutineCheckEnabled?: boolean;
        /** Whether proactive (fall-risk) alerting is enabled for this resident. Defaults to false when absent. Alerts also require the facility's proactive alerting configuration to be enabled and within its alert window. */
        isProactiveAlertingEnabled?: boolean;
        isOnHold?: boolean;
        isArchived?: boolean;
        isCheckInsEnabled?: boolean;
        archiveReason?: string;
        healthInfo?: HealthInfo;
        preferredHospital?: Hospital;
        detectConsentStatus?: DetectConsentStatusEnum;
        /** Returns all expected exits for resident, sorted by active, then create datetime DESC */
        expectedExits?: {
            beginTimestampInUtc: string;
            endTimestampInUtc: string;
            isActive?: boolean;
            /** The staff member who created the expected exit */
            staffMemberRid?: string;
        }[];
    };
    /** A residential unit */
    unit?: {
        id?: string;
        currentOccupants?: Resident[];
        devices?: DeviceAllocation[];
        phoneNumber?: string;
        displayName?: string;
        facility?: Facility;
        enterprise?: Enterprise;
        floor?: string;
        wing?: string;
        careType?: string;
        building?: string;
        ward?: string;
    };
    door?: {
        rid: string;
        facilityRid: string;
        name?: string;
        doorAlarmSchedule: DoorAlarmSchedule;
        unit: Unit;
        deviceAllocation?: DeviceAllocation;
        /** The number of seconds a door can be left open before an alert is triggered. A value of 0 means there is no threshold and alerts will not be triggered for propped doors. This setting is currently only applicable to offline doors, with an access control device and a door sensor. */
        doorProppedThresholdInSeconds: number;
    };
    creator?: Author;
    group?: Group;
    schedule?: TaskSchedule;
    dueDateAndTime?: string;
    isRecurring?: boolean;
    creationTimestamp?: string;
    creationTimestampInMs?: number;
    lastUpdateTimestamp?: string;
    lastUpdateTimestampInMs?: number;
    careProvided?: string[];
    alertOutcome?: AlertOutcome;
    careEvent?: CareEventWithStaffDetails;
    pccTaskFollowUp?: PccTaskFollowUp;
    careTasks?: CareTask[];
    acknowledgements?: TaskAcknowledgement[];
};
export type ResidentActivity = {
    careEvent?: CareEventWithStaffDetails;
    /** The task that created this care event, such as an alert that was resolved. */
    originatingTask?: Task;
    /** Tasks that are created as a follow-up to this care event, such as an observation escalation. */
    followUpTasks?: Task[];
    alert?: Task;
    task?: Task;
};
export type CareEventResponse = ResidentActivity & {
    resident?: Resident;
};
export type RidResponse = {
    rid?: string;
};
export type CareItemType = 'PRN' | 'PLANNED';
export type ResponderType = 'COMMUNITY' | 'THIRD_PARTY';
export type CareItem = {
    id: string;
    facilityId: string;
    residentId: string;
    name: string;
    careItemType: CareItemType;
    careTaskCategory: CareProvided;
    responderType: ResponderType;
    careTaskOutcomeType: CareTaskOutcomeType;
    estimatedTimeMs?: number;
    instructions?: string;
    supportedCustomOutcomes?: CareTaskSupportedCustomOutcome[];
    chartRequirements?: CareTaskChartRequirement[];
    supportedChartingReasons?: CareTaskChartingReason[];
    interventions?: CareTaskIntervention[];
};
export type GetCareTasksResponse = {
    careTasks?: CareTask[];
    tasks?: Task[];
    careItems?: CareItem[];
};
export type UpdateCareTaskResponse = {
    careTask: CareTask;
};
export type UpdateCareTaskRequest = {
    careTaskCategory?: CareProvided;
    responderType?: ResponderType;
    careTaskOutcomeType?: CareTaskOutcomeType;
    shiftInstanceId?: string;
    scheduledAtMs?: number;
    startTimeMs?: number;
    endTimeMs?: number;
    durationMs?: number;
    estimatedTimeMs?: number;
    instructions?: string;
    note?: string;
    chartedOutcome?: OutcomeType;
    chartedCustomOutcomeId?: string;
    supportedCustomOutcomes?: CareTaskSupportedCustomOutcome[];
    chartedAtMs?: number;
    wasChartedFromEhr?: boolean;
    respondingStaffMemberIds?: string[];
    chartRequirements?: CareTaskChartRequirement[];
    shouldClearChartRequirements?: boolean;
    supportedChartingReasons?: CareTaskChartingReason[];
    chartedReasonId?: string;
    interventions?: CareTaskIntervention[];
    wasDisabledFromEhr?: boolean;
    /** Timestamp in milliseconds in UTC. */
    disabledFromEhrMs?: number;
};
export type AddEhrLockedFieldsResponse = {
    ehrLockedFields: EhrLockedField[];
};
export type AddEhrLockedFieldsRequest = {
    ehrLockedFields: {
        id?: string;
        ehrProvider?: EhrProvider;
        resourceType?: ResourceType;
        resourceField?: ResourceField;
        /** Timestamp when the locked field was created */
        createdAtMillis?: number;
    }[];
};
export type CreateOrUpdateResidentRequest = {
    /** Optional unique identifier for the resident. If provided during creation, this value will be used as the resident's ID. If omitted, a new ID will be automatically generated. */
    rid?: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    preferredName?: string;
    dateOfBirth?: string;
    personalPhoneNumber?: string;
    email?: string;
    salutation?: string;
    emergencyContactId?: string;
    unitId?: string;
    facilityId?: string;
    enterpriseId?: string;
    isRoutineCheckEnabled?: boolean;
    /** Whether proactive (fall-risk) alerting is enabled for this resident */
    isProactiveAlertingEnabled?: boolean;
    /** Indicates if the resident is on hold at the facility */
    isOnHold?: boolean;
    /** Indicates if this resident has opted in or out of check-ins. Defaults to true for IL residents. */
    isCheckInsEnabled?: boolean;
    healthInfo?: HealthInfo;
};
export type CreateOrUpdateContactRequest = {
    firstName?: string;
    lastName?: string;
    salutation?: string;
    email?: string;
    primaryPhoneNumber?: string;
    secondaryPhoneNumber?: string;
    address?: Address;
    hasPowerOfAttorney?: boolean;
    primaryLanguage?: string;
    preferredContactMethod?: string;
    isLocal?: boolean;
    relationship?: string;
};
export type CreateOrUpdateProfessionalContactRequest = {
    companyName?: string;
    companyDescription?: string;
    salutation?: string;
    firstName?: string;
    lastName?: string;
    officePhoneNumber?: string;
    personalPhoneNumber?: string;
    email?: string;
    officeFax?: string;
};
export type CreateEntireResidentRequest = {
    resident: CreateOrUpdateResidentRequest;
    primaryEmergencyContact?: CreateOrUpdateContactRequest;
    otherContacts?: CreateOrUpdateContactRequest[];
    healthInfo?: HealthInfo;
    primaryCarePhysician?: CreateOrUpdateProfessionalContactRequest;
};
export type BulkCreateResidentsRequest = {
    facilityRid?: string;
    enterpriseRid?: string;
    requests: CreateEntireResidentRequest[];
};
export type ToggleRoutineCheckRequest = {
    residentRids: RidArray;
    shouldEnableRoutineCheck: boolean;
};
export type CreateOrUpdateUnitRequest = {
    phoneNumber?: string;
    displayName?: string;
    facilityId?: string;
    enterpriseId?: string;
    floor?: string;
    wing?: string;
    careType?: string;
    building?: string;
    ward?: string;
};
export type CreateUnitRequestForFacility = {
    phoneNumber?: string;
    displayName?: string;
    floor?: string;
    wing?: string;
    careType?: string;
    building?: string;
    ward?: string;
    unitRid?: string;
};
export type BulkCreateUnitRequestForFacility = {
    facilityRid?: string;
    enterpriseRid?: string;
    requests?: CreateUnitRequestForFacility[];
};
export type UpdateUnitRequest = {
    unitRid: string;
    phoneNumber?: string;
    displayName?: string;
    floor?: string;
    wing?: string;
    careType?: string;
    building?: string;
    ward?: string;
};
export type BulkUpdateUnitsRequest = {
    units?: UpdateUnitRequest[];
};
export type CheckIn = {
    id?: string;
    date?: string;
    unit?: Unit;
    status?: string;
    checkInReason?: string;
    checkInTimestamp?: string;
};
export type CreateCheckInRequest = {
    date?: string;
    unitId?: string;
    facilityId?: string;
    enterpriseId?: string;
};
export type UpdateCheckInRequest = {
    status?: string;
    checkInReason?: string;
};
export type CreateOrUpdateStaffMemberFacilityConfigRequest = {
    /** Optional only for a creation request. The API will throw an error if this is not provided to an update. */
    id?: string;
    facilityId?: string;
    staffMemberId?: string;
    careZoneIds?: RidArray;
    jobTitle?: string;
    isAgencyStaff?: boolean;
    isCareAdmin?: boolean;
    shouldReceiveAllCareMessages?: boolean;
    shouldReceiveAllCareAlerts?: boolean;
};
export type CreateOrUpdateStaffMemberRequest = {
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    email?: string;
    enterpriseId?: string;
    /** Not required. The identity provider connection associated with the staff member's user identity, if one exists.
     */
    identityProviderConnectionRid?: string;
    /** A list of group RIDs to grant membership to. May not have duplicate groups or groups from different facilities. */
    groupsToGrantMembership?: string[];
    /** Only used when adding a staff member. Used to create a new staffMemberFacilityConfig or update an existing one. */
    isAgencyStaff?: boolean;
    /** Only used when adding a staff member. Used to create a new staffMemberFacilityConfig or update an existing one. */
    isCareAdmin?: boolean;
    /** Only used when adding a staff member. Used to create a new staffMemberFacilityConfig or update an existing one. */
    jobTitle?: string;
    /** Only used when adding a staff member. Used to create a new staffMemberFacilityConfig or update an existing one. */
    shouldReceiveAllCareMessages?: boolean;
    /** Used only when adding a staff member. The careZoneIds to associate the staff member with. It is *strongly encouraged* that this field be provided.
    If not, the staff member will be associated with all care zones in the facility if there is only 1, or none if there are multiple.
     */
    careZoneIds?: string[];
    /** Used only when updating a staff member. */
    staffMemberConfigs?: CreateOrUpdateStaffMemberFacilityConfigRequest[];
    /** The facility resource that a detect video permission change applies to. Required when grantFallReplayView is provided.
     */
    facilityRid?: string;
    /** Whether to grant (true) or revoke (false) fall replay video view for the staff member; omit or set null to leave it unchanged. Requires facilityRid, and the caller to have detect video admin permission on the facility.
     */
    grantFallReplayView?: boolean | null;
};
export type UpdateStaffPermissionsResponse = {
    numProfilesFound?: number;
};
export type UpdateStaffPermissionsRequest = {
    facilityIds?: RidArray;
    enterpriseIds?: RidArray;
};
export type GetStaffWithFallReplayViewResponse = {
    /** RIDs of staff members who have fall replay video view access at the facility. */
    canViewFallReplay?: string[];
};
export type FallReplayPermissions = {
    /** Whether the staff member has detect video view access, from any source (a reviewer or admin role at any level).
     */
    canViewVideo?: boolean;
    /** Whether the staff member holds the detect video admin role (at any level), i.e. the ability to grant or revoke others' detect video view access.
     */
    isVideoAdmin?: boolean;
    /** Whether the staff member's detect video view access is granted above the facility, via a reviewer or admin role at a higher-level resource (e.g. enterprise or root).
     */
    isVideoAccessGrantedAtHigherLevel?: boolean;
};
export type GetStaffPermissionsResponse = {
    fallReplay?: FallReplayPermissions;
};
export type StaffPermission = 'FALL_REPLAY_VIEW' | 'FALL_REPLAY_ADMIN';
export type StaffPermissionRequest = {
    roleType: StaffPermission;
};
export type UpdatePhoneNumberVerificationRequest = {
    isPhoneNumberVerified?: boolean;
};
export type CreateStaffMembersResponse = {
    data?: {
        draftRid?: string;
        publishedRid?: string;
        status?: number;
        message?: string;
    }[];
};
export type GroupAndAdminMembershipRequest = {
    groupRid?: string;
    isGroupAdmin?: boolean;
};
export type CreateEntireStaffMemberRequest = {
    staff: CreateOrUpdateStaffMemberRequest;
    groups: GroupAndAdminMembershipRequest[];
    draftRid?: string;
};
export type BulkCreateStaffMembersRequest = {
    facilityRid?: string;
    enterpriseRid?: string;
    requests?: CreateEntireStaffMemberRequest[];
};
export type MergeStaffProfilesPair = {
    sourceRid: string;
    targetRid: string;
    /** Hardware ID (TXID serial) of the device that emitted the event (e.g. 05665736) */
    deviceHardwareId?: string;
    /** Original time the event occurred on the device, in epoch milliseconds */
    occurredAtTimestampInMs?: number;
};
export type MergeStaffProfilesResponse = {
    successful?: MergeStaffProfilesPair[];
    failed?: MergeStaffProfilesPair[];
};
export type MergeStaffProfilesRequest = {
    mergePairs: MergeStaffProfilesPair[];
};
export type GetResidentActivitiesResponse = {
    hasNextPage: boolean;
    pageSize: number;
    pageNumber: number;
} & {
    activities?: ResidentActivity[];
};
export type CreateEnterpriseRequest = {
    name?: string;
    address?: Address;
    phoneNumber?: string;
    logoUrl?: string;
    notificationApplicationId?: string;
    enterpriseRid?: string;
    enterpriseConfig?: {
        ssoStatus?: 'DISABLED' | 'PENDING' | 'ENABLED' | 'ENFORCED';
        ssoExemptDomains?: string[];
    };
};
export type CreateFacilityRequest = {
    name?: string;
    /** Address */
    address?: {
        addressLine1?: string;
        addressLine2?: string;
        city?: string;
        state?: string;
        zipcode?: string;
        country?: string;
    };
    phoneNumber?: string;
    enterpriseId?: string;
    logoUrl?: string;
    emergencyNotificationNumber?: string;
    requestNotificationNumber?: string;
    taskNotificationNumber?: string;
    timezone?: string;
    facilityRid?: string;
    /** Delivery methods in order of preference.  Will attempt to send all messages using the first delivery methods, falling back to second delivery method and so on. */
    messageDeliveryMethods?: string[];
    /** If true and facilityConfig set, will set default observations on facility config */
    setDefaultFacilityConfigObservations?: boolean;
    facilityConfig?: {
        isAlertLocationEnabled?: boolean;
        /** Care types for which check-in buttons are enabled at this facility. Defaults to [INDEPENDENT_LIVING] when absent.
         */
        checkInsEnabledCareTypes?: string[];
        routineCheckIntervalInMin?: number;
        isObservationsEnabled?: boolean;
        isEventPublishingEnabled?: boolean;
        isExtendedFallVideoEnabled?: boolean;
        isDomeLightsEnabled?: boolean;
        isStaleM3AlertClosureEnabled?: boolean;
        isDetectHumanInTheLoopEnabled?: boolean;
        isDoorAlertCareConfirmationEnabled?: boolean;
        /** Mapping of CareType (string) to list of ObservationTypes */
        observationsByCareType?: {
            [key: string]: ObservationType[];
        };
        /** Non-SNF care types for which observation details are enabled. Details are enabled for SNF care types. SNF care types are SKILLED_NURSING, SHORT_TERM_REHAB and LONG_TERM_CARE. Other care types (INDEPENDENT_LIVING, ASSISTED_LIVING, MEMORY_CARE) are considered non-SNF care types. */
        observationDetailsEnabledCareTypes?: string[];
        /** Map of EHR provider names to their configurations */
        ehrIntegrationsConfig?: {
            [key: string]: EhrProviderConfig;
        };
        /** Detect information for the facility */
        detectConfig?: {
            /** Whether proactive alerting is enabled at this facility. Absent means disabled. */
            isProactiveAlertingEnabled?: boolean;
            /** Facility-local time of day (HH:mm, 24-hour) at which the proactive alerting window opens. Defaults to "22:00" when absent. May be later than proactiveAlertWindowEndTime, in which case the window spans midnight. */
            proactiveAlertWindowStartTime?: string;
            /** Facility-local time of day (HH:mm, 24-hour) at which the proactive alerting window closes. Defaults to "06:00" when absent. */
            proactiveAlertWindowEndTime?: string;
        } & {
            detectCameras?: 'NONE' | 'ONBOARDING' | 'QA' | 'LIVE';
            isDetectHumanInTheLoopEnabled?: boolean;
            isExtendedFallVideoEnabled?: boolean;
        };
        /** Gen3 features configured for this facility */
        gen3Features?: Gen3Feature[];
    };
};
export type CreateOrUpdateTaskRequest = {
    title?: string;
    description?: string;
    status?: string;
    severity?: string;
    durationInMinutes?: number;
    assigneeRid?: string;
    residentRid?: string;
    unitRid?: string;
    doorRid?: string;
    creatorRid?: string;
    groupRid?: string;
    facilityRid?: string;
    enterpriseRid?: string;
    /** Only applicable on updateTask.
    If true, will bypass any care confirmation related status checks.
     */
    bypassCareConfirmationChecks?: boolean;
};
export type CreateOrUpdateGroupRequest = {
    name?: string;
    facilityId?: string;
    enterpriseId?: string;
    iconUrl?: string;
};
export type UpdateEnterpriseRequest = {
    name?: string;
    address?: Address;
    phoneNumber?: string;
    administratorId?: string;
    logoUrl?: string;
    notificationApplicationId?: string;
    enterpriseConfig?: EnterpriseConfig;
};
export type UpdateFacilityRequest = {
    name?: string;
    address?: Address;
    phoneNumber?: string;
    administratorId?: string;
    enterpriseId?: string;
    logoUrl?: string;
    emergencyNotificationNumber?: string;
    requestNotificationNumber?: string;
    taskNotificationNumber?: string;
    timezone?: string;
    messageDeliveryMethods?: MessageDeliveryMethods;
    /** If true and facilityConfig set, will upsert default observations on facility config */
    setDefaultFacilityConfigObservations?: boolean;
    facilityConfig?: FacilityConfig;
};
export type CreateResidentExpectedExitRequest = {
    /** Optional. Defaults to the current time in the service if not provided. */
    beginTimestampInUtc?: string;
};
export type UpdateResidentDetectConsentRequest = {
    detectConsentStatus?: 'ACCEPTED' | 'DECLINED' | 'NOT_RECEIVED';
};
export type LatestCareActivity = {
    task?: Task;
    careEvent?: CareEvent;
    timeSinceLastCareActivityInMin: number;
};
export type LatestCareActivityMap = {
    [key: string]: LatestCareActivity;
};
export type GetLatestCareActivityRequest = {
    residentRids?: RidArray;
};
export type UpdatePlannedCareTask = {
    id: string;
    note?: string;
    chartedOutcome?: OutcomeType;
    chartedCustomOutcomeId?: string;
    chartedReasonId?: string;
    chartedAtMs: number;
    respondingStaffMemberIds: string[];
};
export type ResolveTaskUpdatePlannedCareTask = UpdatePlannedCareTask;
export type CreateUnplannedCareTask = {
    facilityId: string;
    shiftInstanceId: string;
    residentId: string;
    careTaskCategory: CareProvided;
    responderType: ResponderType;
    chartedOutcome: OutcomeType;
    chartedAtMs: number;
    note?: string;
    respondingStaffMemberIds: string[];
};
export type CreatePrnCareTask = {
    facilityId: string;
    shiftInstanceId: string;
    /** The PRN care item this care task is being created from. */
    careItemId: string;
    residentId: string;
    careTaskCategory: CareProvided;
    responderType: ResponderType;
    chartedOutcome: OutcomeType;
    /** Required if the care task is being marked with a custom outcome from the care item's supportedCustomOutcomes. This validation will be enforced by BE. */
    chartedCustomOutcomeId?: string;
    chartedAtMs: number;
    note?: string;
    startTimeMs?: number;
    endTimeMs?: number;
    durationMs?: number;
    respondingStaffMemberIds: string[];
};
export type ResolveTaskRequest = {
    /** Deprecated. Use careOutcomes instead.
    A list of resolution types (i.e. care provided) to resolve the task with.
     */
    resolutionTypes?: string[];
    alertOutcomeType?: AlertOutcomeType;
    resolutionNote?: string;
    residentId?: string;
    /** If true, will bypass any care confirmation related status checks. */
    bypassCareConfirmationChecks?: boolean;
    careOutcomes?: {
        careProvided: CareProvided;
        outcome: OutcomeType;
        /** This enum is add only. If you delete or update the values below, the api will break. */
        assistanceLevel?:
            | 'INDEPENDENT'
            | 'SETUP_CLEANUP'
            | 'SUPERVISION_TOUCH'
            | 'MODERATE_ASSIST'
            | 'SUBSTANTIAL_ASSIST'
            | 'DEPENDENT';
        creatorEntityRid?: string;
        alertRid?: string;
    }[];
    observations?: {
        /** Supported options for observation types */
        observationType:
            | 'BOWEL_MOVEMENT'
            | 'URINE'
            | 'MOOD_BEHAVIOR'
            | 'PAIN'
            | 'SKIN'
            | 'EATING_DRINKING_LESS'
            | 'OTHER'
            | 'SEEMS_DIFFERENT'
            | 'NEEDS_MORE_HELP'
            | 'SKIN_ISSUE'
            | 'ABNORMAL_BOWEL_MOVEMENT'
            | 'ABNORMAL_URINE'
            | 'MOOD_BEHAVIOR_ISSUE';
        observationValue?: string;
        observationNote?: string;
    }[];
    respondingStaffRids?: string[];
    updatePlannedCareTasks?: ResolveTaskUpdatePlannedCareTask[];
    createUnplannedCareTasks?: CreateUnplannedCareTask[];
    createPrnCareTasks?: CreatePrnCareTask[];
    pccTaskFollowUpAnswers?: PccTaskFollowUpAnswer[];
};
export type ReviewTaskRequest = {
    alertOutcomeType?: AlertOutcomeType;
};
export type CareConfirmationTracker = {
    task?: Task;
    /** A stringified Java Duration represented in milliseconds. Normally should be less than approximately 30s. */
    timeSinceLastUpdate?: string;
};
export type SyncOfflineAlertResponse = {
    alert: Task;
};
export type SyncOfflineAlertRequest = {
    /** Stable identifier for the episode this trail describes, not for the attempt. The same
    episode retried must carry the same key, and two different episodes must never share one.
    Callers built on the Inovonics relay path use the episode's event_id, which every event
    of the trail already shares.
    
    A request without a key is rejected with a 400: without one, a retry after an ambiguous
    failure (a timeout on a request that in fact succeeded) would create a second closed
    Task for one episode.
     */
    idempotencyKey: string;
    events: {
        facilityRid: string;
        serialNumber: string;
        occurenceEpochTimeInMs: number;
        /** Event kind for non-Flic producers. Supported values depend on the device type.
        Inovonics:
          "PRESS" = normal button press / alert trigger
          "CONFIRM" = on-device confirmation via magnet or press sequence
         */
        eventKind: string;
        /** Optional. Battery level of the device at the time of the event. */
        batteryLevel?: number;
    }[];
};
export type CreateTaskUpdateRequest = {
    type?: string;
    description?: string;
    authorRid?: string;
    taskRid?: string;
};
export type AllocationType =
    | 'BUTTON'
    | 'BUTTON_WITH_RESET'
    | 'CARE_CONFIRMATION_BUTTON'
    | 'CHECKIN_MOVEMENT_SENSOR'
    | 'DOOR'
    | 'FALL_DETECTION_DEVICE'
    | 'M3'
    | 'OFFLINE_DOOR'
    | 'ONLINE_DOOR'
    | 'PULL_CORD'
    | 'UNIVERSAL_TRANSMITTER'
    | 'UNIVERSAL_TRANSMITTER_BUTTON';
export type CreateOrUpdateDeviceAllocationRequest = {
    /** Deprecated. Use deviceRids instead. */
    deviceRid?: string;
    deviceRids?: string[];
    allocationType?: AllocationType;
    unitRid?: string;
    room?: string;
    facilityId?: string;
    enterpriseId?: string;
    displayName?: string;
    residentId?: string;
    isCheckIn?: boolean;
    doorRid?: string;
    isSilent?: boolean;
};
export type CreateDeviceRequest = {
    hardwareId?: string;
    type?: string;
};
export type CreateSpeakerRequest = {
    hardwareId: string;
    unitRid?: string;
    facilityRid?: string;
};
export type Speaker = {
    rid?: string;
    hardwareId?: string;
    unitRid?: string;
    facilityRid?: string;
};
export type UpdateSpeakerRequest = {
    hardwareId?: string;
    unitRid?: string;
    facilityRid?: string;
};
export type CreateNurseStationRequest = {
    facilityId: string;
    email?: string;
    displayName?: string;
};
export type NurseStation = {
    id: string;
    facilityId: string;
    email?: string;
    displayName?: string;
};
export type UpdateNurseStationRequest = {
    email?: string;
    displayName?: string;
};
export type UpdateOnCallStateRequest = {
    isOnCall: boolean;
    companyPhoneId?: string;
    shiftInstanceId?: string;
};
export type GroupEnrollmentEntry = {
    /** The name of the group to enroll the staff member in (e.g. "Management", "Nurse", "MedTech") */
    groupName: string;
    /** Whether the staff member should be set as an admin of the group. Defaults to false if omitted. */
    isAdmin?: boolean;
};
export type SetEnterpriseGroupMembershipsRequest = {
    /** The specific facilities to target. If omitted, all facilities in the enterprise are targeted. */
    facilityRids?: string[];
    /** If provided, sets the job title on the staff member's facility config for each targeted
    facility. An empty string clears the job title. If omitted, the existing job title is unchanged.
     */
    jobTitle?: string;
    /** Duplicate groupNames within this list will result in a 400. */
    groupEnrollments: GroupEnrollmentEntry[];
};
export type GroupMembershipEntry = {
    staffMember?: StaffMember;
    isAdmin?: boolean;
};
export type CreateOrUpdateGroupMembershipRequest = {
    staffMemberId?: string;
    isAdmin?: boolean;
};
export type CountryCode = {
    name?: string;
    abbreviation?: string;
    code?: string;
};
export type TaskSeverity = {
    id?: string;
    displayName?: string;
    severity?: number;
};
export type NamedEnum = {
    id?: string;
    displayName?: string;
};
export type ResidentHealthEnum = {
    id?: string;
    displayName?: string;
    severity?: number;
};
export type MemoryConfiguration = {
    memoryLossLevels?: ResidentHealthEnum[];
};
export type ContinenceConfiguration = {
    continenceLevels?: ResidentHealthEnum[];
    productProviders?: ResidentHealthEnum[];
};
export type AdlConfiguration = {
    safetyLevels?: ResidentHealthEnum[];
    feedingLevels?: ResidentHealthEnum[];
    bathingLevels?: ResidentHealthEnum[];
    toiletingLevels?: ResidentHealthEnum[];
    transferringLevels?: ResidentHealthEnum[];
    mobilityLevels?: ResidentHealthEnum[];
    dressingLevels?: ResidentHealthEnum[];
    groomingLevels?: ResidentHealthEnum[];
};
export type ServiceConfiguration = {
    countryCodes?: CountryCode[];
    taskSeverityLevels?: TaskSeverity[];
    taskStatuses?: NamedEnum[];
    taskUpdateTypes?: string[];
    contactRelationships?: NamedEnum[];
    contactMethods?: NamedEnum[];
    salutations?: NamedEnum[];
    defaultLanguages?: NamedEnum[];
    defaultRooms?: NamedEnum[];
    defaultGroups?: NamedEnum[];
    alertResolutionTypes?: NamedEnum[];
    memoryConfiguration?: MemoryConfiguration;
    continenceConfiguration?: ContinenceConfiguration;
    adlConfiguration?: AdlConfiguration;
    resuscitationStrategies?: NamedEnum[];
    residentArchiveReasons?: NamedEnum[];
};
export type ResolveNumbersResponse = {
    applicationId?: string;
    facility?: Facility;
    sendingStaffMember?: StaffMember;
    sendingStaffMemberGroups?: Group[];
    recipientStaffMembers?: StaffMember[];
};
export type CreateOrUpdateTaskNoteRequest = {
    content?: string;
    subject?: string;
};
export type UpdateResidentArchiveStatusRequest = {
    isArchived?: boolean;
    archiveReason?: string;
};
export type StaffMemberFacilityMetadata = {
    facilityId?: string;
    onCallState?: {
        isOnCall: boolean;
        lastModified: string;
        facilityId: string;
        companyPhoneId?: string;
        shiftInstanceId?: string;
    };
    groups?: {
        groupId?: string;
        groupName?: string;
        isAdmin?: boolean;
    }[];
};
export type VideoReplayType = 'FALL' | 'EXTENDED_FALL' | 'FALL_RISK' | 'EXTENDED_FALL_RISK';
export type VideoReplayResponse = {
    /** Presigned URL for downloading the video replay */
    downloadUrl: string;
    /** When the download URL expires (unix timestamp in milliseconds) */
    expiresAtTimestampInMs?: number;
    type: VideoReplayType;
};
export type VideoReplayAvailabilityStatus = 'NOT_FOUND' | 'NOT_READY' | 'READY';
export type VideoReplayAvailabilityResponse = {
    fall: VideoReplayAvailabilityStatus;
    extendedFall: VideoReplayAvailabilityStatus;
    fallRisk: VideoReplayAvailabilityStatus;
    extendedFallRisk: VideoReplayAvailabilityStatus;
};
export type TaskSearchResult = {
    tasks?: Task[];
    numResults?: number;
    numPages?: number;
    pageSize?: number;
    hasNextPage?: boolean;
};
export type TaskSearchRequest = {
    facilityId?: string;
    enterpriseId?: string;
    assigneeId?: string;
    creatorId?: string;
    residentId?: string;
    unitId?: string;
    doorId?: string;
    scheduleId?: string;
    deviceType?: string;
    deviceTypes?: string[];
    groupIds?: string[];
    statuses?: string[];
    severities?: string[];
    careZoneIds?: string[];
    pageSize?: number;
    pageNumber?: number;
};
export type ClosedTaskSearchRequest = TaskSearchRequest & {
    alertOutcomeTypes?: AlertOutcomeType[];
    /** Filter tasks created at or after this timestamp */
    fromCreatedTimestampInEpochMs?: number;
};
export type TaskStatus =
    | 'OPEN_UNCLAIMED'
    | 'OPEN_UNACKNOWLEDGED'
    | 'OPEN_CLAIMED'
    | 'OPEN_ACKNOWLEDGED'
    | 'OPEN_CONFIRMING'
    | 'OPEN_CONFIRMED'
    | 'CLOSED_RESOLVED'
    | 'CLOSED_CANCELED'
    | 'CLOSED_UNRESOLVED'
    | 'CLOSED_REVIEWED';
export type TaskSeverityType =
    | 'CODE_BLUE'
    | 'ELOPEMENT'
    | 'WANDER'
    | 'POTENTIAL_FALL'
    | 'PROACTIVE_FALL_RISK'
    | 'EMERGENCY'
    | 'STAFF_ASSIST'
    | 'PROPPED_DOOR'
    | 'UNAUTHORIZED_EXIT'
    | 'DOOR_OPEN'
    | 'ESCALATION_OBSERVATION'
    | 'HIGH_PRIORITY'
    | 'MEDIUM_PRIORITY'
    | 'LOW_PRIORITY'
    | 'NO_PRIORITY';
export type OpenAlert = {
    id: string;
    status: TaskStatus;
    severity: TaskSeverityType;
    /** Return whether caller has acknowledged the task, if applicable */
    acknowledgedByUserId?: boolean;
    creationTimestampInMs?: number;
    unit?: Unit;
    door?: Door;
    assignee?: StaffMember;
    resident?: Resident;
    creator?: Author;
    updates?: TaskUpdate[];
};
export type SearchOpenAlertsRequest = {
    facilityRid?: string;
    careZoneIds?: string[];
    groupIds?: string[];
};
export type ResidentCareInfoCategory = 'FALL_RISK_INFO' | 'ASSIST_LEVEL_INFO';
export type ResidentCareInfoKey = 'FALL_RISK' | 'ASSIST_LEVEL' | 'LIFT_TYPE';
export type ResidentCareInfoValue = any | null;
export type ResidentCareInfoItem = {
    rid?: string;
    category?: ResidentCareInfoCategory;
    key?: ResidentCareInfoKey;
    value?: ResidentCareInfoValue;
};
export type GetResidentCareInfoResponse = {
    careInfo?: ResidentCareInfoItem[];
};
export type SetResidentCareInfoItem = {
    category: ResidentCareInfoCategory;
    key: ResidentCareInfoKey;
    value?: ResidentCareInfoValue;
};
export type SetResidentCareInfoRequest = {
    careInfo: SetResidentCareInfoItem[];
};
export type TaskTemplateDefinition = {
    title?: string;
    description?: string;
    severity?: string;
    assigneeId?: string;
    groupId?: string;
    residentId?: string;
    unitId?: string;
};
export type CreateOrUpdateTaskScheduleRequest = {
    taskTemplateDefinition?: TaskTemplateDefinition;
    recurrenceDefinition?: ScheduleRecurrenceDefinition;
    facilityId?: string;
    enterpriseId?: string;
};
export type TaskScheduleSearchResult = {
    taskSchedules?: TaskSchedule[];
    numResults?: number;
    numPages?: number;
    pageSize?: number;
};
export type TaskScheduleSearchRequest = {
    facilityId?: string;
    enterpriseId?: string;
    creatorId?: string;
    residentId?: string;
    unitId?: string;
    groupIds?: string[];
    severities?: string[];
    isRecurring?: boolean;
    hasRunOnce?: boolean;
    pageSize?: number;
    pageNumber?: number;
};
export type UserTaskSearchRequest = {
    facilityId?: string;
    statuses?: string[];
    severities?: string[];
    pageSize?: number;
    pageNumber?: number;
};
export type AddDeviceActivityRequest = {
    hardwareID?: string;
    batteryLevel?: number;
    interactionType?: string;
    hubId?: string;
};
export type LatestDeviceActivitySummary = {
    batteryLevel?: number;
    latestActivityTimestamp: number;
};
export type LatestDeviceActivityValue = number | LatestDeviceActivitySummary;
export type LatestDeviceActivities = {
    [key: string]: LatestDeviceActivityValue;
};
export type GetLatestDeviceActivityRequest = {
    facilityRid?: string;
    deviceRids?: RidArray;
};
export type ResidentActivityUpdatePlannedCareTask = UpdatePlannedCareTask & {
    startTimeMs?: number;
    endTimeMs?: number;
    durationMs?: number;
};
export type CreateResidentActivityRequest = {
    residentId: string;
    unitId?: string;
    creatorId?: string;
    groupId?: string;
    facilityId: string;
    enterpriseId?: string;
    /** Deprecated. Use careOutcomes instead. */
    resolutionType?: string;
    /** Deprecated. Use careOutcomes instead. */
    resolutionTypes?: string[];
    careOutcomes: CareOutcome[];
    observations?: Observation[];
    resolutionNote?: string;
    respondingStaffRids: string[];
    updatePlannedCareTasks?: ResidentActivityUpdatePlannedCareTask[];
    createUnplannedCareTasks?: CreateUnplannedCareTask[];
    createPrnCareTasks?: CreatePrnCareTask[];
    pccTaskFollowUpAnswers?: PccTaskFollowUpAnswer[];
};
export type CompanyPhone = {
    id?: string;
    phoneNumber?: string;
    displayName?: string;
    facility?: Facility;
    enterprise?: Enterprise;
};
export type CreateOrUpdateCompanyPhoneRequest = {
    phoneNumber?: string;
    displayName?: string;
    facilityId?: string;
    enterpriseId?: string;
    companyPhoneRid?: string;
};
export type BulkCreateCompanyPhonesRequest = {
    facilityRid?: string;
    enterpriseRid?: string;
    requests?: CreateOrUpdateCompanyPhoneRequest[];
};
export type CreateOrUpdateHospitalRequest = {
    name?: string;
    phoneNumber?: string;
    faxNumber?: string;
    address?: Address;
};
export type CreateOrUpdateCareZoneRequest = {
    facilityId?: string;
    displayName?: string;
    /** A collection of tags used to describe a Unit. These tags will all be collected and used as search parameters, to find matching Units which may have a matching tag. */
    unitTags?: {
        floors?: string[];
        wings?: string[];
        careTypes?: string[];
        buildings?: string[];
        wards?: string[];
        unitRids?: RidArray;
    };
};
export type StaffOnCallRequest = {
    facilityRid?: string;
    groupRids?: string[];
};
export type UnitSearchResult = {
    units?: Unit[];
    numResults?: number;
    pageSize?: number;
    pageNumber?: number;
};
export type UnitSearchRequest = {
    unitIds?: string[];
    enterpriseIds?: string[];
    facilityIds?: string[];
    careZoneIds?: string[];
    displayNames?: string[];
    phoneNumbers?: string[];
    floors?: string[];
    wings?: string[];
    careTypes?: string[];
    buildings?: string[];
    wards?: string[];
    pageSize?: number;
    pageNumber?: number;
};
export type CheckInSearchResult = {
    checkIns?: CheckIn[];
    numResults?: number;
    pageSize?: number;
    pageNumber?: number;
};
export type CheckInSearchRequest = {
    fromDate?: string;
    toDate?: string;
    unitIds?: string[];
    careZoneIds?: string[];
    facilityIds?: string[];
    enterpriseIds?: string[];
    statuses?: string[];
    checkInReasons?: string[];
    isOnHold?: boolean;
    pageSize?: number;
    pageNumber?: number;
};
export type CreateStaffFacilityPreferencesRequest = {
    facilityId: string;
    staffMemberId: string;
    pinnedResidentIds?: RidArray;
};
export type UpdateStaffFacilityPreferencesRequest = {
    pinnedResidentIds: RidArray;
};
export type StaffFacilityPreferences = {
    id?: string;
    staffMemberId?: string;
    facilityId?: string;
    pinnedResidentIds?: RidArray;
};
export type StaffFacilityPreferenceSearchRequest = {
    facilityId: string;
    staffMemberIds?: RidArray;
};
export type StaffMemberFacilityConfigSearchRequest = {
    staffMemberIds?: RidArray;
    facilityIds?: RidArray;
    careZoneIds?: RidArray;
};
export type StaffProfileSearchRequest = {
    enterpriseId: string;
    facilityId: string;
    phoneNumber?: string;
    lastName?: string;
    email?: string;
};
export type CreateOrUpdateDoorRequest = {
    facilityRid?: string;
    unitRid?: string;
    doorRid?: string;
    doorAlarmScheduleRid?: string;
    doorName?: string;
    /** The number of seconds a door can be left open before an alert is triggered. A value of 0 means there is no threshold and alerts will not be triggered for propped doors. This setting is currently only applicable to offline doors, with an access control device and a door sensor. */
    doorProppedThresholdInSeconds?: number;
};
export type BulkCreateDoorsRequest = {
    facilityRid?: string;
    requests?: {
        facilityRid?: string;
        unitRid?: string;
        doorRid?: string;
        doorAlarmScheduleRid?: string;
        doorName?: string;
        /** The number of seconds a door can be left open before an alert is triggered. A value of 0 means there is no threshold and alerts will not be triggered for propped doors. This setting is currently only applicable to offline doors, with an access control device and a door sensor. */
        doorProppedThresholdInSeconds?: number;
    }[];
};
export type Feature = {
    id?: string;
    featureName?: string;
    enabledFacilityIds?: RidArray;
};
export type FeaturesResponse = {
    features?: Feature[];
};
export type CreateFeatureRequest = {
    /** A Title Cased user-friendly name for the feature (e.g., "New Feature"). This is used to generate the feature ID. The feature name cannot be updated after it is created. */
    featureName: string;
    /** The list of facility RIDs that the feature should be enabled for. These RIDs will be added to the enabledFacilityIds list. */
    enabledFacilityIds?: RidArray;
    /** The list of facility RIDs that the feature should be enabled for. These RIDs will be removed from the enabledFacilityIds list. */
    disabledFacilityIds?: RidArray;
};
export type UpdateFeatureRequest = {
    /** The list of facility RIDs that the feature should be enabled for. These RIDs will be added to the enabledFacilityIds list. */
    enabledFacilityIds?: RidArray;
    /** The list of facility RIDs that the feature should be enabled for. These RIDs will be removed from the enabledFacilityIds list. */
    disabledFacilityIds?: RidArray;
};
export type FacilityNotificationContext = {
    facilityRid?: string;
    facilityName?: string;
    emergencyNotificationNumber?: string;
    taskNotificationNumber?: string;
    messageDeliveryMethods?: MessageDeliveryMethods;
};
export type NotificationContext = {
    eligibleRecipients?: {
        phoneNumber?: string;
        recipientId?: string;
        subscriptions?: {
            recipientId: string;
            keys?: {
                /** The public key used for encryption. */
                p256dh?: string;
                /** The authentication secret used for encryption. */
                auth?: string;
            };
            /** The URL identifying the push endpoint. */
            endpoint: string;
            expirationTime?: string;
            rid?: string;
            isMobilePhone?: boolean;
        }[];
        isCompanyPhone?: boolean;
    }[];
    ineligibleRecipients?: {
        recipientId: string;
        reason: string;
    }[];
    facilityNotificationContext?: FacilityNotificationContext;
};
export type GetNotificationContextRequest = {
    recipientIds: string[];
    recipientType: string;
    textDeliveryStrategy?: string;
    facilityRid: string;
};
export type NotificationType = 'ALERT' | 'WANDER';
export type NotificationTemplate =
    | 'ALERT_UNCLAIMED_GROUP_REMINDER'
    | 'ALERT_UNRESOLVED_ASSIGNEE_REMINDER'
    | 'ALERT_BREAK_GLASS'
    | 'ALERT_BREAK_GLASS_CARE_ZONE'
    | 'ALERT_BREAK_GLASS_SHIFT'
    | 'WANDER_UNRESOLVED_REMINDER'
    | 'WANDER_BREAK_GLASS';
export type DurationInMs = number;
export type NotificationFilterBase = {
    rid?: string;
};
export type OnCallStatusFilter = {
    type: 'OnCallStatusFilter';
} & NotificationFilterBase & {
        onCall?: boolean;
    };
export type GroupMembershipComparator = {
    onlyAdmins?: boolean | null;
};
export type GroupMembershipComparatorMap = {
    [key: string]: GroupMembershipComparator;
};
export type GroupMembershipFilter = {
    type: 'GroupMembershipFilter';
} & NotificationFilterBase & {
        inclusive?: boolean | null;
        groupMembershipComparators?: GroupMembershipComparatorMap;
    };
export type StaffMemberFilter = {
    type: 'StaffMemberFilter';
} & NotificationFilterBase & {
        inclusive?: boolean | null;
        staffMemberRids?: string[];
    };
export type CareZoneFilter = {
    type: 'CareZoneFilter';
} & NotificationFilterBase & {
        inclusive?: boolean | null;
        byPassIfEmptyResult?: boolean;
    };
export type Rids = string[];
export type NonAssignedCareZoneFilter = {
    type: 'NonAssignedCareZoneFilter';
} & NotificationFilterBase & {
        inclusive?: boolean | null;
        byPassIfEmptyResult?: boolean;
        careZoneRids?: Rids;
    };
export type AssigneeFilter = {
    type: 'AssigneeFilter';
} & NotificationFilterBase & {
        byPassIfEmptyResult?: boolean | null;
    };
export type NotificationFilter =
    | OnCallStatusFilter
    | GroupMembershipFilter
    | StaffMemberFilter
    | CareZoneFilter
    | NonAssignedCareZoneFilter
    | AssigneeFilter;
export type NotificationBlock = {
    rid?: string;
    name: string;
    template: NotificationTemplate;
    executionTimeAfterStartInMs: DurationInMs;
    relativePriority: number;
    recurringIntervalInMs?: DurationInMs;
    matchingStatuses: string[];
    isBreakGlass: boolean;
    filters?: NotificationFilter[];
};
export type NotificationPolicy = {
    rid?: string;
    version?: number;
    facilityRid: string;
    type: NotificationType;
    notificationBlocks: NotificationBlock[];
    /** A list of statuses that will cause an active notification policy to be cleaned up and removed.
    This is used to prevent sending notifications for that are no longer relevant. For now, it only supports task statuses.
     */
    terminalStatuses?: string[];
};
export type UpdateNotificationFilterRequestBase = {
    inclusive?: boolean | null;
};
export type UpdateStaffFilterRequest = {
    type: 'UpdateStaffFilterRequest';
} & UpdateNotificationFilterRequestBase & {
        staffRidsToAdd?: Rids;
        staffRidsToDelete?: Rids;
    };
export type UpdateGroupMembershipFilterRequest = {
    type: 'UpdateGroupMembershipFilterRequest';
} & UpdateNotificationFilterRequestBase & {
        groupMembershipComparatorsToMerge?: GroupMembershipComparatorMap;
        groupMembershipComparatorsToDelete?: GroupMembershipComparatorMap;
    };
export type UpdateNotificationFilterRequest =
    | UpdateStaffFilterRequest
    | UpdateGroupMembershipFilterRequest;
export type ComponentsSchemasSimulateDeviceEventRequestPropertiesDoorAlarmEventAllOf0 = {
    devEui?: string;
    gatewayRid?: string;
    facilityRid?: string;
    enterpriseRid?: string;
    gatewayName?: string;
    gatewayLocation?: string;
    gatewayDescription?: string;
    sqsApproximateReceiveCount?: number;
    sqsSentEpochTimeInMs?: number;
    sqsFirstReceiveEpochTimeInMs?: number;
    gatewayReceivedEpochTimeInSec?: number;
    serviceReceivedEpochTimeInMs?: number;
    rssi?: number;
    loraSnr?: number;
    frameCount?: number;
    framePort?: number;
    frequency?: number;
    adaptiveDataRate?: boolean;
    altitude?: number;
    latitude?: number;
    longitude?: number;
    data?: string;
    deviceType?: string;
    ridType?: string;
    /** Not every uplink carries a battery reading -- e.g. the Milesight WS203's Historical Data
    uplink never includes one (only its Battery Report does). Null means the originating
    uplink type doesn't report battery, not a device fault or missing data.
     */
    batteryLevel?: number | null;
};
export type SimulateDeviceEventRequest =
    | {
          buttonEvent: {
              nodeBroadcast?: {
                  bdaddr?: string;
                  nodeId?: string;
                  rssi?: number;
                  requestEpochTimeInMs?: number;
                  broadcastReceivedEpochTimeInMs?: number;
                  facilityRid?: string;
                  enterpriseRid?: string;
                  /** Optional event kind (e.g. for simulating an Inovonics device event). null/absent or "PRESS" = a normal alarm press; "CONFIRM" = a magnet-confirm that moves the unit's open alert to OPEN_CONFIRMED. Maps to NodeBroadcastDetection.eventKind. */
                  eventKind?: string;
              };
              buttonSerialNumber?: string;
              eventType?: string;
          };
          doorAlarmEvent?: {
              devEui?: string;
              gatewayRid?: string;
              facilityRid?: string;
              enterpriseRid?: string;
              gatewayName?: string;
              gatewayLocation?: string;
              gatewayDescription?: string;
              sqsApproximateReceiveCount?: number;
              sqsSentEpochTimeInMs?: number;
              sqsFirstReceiveEpochTimeInMs?: number;
              gatewayReceivedEpochTimeInSec?: number;
              serviceReceivedEpochTimeInMs?: number;
              rssi?: number;
              loraSnr?: number;
              frameCount?: number;
              framePort?: number;
              frequency?: number;
              adaptiveDataRate?: boolean;
              altitude?: number;
              latitude?: number;
              longitude?: number;
              data?: string;
              deviceType?: string;
              ridType?: string;
              /** Not every uplink carries a battery reading -- e.g. the Milesight WS203's Historical Data
        uplink never includes one (only its Battery Report does). Null means the originating
        uplink type doesn't report battery, not a device fault or missing data.
         */
              batteryLevel?: number | null;
          } & {
              isDoorOpen?: boolean;
              doorEventCount?: number;
          };
          fallDetectionEvent?: {
              hardwareId?: string;
              /** Device type as defined by the manufacturer, NOT a Sage device entity type */
              deviceType?: string;
              enterpriseRid?: string;
              facilityRid?: string;
              unitRid?: string;
              ridType?: string;
              /** The specific activity detected by a Detect camera, produced by video-sensing-worker-service. Absent means POTENTIAL_FALL (producers that predate this type do not set it). Every value other than POTENTIAL_FALL is a fall-risk activity that feeds proactive alerting; new activity values are added here as detection expands. This enum is add only. If you delete or update the values below, the api will break. */
              eventType?: 'POTENTIAL_FALL' | 'STANDING' | 'EDGE_OF_BED';
          };
          meltenM3Event?: {
              deviceAddress?: string;
              relayRid?: string;
              facilityRid?: string;
              unitRid?: string;
              callingType?: string;
              actionType?: string;
              relayReceivedEpochTimeInMs?: number;
              serviceReceivedEpochTimeInMs?: number;
          };
          movementSensorMessage?: ComponentsSchemasSimulateDeviceEventRequestPropertiesDoorAlarmEventAllOf0 & {
              occupied?: boolean;
          };
      }
    | {
          buttonEvent?: {
              nodeBroadcast?: {
                  bdaddr?: string;
                  nodeId?: string;
                  rssi?: number;
                  requestEpochTimeInMs?: number;
                  broadcastReceivedEpochTimeInMs?: number;
                  facilityRid?: string;
                  enterpriseRid?: string;
                  /** Optional event kind (e.g. for simulating an Inovonics device event). null/absent or "PRESS" = a normal alarm press; "CONFIRM" = a magnet-confirm that moves the unit's open alert to OPEN_CONFIRMED. Maps to NodeBroadcastDetection.eventKind. */
                  eventKind?: string;
              };
              buttonSerialNumber?: string;
              eventType?: string;
          };
          doorAlarmEvent: {
              devEui?: string;
              gatewayRid?: string;
              facilityRid?: string;
              enterpriseRid?: string;
              gatewayName?: string;
              gatewayLocation?: string;
              gatewayDescription?: string;
              sqsApproximateReceiveCount?: number;
              sqsSentEpochTimeInMs?: number;
              sqsFirstReceiveEpochTimeInMs?: number;
              gatewayReceivedEpochTimeInSec?: number;
              serviceReceivedEpochTimeInMs?: number;
              rssi?: number;
              loraSnr?: number;
              frameCount?: number;
              framePort?: number;
              frequency?: number;
              adaptiveDataRate?: boolean;
              altitude?: number;
              latitude?: number;
              longitude?: number;
              data?: string;
              deviceType?: string;
              ridType?: string;
              /** Not every uplink carries a battery reading -- e.g. the Milesight WS203's Historical Data
        uplink never includes one (only its Battery Report does). Null means the originating
        uplink type doesn't report battery, not a device fault or missing data.
         */
              batteryLevel?: number | null;
          } & {
              isDoorOpen?: boolean;
              doorEventCount?: number;
          };
          fallDetectionEvent?: {
              hardwareId?: string;
              /** Device type as defined by the manufacturer, NOT a Sage device entity type */
              deviceType?: string;
              enterpriseRid?: string;
              facilityRid?: string;
              unitRid?: string;
              ridType?: string;
              /** The specific activity detected by a Detect camera, produced by video-sensing-worker-service. Absent means POTENTIAL_FALL (producers that predate this type do not set it). Every value other than POTENTIAL_FALL is a fall-risk activity that feeds proactive alerting; new activity values are added here as detection expands. This enum is add only. If you delete or update the values below, the api will break. */
              eventType?: 'POTENTIAL_FALL' | 'STANDING' | 'EDGE_OF_BED';
          };
          meltenM3Event?: {
              deviceAddress?: string;
              relayRid?: string;
              facilityRid?: string;
              unitRid?: string;
              callingType?: string;
              actionType?: string;
              relayReceivedEpochTimeInMs?: number;
              serviceReceivedEpochTimeInMs?: number;
          };
          movementSensorMessage?: ComponentsSchemasSimulateDeviceEventRequestPropertiesDoorAlarmEventAllOf0 & {
              occupied?: boolean;
          };
      }
    | {
          buttonEvent?: {
              nodeBroadcast?: {
                  bdaddr?: string;
                  nodeId?: string;
                  rssi?: number;
                  requestEpochTimeInMs?: number;
                  broadcastReceivedEpochTimeInMs?: number;
                  facilityRid?: string;
                  enterpriseRid?: string;
                  /** Optional event kind (e.g. for simulating an Inovonics device event). null/absent or "PRESS" = a normal alarm press; "CONFIRM" = a magnet-confirm that moves the unit's open alert to OPEN_CONFIRMED. Maps to NodeBroadcastDetection.eventKind. */
                  eventKind?: string;
              };
              buttonSerialNumber?: string;
              eventType?: string;
          };
          doorAlarmEvent?: {
              devEui?: string;
              gatewayRid?: string;
              facilityRid?: string;
              enterpriseRid?: string;
              gatewayName?: string;
              gatewayLocation?: string;
              gatewayDescription?: string;
              sqsApproximateReceiveCount?: number;
              sqsSentEpochTimeInMs?: number;
              sqsFirstReceiveEpochTimeInMs?: number;
              gatewayReceivedEpochTimeInSec?: number;
              serviceReceivedEpochTimeInMs?: number;
              rssi?: number;
              loraSnr?: number;
              frameCount?: number;
              framePort?: number;
              frequency?: number;
              adaptiveDataRate?: boolean;
              altitude?: number;
              latitude?: number;
              longitude?: number;
              data?: string;
              deviceType?: string;
              ridType?: string;
              /** Not every uplink carries a battery reading -- e.g. the Milesight WS203's Historical Data
        uplink never includes one (only its Battery Report does). Null means the originating
        uplink type doesn't report battery, not a device fault or missing data.
         */
              batteryLevel?: number | null;
          } & {
              isDoorOpen?: boolean;
              doorEventCount?: number;
          };
          fallDetectionEvent: {
              hardwareId?: string;
              /** Device type as defined by the manufacturer, NOT a Sage device entity type */
              deviceType?: string;
              enterpriseRid?: string;
              facilityRid?: string;
              unitRid?: string;
              ridType?: string;
              /** The specific activity detected by a Detect camera, produced by video-sensing-worker-service. Absent means POTENTIAL_FALL (producers that predate this type do not set it). Every value other than POTENTIAL_FALL is a fall-risk activity that feeds proactive alerting; new activity values are added here as detection expands. This enum is add only. If you delete or update the values below, the api will break. */
              eventType?: 'POTENTIAL_FALL' | 'STANDING' | 'EDGE_OF_BED';
          };
          meltenM3Event?: {
              deviceAddress?: string;
              relayRid?: string;
              facilityRid?: string;
              unitRid?: string;
              callingType?: string;
              actionType?: string;
              relayReceivedEpochTimeInMs?: number;
              serviceReceivedEpochTimeInMs?: number;
          };
          movementSensorMessage?: ComponentsSchemasSimulateDeviceEventRequestPropertiesDoorAlarmEventAllOf0 & {
              occupied?: boolean;
          };
      }
    | {
          buttonEvent?: {
              nodeBroadcast?: {
                  bdaddr?: string;
                  nodeId?: string;
                  rssi?: number;
                  requestEpochTimeInMs?: number;
                  broadcastReceivedEpochTimeInMs?: number;
                  facilityRid?: string;
                  enterpriseRid?: string;
                  /** Optional event kind (e.g. for simulating an Inovonics device event). null/absent or "PRESS" = a normal alarm press; "CONFIRM" = a magnet-confirm that moves the unit's open alert to OPEN_CONFIRMED. Maps to NodeBroadcastDetection.eventKind. */
                  eventKind?: string;
              };
              buttonSerialNumber?: string;
              eventType?: string;
          };
          doorAlarmEvent?: {
              devEui?: string;
              gatewayRid?: string;
              facilityRid?: string;
              enterpriseRid?: string;
              gatewayName?: string;
              gatewayLocation?: string;
              gatewayDescription?: string;
              sqsApproximateReceiveCount?: number;
              sqsSentEpochTimeInMs?: number;
              sqsFirstReceiveEpochTimeInMs?: number;
              gatewayReceivedEpochTimeInSec?: number;
              serviceReceivedEpochTimeInMs?: number;
              rssi?: number;
              loraSnr?: number;
              frameCount?: number;
              framePort?: number;
              frequency?: number;
              adaptiveDataRate?: boolean;
              altitude?: number;
              latitude?: number;
              longitude?: number;
              data?: string;
              deviceType?: string;
              ridType?: string;
              /** Not every uplink carries a battery reading -- e.g. the Milesight WS203's Historical Data
        uplink never includes one (only its Battery Report does). Null means the originating
        uplink type doesn't report battery, not a device fault or missing data.
         */
              batteryLevel?: number | null;
          } & {
              isDoorOpen?: boolean;
              doorEventCount?: number;
          };
          fallDetectionEvent?: {
              hardwareId?: string;
              /** Device type as defined by the manufacturer, NOT a Sage device entity type */
              deviceType?: string;
              enterpriseRid?: string;
              facilityRid?: string;
              unitRid?: string;
              ridType?: string;
              /** The specific activity detected by a Detect camera, produced by video-sensing-worker-service. Absent means POTENTIAL_FALL (producers that predate this type do not set it). Every value other than POTENTIAL_FALL is a fall-risk activity that feeds proactive alerting; new activity values are added here as detection expands. This enum is add only. If you delete or update the values below, the api will break. */
              eventType?: 'POTENTIAL_FALL' | 'STANDING' | 'EDGE_OF_BED';
          };
          meltenM3Event: {
              deviceAddress?: string;
              relayRid?: string;
              facilityRid?: string;
              unitRid?: string;
              callingType?: string;
              actionType?: string;
              relayReceivedEpochTimeInMs?: number;
              serviceReceivedEpochTimeInMs?: number;
          };
          movementSensorMessage?: ComponentsSchemasSimulateDeviceEventRequestPropertiesDoorAlarmEventAllOf0 & {
              occupied?: boolean;
          };
      }
    | {
          buttonEvent?: {
              nodeBroadcast?: {
                  bdaddr?: string;
                  nodeId?: string;
                  rssi?: number;
                  requestEpochTimeInMs?: number;
                  broadcastReceivedEpochTimeInMs?: number;
                  facilityRid?: string;
                  enterpriseRid?: string;
                  /** Optional event kind (e.g. for simulating an Inovonics device event). null/absent or "PRESS" = a normal alarm press; "CONFIRM" = a magnet-confirm that moves the unit's open alert to OPEN_CONFIRMED. Maps to NodeBroadcastDetection.eventKind. */
                  eventKind?: string;
              };
              buttonSerialNumber?: string;
              eventType?: string;
          };
          doorAlarmEvent?: {
              devEui?: string;
              gatewayRid?: string;
              facilityRid?: string;
              enterpriseRid?: string;
              gatewayName?: string;
              gatewayLocation?: string;
              gatewayDescription?: string;
              sqsApproximateReceiveCount?: number;
              sqsSentEpochTimeInMs?: number;
              sqsFirstReceiveEpochTimeInMs?: number;
              gatewayReceivedEpochTimeInSec?: number;
              serviceReceivedEpochTimeInMs?: number;
              rssi?: number;
              loraSnr?: number;
              frameCount?: number;
              framePort?: number;
              frequency?: number;
              adaptiveDataRate?: boolean;
              altitude?: number;
              latitude?: number;
              longitude?: number;
              data?: string;
              deviceType?: string;
              ridType?: string;
              /** Not every uplink carries a battery reading -- e.g. the Milesight WS203's Historical Data
        uplink never includes one (only its Battery Report does). Null means the originating
        uplink type doesn't report battery, not a device fault or missing data.
         */
              batteryLevel?: number | null;
          } & {
              isDoorOpen?: boolean;
              doorEventCount?: number;
          };
          fallDetectionEvent?: {
              hardwareId?: string;
              /** Device type as defined by the manufacturer, NOT a Sage device entity type */
              deviceType?: string;
              enterpriseRid?: string;
              facilityRid?: string;
              unitRid?: string;
              ridType?: string;
              /** The specific activity detected by a Detect camera, produced by video-sensing-worker-service. Absent means POTENTIAL_FALL (producers that predate this type do not set it). Every value other than POTENTIAL_FALL is a fall-risk activity that feeds proactive alerting; new activity values are added here as detection expands. This enum is add only. If you delete or update the values below, the api will break. */
              eventType?: 'POTENTIAL_FALL' | 'STANDING' | 'EDGE_OF_BED';
          };
          meltenM3Event?: {
              deviceAddress?: string;
              relayRid?: string;
              facilityRid?: string;
              unitRid?: string;
              callingType?: string;
              actionType?: string;
              relayReceivedEpochTimeInMs?: number;
              serviceReceivedEpochTimeInMs?: number;
          };
          movementSensorMessage: ComponentsSchemasSimulateDeviceEventRequestPropertiesDoorAlarmEventAllOf0 & {
              occupied?: boolean;
          };
      };
export type GetShiftInstancesResponse = {
    shiftInstances?: {
        id?: string;
        facilityRid?: string;
        name?: string;
        startTimeMs?: number;
        endTimeMs?: number;
    }[];
};
export type ShiftInstance = {
    id?: string;
    facilityRid?: string;
    name?: string;
    startTimeMs?: number;
    endTimeMs?: number;
};
export type UpsertShiftInstancesResponse = {
    shiftInstances?: ShiftInstance[];
};
export type UpsertShiftInstancesRequest = {
    shiftInstances: ShiftInstance[];
};
export const {
    useGetCareEventQuery,
    useLazyGetCareEventQuery,
    useAddCareEventNoteMutation,
    useGetCareTasksQuery,
    useLazyGetCareTasksQuery,
    useUpdateCareTaskMutation,
    useAddEhrLockedFieldsMutation,
    useDeleteEhrLockedFieldMutation,
    useCreateResidentMutation,
    useGetResidentsQuery,
    useLazyGetResidentsQuery,
    useCreateResidentsMutation,
    useBulkSetRoutineCheckStatusMutation,
    useGetUnitsQuery,
    useLazyGetUnitsQuery,
    useCreateUnitMutation,
    useDeleteUnitsMutation,
    useCreateUnitsMutation,
    useUpdateUnitsMutation,
    useGetCheckInsQuery,
    useLazyGetCheckInsQuery,
    useCreateCheckInMutation,
    useUpdateCheckInMutation,
    useGetCheckInQuery,
    useLazyGetCheckInQuery,
    useCreateStaffMemberMutation,
    useGetStaffMembersQuery,
    useLazyGetStaffMembersQuery,
    useUpdateStaffMemberPermissionsMutation,
    useGetStaffWithFallReplayViewQuery,
    useLazyGetStaffWithFallReplayViewQuery,
    useGetStaffPermissionsQuery,
    useLazyGetStaffPermissionsQuery,
    useGrantRoleOnResourceMutation,
    useRevokeRoleOnResourceMutation,
    useUpdatePhoneNumberVerificationStatusMutation,
    useCreateStaffMembersMutation,
    useMergeStaffProfilesMutation,
    useGetContactsOfResidentQuery,
    useLazyGetContactsOfResidentQuery,
    useCreateContactForResidentMutation,
    useGetResidentActivitiesQuery,
    useLazyGetResidentActivitiesQuery,
    useGetEnterprisesQuery,
    useLazyGetEnterprisesQuery,
    useCreateEnterpriseMutation,
    useGetFacilitiesQuery,
    useLazyGetFacilitiesQuery,
    useCreateFacilityMutation,
    useCreateTaskMutation,
    useGetGroupsQuery,
    useLazyGetGroupsQuery,
    useCreateGroupMutation,
    useGetEnterpriseQuery,
    useLazyGetEnterpriseQuery,
    useUpdateEnterpriseMutation,
    useDeleteEnterpriseMutation,
    useRetryEnterpriseSsoEnforcementMutation,
    useGetFacilityQuery,
    useLazyGetFacilityQuery,
    useUpdateFacilityMutation,
    useDeleteFacilityMutation,
    useUpdateFacilityStatusMutation,
    useGetGroupQuery,
    useLazyGetGroupQuery,
    useUpdateGroupMutation,
    useDeleteGroupMutation,
    useGetResidentQuery,
    useLazyGetResidentQuery,
    useUpdateResidentMutation,
    useDeleteResidentMutation,
    useCreateExpectedExitMutation,
    useDeleteResidentExpectedExitMutation,
    useUpdateResidentDetectConsentMutation,
    useGetLatestCareActivityQuery,
    useLazyGetLatestCareActivityQuery,
    useGetContactOfResidentQuery,
    useLazyGetContactOfResidentQuery,
    useUpdateContactMutation,
    useDeleteContactMutation,
    useGetStaffMemberQuery,
    useLazyGetStaffMemberQuery,
    useUpdateStaffMemberMutation,
    useArchiveStaffMemberMutation,
    useAssignPhoneNumberMutation,
    useVerifyPhoneNumberMutation,
    useGetTaskQuery,
    useLazyGetTaskQuery,
    useUpdateTaskMutation,
    useDeleteTaskMutation,
    useResolveTaskMutation,
    useReviewTaskMutation,
    useAcknowledgeTaskMutation,
    useGetCareConfirmationTrackerQuery,
    useLazyGetCareConfirmationTrackerQuery,
    useResolveAllAlertsMutation,
    useSyncOfflineAlertMutation,
    useGetUnitQuery,
    useLazyGetUnitQuery,
    useUpdateUnitMutation,
    useDeleteUnitMutation,
    useCreateUpdateForTaskMutation,
    useDeleteUpdateMutation,
    useGetAllocatedDevicesQuery,
    useLazyGetAllocatedDevicesQuery,
    useAllocateDeviceMutation,
    useSearchLatestAllocatedDeviceActivityQuery,
    useLazySearchLatestAllocatedDeviceActivityQuery,
    useGetDeviceAllocationQuery,
    useLazyGetDeviceAllocationQuery,
    useUpdateDeviceAllocationMutation,
    useDeallocateDeviceMutation,
    useGetDevicesQuery,
    useLazyGetDevicesQuery,
    useCreateDeviceMutation,
    useGetDeviceQuery,
    useLazyGetDeviceQuery,
    useDeleteDeviceMutation,
    useCreateSpeakerMutation,
    useGetSpeakersQuery,
    useLazyGetSpeakersQuery,
    useGetSpeakerQuery,
    useLazyGetSpeakerQuery,
    useUpdateSpeakerMutation,
    useDeleteSpeakerMutation,
    useCreateNurseStationMutation,
    useGetNurseStationsByFacilityQuery,
    useLazyGetNurseStationsByFacilityQuery,
    useGetNurseStationQuery,
    useLazyGetNurseStationQuery,
    useUpdateNurseStationMutation,
    useDeleteNurseStationMutation,
    useGetOnCallStateQuery,
    useLazyGetOnCallStateQuery,
    useUpdateOnCallStateMutation,
    useSetEnterpriseGroupMembershipsMutation,
    useGetMembershipDetailsQuery,
    useLazyGetMembershipDetailsQuery,
    useUpdateGroupMembershipMutation,
    useRemoveMemberFromGroupMutation,
    useGetApplicationConfigurationQuery,
    useLazyGetApplicationConfigurationQuery,
    useResolveNumbersQuery,
    useLazyResolveNumbersQuery,
    useCreateNoteForTaskMutation,
    useDeleteTaskNoteMutation,
    useUpdateTaskNoteMutation,
    useUnassignTaskMutation,
    useUpdateResidentArchiveStatusMutation,
    useUpdateStaffMemberArchiveStatusMutation,
    useGetStaffMemberFacilityMetadataQuery,
    useLazyGetStaffMemberFacilityMetadataQuery,
    useDeleteStaffMemberFromFacilitiesMutation,
    useGetImageForResourceQuery,
    useLazyGetImageForResourceQuery,
    useUploadImageForResourceMutation,
    useDeleteImageForResourceMutation,
    useGetVideoReplayDownloadUrlQuery,
    useLazyGetVideoReplayDownloadUrlQuery,
    useGetVideoReplayAvailabilityQuery,
    useLazyGetVideoReplayAvailabilityQuery,
    useSetPcpForResidentMutation,
    useDeletePcpForResidentMutation,
    useGetPcpForResidentQuery,
    useLazyGetPcpForResidentQuery,
    useGetSpecialistsForResidentQuery,
    useLazyGetSpecialistsForResidentQuery,
    useAddSpecialistForResidentMutation,
    useUpdateSpecialistForResidentMutation,
    useDeleteSpecialistForResidentMutation,
    useUnallocateDeviceMutation,
    useBulkUpdateDeviceAllocationsMutation,
    useTaskSearchQuery,
    useLazyTaskSearchQuery,
    useSearchClosedTasksQuery,
    useLazySearchClosedTasksQuery,
    useSearchOpenAlertsQuery,
    useLazySearchOpenAlertsQuery,
    useSetResidentHealthInfoMutation,
    useDeleteResidentHealthInfoMutation,
    useGetResidentCareInfoQuery,
    useLazyGetResidentCareInfoQuery,
    useSetResidentCareInfoMutation,
    useCreateTaskScheduleMutation,
    useGetTaskScheduleQuery,
    useLazyGetTaskScheduleQuery,
    useUpdateTaskScheduleMutation,
    useDeleteTaskScheduleMutation,
    useTaskScheduleSearchQuery,
    useLazyTaskScheduleSearchQuery,
    useGetMyTasksMutation,
    useUnassignTaskScheduleMutation,
    useAddDeviceActivityMutation,
    useGetLatestDeviceActivityQuery,
    useLazyGetLatestDeviceActivityQuery,
    useCreateResidentActivityMutation,
    useUnsetTaskScheduleDurationMutation,
    useGetCompanyPhonesQuery,
    useLazyGetCompanyPhonesQuery,
    useCreateCompanyPhoneMutation,
    useDeleteCompanyPhonesMutation,
    useGetCompanyPhoneQuery,
    useLazyGetCompanyPhoneQuery,
    useUpdateCompanyPhoneMutation,
    useDeleteCompanyPhoneMutation,
    useCreateCompanyPhonesMutation,
    useGetHospitalForResidentQuery,
    useLazyGetHospitalForResidentQuery,
    useSetHospitalForResidentMutation,
    useDeleteHospitalForResidentMutation,
    useGetCareZoneQuery,
    useLazyGetCareZoneQuery,
    useUpdateCareZoneMutation,
    useDeleteCareZoneMutation,
    useGetAllCareZonesQuery,
    useLazyGetAllCareZonesQuery,
    useCreateCareZoneMutation,
    useGetOnCallStaffMembersQuery,
    useLazyGetOnCallStaffMembersQuery,
    useUnitsSearchQuery,
    useLazyUnitsSearchQuery,
    useGetCamerasQuery,
    useLazyGetCamerasQuery,
    useCheckInSearchQuery,
    useLazyCheckInSearchQuery,
    useCreateStaffFacilityPreferencesMutation,
    useGetStaffFacilityPreferencesQuery,
    useLazyGetStaffFacilityPreferencesQuery,
    useUpdateStaffFacilityPreferencesMutation,
    useDeleteStaffFacilityPreferencesMutation,
    useSearchStaffFacilityPreferencesMutation,
    useCreateStaffMemberFacilityConfigMutation,
    useGetStaffMemberFacilityConfigQuery,
    useLazyGetStaffMemberFacilityConfigQuery,
    useUpdateStaffMemberFacilityConfigMutation,
    useDeleteStaffMemberFacilityConfigMutation,
    useSearchStaffMemberFacilityConfigsMutation,
    useStaffProfileSearchQuery,
    useLazyStaffProfileSearchQuery,
    useGetUserStaffMemberProfilesQuery,
    useLazyGetUserStaffMemberProfilesQuery,
    useCreateDoorMutation,
    useDeleteDoorsMutation,
    useCreateDoorsMutation,
    useGetDoorQuery,
    useLazyGetDoorQuery,
    useUpdateDoorMutation,
    useDeleteDoorMutation,
    useCreateOrUpdateDoorAlarmScheduleMutation,
    useGetDoorAlarmScheduleQuery,
    useLazyGetDoorAlarmScheduleQuery,
    useDeleteDoorAlarmScheduleMutation,
    useCreateOrUpdateFacilityDefaultDoorAlarmScheduleMutation,
    useGetFacilityDefaultDoorAlarmScheduleQuery,
    useLazyGetFacilityDefaultDoorAlarmScheduleQuery,
    useDeleteFacilityDefaultDoorAlarmScheduleMutation,
    useSearchDoorsQuery,
    useLazySearchDoorsQuery,
    useSearchDoorAlarmSchedulesQuery,
    useLazySearchDoorAlarmSchedulesQuery,
    useGetAllFeaturesQuery,
    useLazyGetAllFeaturesQuery,
    useCreateFeatureMutation,
    useGetFeatureQuery,
    useLazyGetFeatureQuery,
    useUpdateFeatureMutation,
    useDeleteFeatureMutation,
    useGetNotificationContextMutation,
    useCreateNotificationPolicyMutation,
    useGetNotificationPoliciesQuery,
    useLazyGetNotificationPoliciesQuery,
    useGetNotificationPolicyQuery,
    useLazyGetNotificationPolicyQuery,
    useUpdateNotificationPolicyMutation,
    useCreateNotificationBlockMutation,
    useUpdateNotificationBlockMutation,
    useDeleteNotificationBlockMutation,
    useGetNotificationFilterQuery,
    useLazyGetNotificationFilterQuery,
    useUpdateNotificationFilterMutation,
    useDeleteNotificationFilterMutation,
    useSimulateDeviceEventMutation,
    useGetShiftInstancesQuery,
    useLazyGetShiftInstancesQuery,
    useUpsertShiftInstancesMutation,
} = injectedRtkApi;
